/* eslint-disable jsx-a11y/anchor-is-valid, no-unused-vars */
import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from "react";
import "./BordApp.css";
// Leaflet is loaded lazily inside ReturnPickupMap — NOT in the initial bundle
import Cursor from "./Cursor";
import { API } from "../lib/api";
import Logo from "./Logo";
import { getFabricById, getBand } from "../lib/fabrics";
import { setPageMeta, resetPageMeta, injectFAQSchema, removeFAQSchema } from "../lib/seo";
import { useMobileSwipeNav } from "./MobileSwipeNav";
import HelperChat from "./HelperChat";
// LocationSearchModal is large (~45KB) — lazy-load so it doesn't block first paint
const NoberoProductScreen = React.lazy(() => import("./NoberoProductScreen"));
const LocationSearchModal = React.lazy(() => import('./location/LocationSearchModal'));
const CheckoutPage = React.lazy(() => import("./CheckoutPage"));
const OrderTrackingPage = React.lazy(() => import("./OrderTrackingPage"));
const AdminPanel = React.lazy(() => import("./AdminPanel"));
const AuthPage = React.lazy(() => import("./AuthPage"));
// ── Sustainable Relaunch: new content pages (lazy-loaded) ──────────────────
const OurFabricsPage     = React.lazy(() => import("./OurFabricsPage"));
const SustainabilityPage = React.lazy(() => import("./SustainabilityPage"));
const CertificationsPage = React.lazy(() => import("./CertificationsPage"));
const CarePage           = React.lazy(() => import("./CarePage"));
const JournalPage        = React.lazy(() => import("./JournalPage"));
const AboutPage          = React.lazy(() => import("./AboutPage"));
const ContactPage        = React.lazy(() => import("./ContactPage"));
const CardsVaultPage     = React.lazy(() => import("./cards/CardsVaultPage"));
const SoloLevelingComicReader = React.lazy(() => import("./comics/SoloLevelingComicReader"));
const CardSpinRewardModal = React.lazy(() => import("./cards/CardSpinRewardModal"));


// ── Motion & Gen Z Layer Utilities (Section 4 & 4B) ────────────────────────
function triggerConfetti() {
  try {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const existing = document.getElementById("bords-confetti-canvas");
    if (existing) existing.remove();

    const canvas = document.createElement("canvas");
    canvas.id = "bords-confetti-canvas";
    canvas.style.position = "fixed";
    canvas.style.inset = "0";
    canvas.style.pointerEvents = "none";
    canvas.style.zIndex = "999999";
    document.body.appendChild(canvas);

    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    ctx.scale(dpr, dpr);

    const colors = ["#D4FF3F", "#FF3EA5", "#2E4CFF", "#EDE6D6", "#4A5D3A"];
    const particles = Array.from({ length: 65 }).map(() => ({
      x: window.innerWidth * (0.4 + Math.random() * 0.2),
      y: window.innerHeight * 0.5,
      vx: (Math.random() - 0.5) * 14,
      vy: -Math.random() * 14 - 6,
      size: Math.random() * 7 + 4,
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: Math.random() * 360,
      rotSpeed: (Math.random() - 0.5) * 10,
    }));

    const startTime = performance.now();
    const duration = 600; // Section 4B: 600ms celebration

    function frame(now) {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.55;
        p.vx *= 0.98;
        p.rotation += p.rotSpeed;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = Math.max(0, 1 - progress);
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      });

      if (progress < 1) {
        requestAnimationFrame(frame);
      } else {
        canvas.remove();
      }
    }
    requestAnimationFrame(frame);
  } catch (_) {}
}

function ThreadWeaveHero() {
  return (
    <div className="thread-weave-wrap" aria-hidden="true">
      <svg className="thread-weave-svg" viewBox="0 0 520 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          className="thread-path thread-path-1"
          d="M 10,20 C 70,5 120,35 190,20 C 260,5 310,35 380,18 C 430,6 470,30 510,20"
          stroke="var(--bords-moss, #4A5D3A)"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <path
          className="thread-path thread-path-2"
          d="M 5,25 C 65,35 130,5 210,25 C 290,38 350,8 430,24 C 465,32 490,12 515,22"
          stroke="var(--bords-pop-lime, #D4FF3F)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="4 6"
        />
      </svg>
    </div>
  );
}

function DropCountdownBanner({ label, subtitle, endDate, products, series, styles }) {
  // Compute time left from real end date every second
  const calcTimeLeft = () => {
    if (!endDate) return null;
    const diff = new Date(endDate).getTime() - Date.now();
    if (isNaN(diff) || diff <= 0) return null;
    const totalSecs = Math.floor(diff / 1000);
    return {
      days:  Math.floor(totalSecs / 86400),
      hours: Math.floor((totalSecs % 86400) / 3600),
      mins:  Math.floor((totalSecs % 3600) / 60),
      secs:  totalSecs % 60,
    };
  };

  const [timeLeft, setTimeLeft] = useState(() => calcTimeLeft());

  useEffect(() => {
    if (!endDate) { setTimeLeft(null); return; }
    setTimeLeft(calcTimeLeft());
    const timer = setInterval(() => setTimeLeft(calcTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, [endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const bannerLabel    = label    || 'VAULT CAPSULE // DROP 01';
  const bannerSubtitle = subtitle || '';
  const seriesName     = series   || '';
  const styleList      = styles ? styles.split(',').map(s => s.trim()).filter(Boolean) : [];

  // Parse products — supports JSON [{name, pct}] or legacy comma-separated
  let productList = [];
  if (products && products.trim()) {
    try {
      const parsed = JSON.parse(products);
      if (Array.isArray(parsed)) productList = parsed.filter(p => p && p.name && p.name.trim());
    } catch (_) {
      // legacy comma format
      productList = products.split(',').map(n => n.trim()).filter(Boolean).map(n => ({ name: n, pct: '' }));
    }
  }

  const maxPct = productList.reduce((max, p) => Math.max(max, Number(p.pct) || 0), 0);

  return (
    <div className="drop-countdown-bar" role="region" aria-label="Drop countdown">
      <div className="countdown-inner">
        {/* Left Section: Spec header & Garment hangtags */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className="countdown-tag" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span className="loud-pulse-dot" style={{ background: '#0D0D0D' }} />
              ⚡ {bannerLabel}
            </span>
            {maxPct > 0 && (
              <span style={{
                background: '#D4FF3F',
                color: '#0D0D0D',
                padding: '3px 8px',
                borderRadius: 3,
                fontSize: '.58rem',
                fontWeight: 900,
                letterSpacing: '.06em',
                fontFamily: "var(--font-stamp, 'Archivo Black', sans-serif)"
              }}>
                UP TO {maxPct}% ARCHIVE SAVINGS
              </span>
            )}
            {seriesName && (
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: '.62rem', letterSpacing: '.14em', opacity: 0.85, textTransform: 'uppercase', color: '#D4FF3F' }}>
                SERIES // {seriesName}
              </span>
            )}
            {styleList.length > 0 && (
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: '.58rem', opacity: 0.65, letterSpacing: '.05em' }}>
                [ {styleList.join(' // ')} ]
              </span>
            )}
          </div>

          {/* Garment Hangtag Chips or Empty State */}
          {productList.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 2 }}>
              {productList.map((p, i) => (
                <div key={i} className="genz-hangtag-chip">
                  <span style={{ opacity: 0.5 }}>{"//"}</span>
                  <span>{p.name}</span>
                  {p.pct ? (
                    <span className="genz-hangtag-pct">
                      ⚡ {p.pct}% OFF
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              fontFamily: "'Space Mono', monospace",
              fontSize: '.65rem',
              color: '#D4FF3F',
              fontWeight: 700,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              marginTop: 2,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6
            }}>
              <span className="loud-pulse-dot" />
              <span>[ STATUS // ARCHIVE VAULT SECURED · NO ITEMS STAGED ]</span>
            </div>
          )}
        </div>

        {/* Right Section: Digital Countdown Timer */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {timeLeft ? (
            <div className="countdown-digits">
              <div className="digit-box"><span>{String(timeLeft.days).padStart(2, '0')}</span><small>DAYS</small></div>
              <span className="digit-colon">:</span>
              <div className="digit-box"><span>{String(timeLeft.hours).padStart(2, '0')}</span><small>HOURS</small></div>
              <span className="digit-colon">:</span>
              <div className="digit-box"><span>{String(timeLeft.mins).padStart(2, '0')}</span><small>MINS</small></div>
              <span className="digit-colon">:</span>
              <div className="digit-box"><span>{String(timeLeft.secs).padStart(2, '0')}</span><small>SECS</small></div>
            </div>
          ) : (
            <div style={{
              background: 'rgba(212,255,63,0.12)',
              border: '1.5px solid #D4FF3F',
              color: '#D4FF3F',
              borderRadius: 4,
              padding: '6px 14px',
              fontFamily: "'Space Mono', monospace",
              fontSize: '.66rem',
              fontWeight: 800,
              letterSpacing: '.12em',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}>
              <span className="loud-pulse-dot" />
              ⚡ VAULT ALLOCATION ACTIVE
            </div>
          )}

          {bannerSubtitle && (
            <span className="countdown-sub" style={{ fontFamily: "'Space Mono', monospace", fontSize: '.62rem' }}>
              {"// "}{bannerSubtitle}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function FooterNoiseCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = 100;
    canvas.height = 100;

    let intervalId = null;

    const renderNoise = () => {
      const imgData = ctx.createImageData(100, 100);
      const data = imgData.data;
      for (let i = 0; i < data.length; i += 4) {
        const val = Math.random() * 255;
        data[i] = val;
        data[i + 1] = val;
        data[i + 2] = val;
        data[i + 3] = 16; // subtle low opacity
      }
      ctx.putImageData(imgData, 0, 0);
    };

    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          renderNoise();
          if (!intervalId) intervalId = setInterval(renderNoise, 66);
        } else {
          if (intervalId) { clearInterval(intervalId); intervalId = null; }
        }
      }, { threshold: 0.05 });
      observer.observe(canvas);
      return () => {
        observer.disconnect();
        if (intervalId) clearInterval(intervalId);
      };
    } else {
      renderNoise();
      intervalId = setInterval(renderNoise, 66);
      return () => { if (intervalId) clearInterval(intervalId); };
    }
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        opacity: 0.12,
        mixBlendMode: "overlay",
        zIndex: 0,
      }}
      aria-hidden="true"
    />
  );
}

// Try multiple Google Drive URL formats — falls back automatically if one fails
// Try all known Google Drive formats in order until one works
// Try all known Google Drive formats in order until one works
// Convert any image URL to the best directly embeddable form
function toDirectImageUrl(url) {
  if (!url || typeof url !== 'string') return url;
  return resolveImageUrl(url.trim());
}

function ReturnPickupMap({ lat, lng }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  // Lazily inject leaflet CSS only when this map actually renders
  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
      link.crossOrigin = '';
      document.head.appendChild(link);
    }
  }, []);

  // Dynamically import Leaflet — keeps it OUT of the initial JS bundle
  useEffect(() => {
    if (!mapRef.current) return;
    if (mapInstance.current) return;
    import('leaflet').then(({ default: L }) => {
      if (!mapRef.current || mapInstance.current) return;
      mapInstance.current = L.map(mapRef.current, { center: [lat, lng], zoom: 15, zoomControl: false, attributionControl: false, dragging: false, touchZoom: false, scrollWheelZoom: false, doubleClickZoom: false });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(mapInstance.current);
      const icon = L.divIcon({ className: "", html: '<div style="font-size:1.8rem;line-height:1;transform:translate(-50%,-100%);filter:drop-shadow(0 2px 4px rgba(0,0,0,0.4))">📍</div>', iconSize: [0,0] });
      L.marker([lat, lng], { icon }).addTo(mapInstance.current);
    }).catch(() => {});
  }, [lat, lng]);

  return (
    <div className="bord-return-pickup-map">
      <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}

function getDriveId(url) {
  if (!url) return null;
  const patterns = [
    /drive\.google\.com\/file\/d\/([^/?#]+)/,
    /drive\.google\.com\/open\?id=([^&]+)/,
    /drive\.google\.com\/thumbnail\?id=([^&]+)/,
    /drive\.google\.com\/uc\?.*id=([^&]+)/,
    /[?&]id=([a-zA-Z0-9_-]{25,})/,
    /lh3\.googleusercontent\.com\/d\/([^=?/]+)/,
  ];
  for (const p of patterns) { const m = url.match(p); if (m) return m[1]; }
  return null;
}



// Resolve any image URL to a directly embeddable src
function resolveImageUrl(url) {
  if (!url) return url;
  const u = url.trim();

  // ── i.ibb.co = already the CDN, use directly ────────────────────────────────
  if (u.includes('i.ibb.co')) return u;

  // ── ibb.co/HASH/slug → i.ibb.co/HASH/slug  (most common admin paste) ────────
  // e.g. https://ibb.co/pjqBCFMz/myimage  →  https://i.ibb.co/pjqBCFMz/myimage
  const ibbSlash = u.match(/^https?:\/\/(www\.)?ibb\.co\/([a-zA-Z0-9]+)\/(.+)$/);
  if (ibbSlash) return `https://i.ibb.co/${ibbSlash[2]}/${ibbSlash[3]}`;

  // ── ibb.co/d/HASH → i.ibb.co/HASH/image (new imgbb share format) ─────────────
  const ibbD = u.match(/^https?:\/\/(www\.)?ibb\.co\/d\/([a-zA-Z0-9]+)\/?$/);
  if (ibbD) return `https://i.ibb.co/${ibbD[2]}/image`;

  // ── ibb.co/HASH only (no slug, old short-link) — can't reliably convert ──────
  if (/^https?:\/\/(www\.)?ibb\.co\/[a-zA-Z0-9]+\/?$/.test(u)) {
    return '__IMGBB_VIEWER__:' + u;
  }

  // ── Already a direct embeddable image ────────────────────────────────────────
  const isDirect = /\.(jpg|jpeg|png|webp|gif|avif|bmp|svg)(\?|$)/i.test(u)
    || u.includes('i.postimg.cc')
    || u.includes('/uploads/')
    || u.includes('githubusercontent.com')
    || u.includes('images.unsplash.com')
    || u.includes('cloudinary.com');
  if (isDirect) return u;

  // ── Google Drive ──────────────────────────────────────────────────────────────
  const driveId = getDriveId(u);
  if (driveId) return `https://lh3.googleusercontent.com/d/${driveId}=w800`;

  // ── Unknown — return as-is and let browser try ────────────────────────────────
  return u;
}

// Smart image component — handles Google Drive fallbacks, imgbb guidance, broken URL fallback (Memoized)
const DriveImg = React.memo(function DriveImg({ src, alt, style, className, onLoad, onError: onErrProp, loading = "lazy", fetchPriority, decoding = "async", width, height }) {
  const resolved = resolveImageUrl(src);
  const isImgbbViewer = resolved && resolved.startsWith('__IMGBB_VIEWER__:');
  const driveId = !isImgbbViewer ? getDriveId(src) : null;
  const [urlIdx, setUrlIdx] = useState(0);
  const [failed,  setFailed]  = useState(false);

  // Build candidate URL list (Drive gets 3 fallback URLs, constrained to max 800px width for fast loading)
  const urls = driveId ? [
    `https://lh3.googleusercontent.com/d/${driveId}=w800`,
    `https://drive.google.com/thumbnail?id=${driveId}&sz=w800`,
    `https://drive.google.com/uc?export=view&id=${driveId}`,
  ] : isImgbbViewer ? [] : [resolved || src];

  // Reset when src changes
  useEffect(() => { setUrlIdx(0); setFailed(false); }, [src]);

  // ── Viewer page warning ──────────────────────────────────────────────────────
  if (isImgbbViewer) {
    return (
      <div style={{...style, display:"flex",flexDirection:"column",alignItems:"center",
        justifyContent:"center",background:"rgba(245,200,66,0.06)",
        border:"1px solid rgba(245,200,66,0.2)",borderRadius:4,gap:6,
        padding:8,boxSizing:"border-box",textAlign:"center"}}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(245,200,66,0.7)" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <div style={{fontSize:".55rem",color:"rgba(245,200,66,0.8)",lineHeight:1.5}}>
          Paste the <strong>Direct link</strong><br/>
          <span style={{opacity:.6}}>(ends in .jpg/.png)</span>
        </div>
      </div>
    );
  }

  // ── All URLs exhausted → show placeholder ───────────────────────────────────
  if (failed || !urls.length) {
    return (
      <div style={{...style, display:"flex",flexDirection:"column",alignItems:"center",
        justifyContent:"center",background:"rgba(var(--bord-fg-rgb,26,26,26),0.03)",
        border:"1px dashed rgba(var(--bord-fg-rgb,26,26,26),0.12)",borderRadius:4,
        gap:6,boxSizing:"border-box"}}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(var(--bord-fg-rgb,26,26,26),0.25)" strokeWidth="1.2">
          <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
          <polyline points="21 15 16 10 5 21"/>
        </svg>
        <div style={{fontSize:".55rem",color:"rgba(var(--bord-fg-rgb,26,26,26),0.4)",letterSpacing:".05em"}}>No image</div>
      </div>
    );
  }

  const current = urls[Math.min(urlIdx, urls.length - 1)];
  return (
    <img src={current} alt={alt} style={style} className={className}
      width={width}
      height={height}
      referrerPolicy="no-referrer"
      loading={loading}
      decoding={decoding}
      fetchPriority={fetchPriority}
      onLoad={onLoad}
      onError={() => {
        if (urlIdx < urls.length - 1) {
          setUrlIdx(i => i + 1); // try next fallback URL
        } else {
          setFailed(true);       // all failed → show placeholder
          if (onErrProp) onErrProp();
        }
      }}
    />
  );
});






// Normalize DB rows: PostgreSQL returns lowercase keys, UI expects UPPERCASE
const toUpper = (obj) => {
  if (!obj || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(toUpper);
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k.toUpperCase(), v]));
};

const cleanBadgeLabel = (str) => {
  if (!str) return "";
  return str.replace(/^[^\w\d\s₹]+/, "").trim();
};

function scheduleIdleWork(callback, timeout = 1200) {
  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    const id = window.requestIdleCallback(callback, { timeout });
    return () => window.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(callback, Math.min(timeout, 800));
  return () => window.clearTimeout(id);
}

const api = {
  getProducts: async (filters = {}) => {
    const params = new URLSearchParams({ summary: "1", ...filters }).toString();
    const res = await fetch(`${API}/products?${params}`, {
      cache: "default",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error("Failed to fetch products");
    const json = await res.json(); return { ...json, data: toUpper(json.data) };
  },
  addToCart: async (item, userId) => {
    if (!userId) return;
    const res = await fetch(`${API}/cart`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...item, user_id: userId }) });
    if (!res.ok) throw new Error("Failed to add to cart");
    return res.json();
  },
  getCart: async (userId) => {
    if (!userId) return { data: [] };
    const res = await fetch(`${API}/cart?user_id=${userId}`);
    if (!res.ok) throw new Error("Failed to fetch cart");
    const json = await res.json(); return { ...json, data: toUpper(json.data) };
  },
  removeFromCart: async (id, userId) => {
    const suffix = userId ? `?user_id=${userId}` : "";
    const res = await fetch(`${API}/cart/${id}${suffix}`, { method: "DELETE" });
    if (!res.ok) throw new Error("Failed to remove from cart");
    return res.json();
  },
  updateCartQty: async (id, qty, userId) => {
    const res = await fetch(`${API}/cart/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ qty, user_id: userId }) });
    if (!res.ok) throw new Error("Failed to update quantity");
    return res.json();
  },
  clearCart: async (userId) => {
    if (!userId) return;
    await fetch(`${API}/cart/clear/${userId}`, { method: "DELETE" });
  },
  subscribe: async (email) => {
    const res = await fetch(`${API}/newsletter/subscribe`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) });
    if (!res.ok) throw new Error("Failed to subscribe");
    return res.json();
  },
};

// ── Size-level stock determination helper ──────────────────────────────────
export function getProductSizeStock(product, size) {
  if (!product) return 10;
  if (product.STOCK === 0 || product.stock === 0 || product.BADGE === "Sold Out" || product.badge === "Sold Out") {
    return 0;
  }
  const rawSizes = product.SIZES || product.sizes;
  if (rawSizes && rawSizes !== "null" && rawSizes !== "[]") {
    try {
      const parsed = typeof rawSizes === "string" ? JSON.parse(rawSizes) : rawSizes;
      if (Array.isArray(parsed) && parsed.length > 0) {
        if (!size) {
          return Math.max(0, Number(product.STOCK ?? product.stock ?? 10));
        }
        const match = parsed.find(
          (s) => (typeof s === "object" ? (s.size || s.name) : s) === size
        );
        if (match && typeof match === "object" && (match.qty !== undefined || match.stock !== undefined)) {
          return Math.max(0, Number(match.qty ?? match.stock));
        }
      }
    } catch (_) {}
  }
  return Math.max(0, Number(product.STOCK ?? product.stock ?? 10));
}

const CACHE_KEY             = 'bords_products_cache';
const SETTINGS_CACHE_KEY    = 'bords_settings_cache';
const COLLECTIONS_CACHE_KEY = 'bords_collections_cache_v3';

// ── Instant Fallback Catalog ─────────────────────────────────────────────────
// Guarantees 0ms first paint for brand new visitors and repeat visitors alike.
// Background API fetch revalidates and seamlessly updates with live DB changes.
const DEFAULT_PRODUCTS = [
  {
    ID: 1,
    NAME: "Beige El Classico ",
    PRICE: "1049.00",
    CATEGORY: "Tops",
    BADGE: "New",
    STOCK: 35,
    IMAGE_URL: "/images/beige.webp",
    IMAGE_URLS: "[\"/images/beige.webp\"]",
    DESCRIPTION: "For the Real you, Wear me to Be you for a little while...",
    SIZES: "[{\"size\":\"S\",\"qty\":5},{\"size\":\"M\",\"qty\":8},{\"size\":\"L\",\"qty\":9},{\"size\":\"XL\",\"qty\":8},{\"size\":\"XXL\",\"qty\":5}]",
    IS_ACTIVE: 1,
    COLLECTION_ID: 1,
    FABRIC_TAG: "organic-cotton-gots"
  },
  {
    ID: 2,
    NAME: "Shadow OverSized tee",
    PRICE: "579.00",
    CATEGORY: "Tops",
    BADGE: "New",
    STOCK: 25,
    IMAGE_URL: "/images/black.webp",
    IMAGE_URLS: "[\"/images/black.webp\"]",
    DESCRIPTION: "Wear the cool you. Be you, yet try new. Wanna have some soft black to be raw \n#WearMe",
    SIZES: "[\"S\",\"M\",\"L\",\"XL\",\"XXL\"]",
    IS_ACTIVE: 1,
    COLLECTION_ID: 3,
    FABRIC_TAG: "recycled-cotton"
  },
  {
    ID: 3,
    NAME: "Creame - White OverSized tee",
    PRICE: "569.00",
    CATEGORY: "Tops",
    BADGE: "New",
    STOCK: 25,
    IMAGE_URL: "/images/off-white.webp",
    IMAGE_URLS: "[\"/images/off-white.webp\"]",
    DESCRIPTION: "With the Blazing Heart, carry your will with BORD's New Off white tee.",
    SIZES: "[\"S\",\"M\",\"L\",\"XL\",\"XXL\"]",
    IS_ACTIVE: 1,
    COLLECTION_ID: 2,
    FABRIC_TAG: "khadi"
  }
];

const DEFAULT_COLLECTIONS = [
  {
    ID: 1,
    id: 1,
    slug: "beige-series",
    POSITION: 1,
    TAG: "New Season",
    NAME: "Beige Series",
    DESCRIPTION: "Warm earthy tones with relaxed drop shoulders. Understated minimalism designed for Bangalore everyday street culture.",
    IMAGE_URL: "/images/col-beige-series.webp",
    IS_ACTIVE: 1,
  },
  {
    ID: 2,
    id: 2,
    slug: "blank-state",
    POSITION: 2,
    TAG: "Essentials",
    NAME: "Blank State",
    DESCRIPTION: "Clean slate streetwear for daily rotations. Uncompromising heavyweight fabric with relaxed proportions.",
    IMAGE_URL: "/images/col-blank-state.webp",
    IS_ACTIVE: 1,
  },
  {
    ID: 3,
    id: 3,
    slug: "raw-edge",
    POSITION: 3,
    TAG: "Statement",
    NAME: "Raw Edge",
    DESCRIPTION: "Bold, textured cuts pushing beyond commercial conformity. Authentic Indian streetwear with attitude.",
    IMAGE_URL: "/images/col-raw-edge.webp",
    IS_ACTIVE: 1,
  }
];

function getCachedSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_CACHE_KEY);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > 60 * 60 * 1000) return null; // 1hr TTL for settings
    return data;
  } catch(_) { return null; }
}
function setCachedSettings(data) {
  try {
    const raw = localStorage.getItem(SETTINGS_CACHE_KEY);
    const prev = raw ? (JSON.parse(raw).data || {}) : {};
    localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify({ ts: Date.now(), data: { ...prev, ...data } }));
  } catch(_) {}
}
const CACHE_TTL  = 24 * 60 * 60 * 1000; // 24 hours

function getCachedProducts() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return DEFAULT_PRODUCTS;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) return (Array.isArray(data) && data.length > 0) ? data : DEFAULT_PRODUCTS;
    return (Array.isArray(data) && data.length > 0) ? data : DEFAULT_PRODUCTS;
  } catch(_) { return DEFAULT_PRODUCTS; }
}

function setCachedProducts(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch(_) {}
}

function getCachedCollections() {
  try {
    const raw = localStorage.getItem(COLLECTIONS_CACHE_KEY);
    if (!raw) return DEFAULT_COLLECTIONS;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) return (Array.isArray(data) && data.length > 0) ? data : DEFAULT_COLLECTIONS;
    return (Array.isArray(data) && data.length > 0) ? data : DEFAULT_COLLECTIONS;
  } catch(_) { return DEFAULT_COLLECTIONS; }
}

function setCachedCollections(data) {
  try {
    localStorage.setItem(COLLECTIONS_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch(_) {}
}

function clearStoredSession() {
  try {
    localStorage.removeItem("bord_token");
    localStorage.removeItem("bord_user");
  } catch(_) {}
}

// eslint-disable-next-line no-unused-vars
function readTokenPayload(token) {
  try {
    if (!token) return null;
    const [, payload] = String(token).split(".");
    if (!payload) return null;
    let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad === 2) base64 += "==";
    else if (pad === 3) base64 += "=";
    return JSON.parse(atob(base64));
  } catch(_) {
    return null;
  }
}

function getStoredUser() {
  try {
    const token = localStorage.getItem("bord_token");
    const rawUser = localStorage.getItem("bord_user");
    if (!token || !rawUser) {
      clearStoredSession();
      return null;
    }
    return JSON.parse(rawUser) || null;
  } catch(_) {
    clearStoredSession();
    return null;
  }
}

function getProductIdFromPath(pathname) {
  const match = pathname.match(/^\/products?\/([a-zA-Z0-9_-]+)/);
  if (!match) return null;
  const val = match[1];
  return /^\d+$/.test(val) ? Number(val) : val;
}

const STATUS_STEPS = ["Pending Payment","Confirmed","Processing","Packing","Shipped","Out For Delivery","Delivered","Cancelled","Return Requested"]; // no hardcoded products — all from DB

const HOMEPAGE_FAQS = [
  {
    q: "What makes BORDS oversized T-shirts heavyweight?",
    a: "BORDS T-shirts are crafted using custom-knit 240 GSM (grams per square meter) 100% combed cotton. This delivers authentic substance, zero transparency, and a structured architectural drape that holds its shape wash after wash.",
  },
  {
    q: "What is the fit of BORDS streetwear?",
    a: "Our pieces feature a true drop-shoulder relaxed oversized silhouette designed from the ground up in Bangalore. If you prefer a generous streetwear look, select your true size. For a more classic regular fit, size down by one.",
  },
  {
    q: "Where is BORDS clothing designed and manufactured?",
    a: "BORDS is founded and designed in Bangalore, India. We partner ethically with certified domestic mills using pre-shrunk organic cotton and dense collar ribbing that never bacon-curls.",
  },
  {
    q: "What is your shipping and return policy?",
    a: "We offer free shipping on orders above ₹999 across India. All orders are processed same-day or within 24 hours. We provide a hassle-free 7-day return and exchange policy if you need a different size.",
  },
];


// ── DOWNTIME SCREEN ───────────────────────────────────────────────────────────
function DowntimeScreen({ until, message }) {
  const [countdown, setCountdown] = useState("");

  useEffect(() => {
    const tick = () => {
      if (!until) { setCountdown(""); return; }
      const diff = new Date(until) - Date.now();
      if (diff <= 0) { setCountdown(""); return; }
      const totalSec = Math.floor(diff / 1000);
      const days = Math.floor(totalSec / 86400);
      const h = Math.floor((totalSec % 86400) / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      const s = totalSec % 60;
      const pad = n => String(n).padStart(2, "0");
      setCountdown(days > 0 ? `${days}d ${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}:${pad(s)}`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [until]);

  return (
    <div style={{
      position:"fixed",inset:0,zIndex:99999,
      background:"radial-gradient(circle at center, #181818 0%, #0a0a0a 70%, #050505 100%)",
      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
      fontFamily:"'DM Sans','Inter',sans-serif",color:"#f4f2ee",
      overflowY:"auto",overflowX:"hidden",
      minHeight:"100dvh",width:"100vw",
      padding:"clamp(16px, 3vh, 32px) clamp(12px, 3vw, 24px)",
      boxSizing:"border-box",
    }}>
      <div style={{position:"absolute",inset:0,opacity:.04,backgroundImage:"url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",pointerEvents:"none"}}/>
      <div style={{position:"absolute",width:"min(500px, 90vw)",height:"min(500px, 90vw)",borderRadius:"50%",border:"1px solid rgba(200,71,26,0.12)",animation:"dtPulse 3s ease-in-out infinite",pointerEvents:"none"}}/>
      <div style={{position:"absolute",width:"min(380px, 75vw)",height:"min(380px, 75vw)",borderRadius:"50%",border:"1px solid rgba(200,71,26,0.18)",animation:"dtPulse 3s ease-in-out infinite 1s",pointerEvents:"none"}}/>
      <div style={{position:"absolute",width:"min(260px, 55vw)",height:"min(260px, 55vw)",borderRadius:"50%",border:"1px solid rgba(200,71,26,0.22)",animation:"dtPulse 3s ease-in-out infinite 2s",pointerEvents:"none"}}/>
      <style>{`
        @keyframes dtPulse{0%,100%{opacity:.4;transform:scale(1)}50%{opacity:.9;transform:scale(1.04)}}
        @keyframes dtFadeIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        @keyframes dtBlink{0%,100%{opacity:1}50%{opacity:.4}}
      `}</style>
      <div style={{
        position:"relative",zIndex:1,
        display:"flex",flexDirection:"column",alignItems:"center",
        gap:"clamp(12px, 2.5vh, 22px)",
        padding:"clamp(16px, 3vw, 28px) clamp(12px, 3vw, 24px)",
        maxWidth:560,width:"100%",margin:"auto",
        textAlign:"center",animation:"dtFadeIn .6s ease both",
        boxSizing:"border-box",
      }}>
        
        {/* BORDS Brand Logo */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",maxWidth:"100%"}}>
          <Logo size="md" showTagline={true} forceDark={true} />
        </div>

        <div style={{width:80,height:1,background:"linear-gradient(90deg,transparent,rgba(200,71,26,0.7),transparent)"}}/>
        <div style={{display:"flex",alignItems:"center",gap:8,padding:"5px 16px",background:"rgba(200,71,26,0.1)",border:"1px solid rgba(200,71,26,0.25)",borderRadius:20,maxWidth:"100%"}}>
          <span style={{width:7,height:7,borderRadius:"50%",background:"#c8471a",animation:"dtBlink 1.4s ease-in-out infinite",display:"inline-block",flexShrink:0}}/>
          <span style={{fontSize:"clamp(0.58rem, 1.8vw, 0.65rem)",letterSpacing:".2em",textTransform:"uppercase",color:"#c8471a",fontWeight:600,whiteSpace:"nowrap"}}>Down for Maintenance</span>
        </div>
        <div style={{fontSize:"clamp(0.88rem, 2.4vw, 1.05rem)",color:"rgba(244,242,238,0.7)",lineHeight:1.65,maxWidth:420,fontWeight:400,wordBreak:"break-word",padding:"0 6px"}}>
          {message || "We're updating the store. Back very soon!"}
        </div>
        {countdown && (
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6,marginTop:2,maxWidth:"100%"}}>
            <div style={{fontFamily:"'Bebas Neue','Impact',sans-serif",fontSize:"clamp(2.4rem, 10vw, 4.8rem)",letterSpacing:".06em",color:"#f4f2ee",lineHeight:1.1,fontVariantNumeric:"tabular-nums",wordBreak:"break-word"}}>
              {countdown}
            </div>
            <div style={{fontSize:"clamp(0.56rem, 1.7vw, 0.62rem)",letterSpacing:".18em",textTransform:"uppercase",color:"rgba(244,242,238,0.35)",padding:"0 8px",lineHeight:1.4}}>
              Back online at{" "}
              <strong style={{color:"rgba(244,242,238,0.7)"}}>{new Date(until).toLocaleString("en-IN",{dateStyle:"medium",timeStyle:"short"})}</strong>
            </div>
          </div>
        )}
        <div style={{marginTop:8,fontSize:"clamp(0.52rem, 1.5vw, 0.62rem)",letterSpacing:".16em",textTransform:"uppercase",color:"rgba(244,242,238,0.25)"}}>Bored of Routine Designs — India</div>
      </div>
    </div>
  );
}

// ── MY ORDERS PAGE ────────────────────────────────────────────────────────────
// (SlowNetworkMsg and SlowLoadBanner removed — replaced with branded CSS splash)

function MyOrdersPage({ user, onBack, API, STATUS_STEPS, prevPage, theme, initialTab = "orders", activeLocation, onSelectLocation, onNavigate }) {
  const [orders,       setOrders]     = useState([]);
  const [loading,      setLoading]    = useState(true);
  const [error,        setError]      = useState("");
  const [expanded,     setExpanded]   = useState(null);
  const [copied,       setCopied]     = useState(null);
  const [actionOrder,  setActionOrder]= useState(null); // {id, type:'cancel'|'return'}
  const [actionReason, setActionReason]= useState("");
  const [actionCondition, setActionCondition]= useState("damaged");
  const [actionLoading,setActionLoading]=useState(false);
  const [actionMsg,    setActionMsg]  = useState({});   // {[orderId]: {ok, text}}
  const [activeTab,    setActiveTab]  = useState(initialTab || "orders");
  const [reviewDrafts, setReviewDrafts] = useState({});
  const [reviewUploading, setReviewUploading] = useState({});
  const [reviewMessages, setReviewMessages] = useState({});
  const [reviewSubmitted, setReviewSubmitted] = useState({});
  const [starHover,    setStarHover]  = useState({});
  const [deliveryDrafts, setDeliveryDrafts] = useState({});
  const [deliveryLoading, setDeliveryLoading] = useState({});
  const [deliveryQuote, setDeliveryQuote] = useState({});
  const [deliveryMessages, setDeliveryMessages] = useState({});
  const [savedAddresses, setSavedAddresses] = useState(() => {
    try {
      const raw = localStorage.getItem("bord_saved_addresses");
      return raw ? JSON.parse(raw) : [];
    } catch (_) { return []; }
  });

  // Address Management state
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [editingAddress,  setEditingAddress]  = useState(null);
  const [addressForm,     setAddressForm]     = useState({
    label: "HOME",
    address: "",
    city: "",
    pincode: "",
    landmark: "",
    is_default: false
  });
  const [addressSaving,   setAddressSaving]   = useState(false);
  const [addressError,    setAddressError]    = useState("");
  const [addressSuccess,  setAddressSuccess]  = useState("");

  // Review tracking: map of { [orderId]: reviewObject }
  const [reviewedOrders,  setReviewedOrders]  = useState(() => {
    try {
      const raw = localStorage.getItem("bord_reviewed_orders");
      return raw ? JSON.parse(raw) : {};
    } catch (_) { return {}; }
  });

  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  const isDark = theme === "dark";
  const bg = isDark ? "#0a0a0a" : "var(--bord-bg, #FAF9F6)";
  const textColor = isDark ? "#f4f2ee" : "var(--bord-fg, #141414)";
  const textMuted = isDark ? "rgba(244,242,238,0.65)" : "var(--bord-muted, #5C5C5C)";
  const textSubtle = isDark ? "rgba(244,242,238,0.35)" : "#767676";
  const borderColor = isDark ? "rgba(244,242,238,0.1)" : "rgba(0,0,0,0.1)";
  const borderSubtle = isDark ? "rgba(244,242,238,0.06)" : "rgba(0,0,0,0.07)";
  const cardBg = isDark ? "rgba(244,242,238,0.03)" : "#FFFFFF";
  const lineBg = isDark ? "rgba(244,242,238,0.08)" : "rgba(0,0,0,0.1)";

  const tabButtonStyle = (active) => ({
    border: active ? '1px solid rgba(200,71,26,0.45)' : (isDark ? '1px solid rgba(244,242,238,0.16)' : '1px solid rgba(0,0,0,0.14)'),
    background: active ? 'linear-gradient(135deg, rgba(200,71,26,0.24), rgba(200,71,26,0.12))' : (isDark ? 'rgba(244,242,238,0.05)' : 'rgba(0,0,0,0.04)'),
    color: active ? (isDark ? '#f4f2ee' : '#c8471a') : (isDark ? 'rgba(244,242,238,0.8)' : '#2B2A28'),
    padding: '10px 14px',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: '.68rem',
    letterSpacing: '.12em',
    textTransform: 'uppercase',
    transition: 'all .2s',
    fontWeight: 600,
    boxShadow: active ? '0 8px 24px rgba(200,71,26,0.16)' : 'none',
  });

  const fieldStyle = {
    width: '100%',
    background: isDark ? 'rgba(244,242,238,0.03)' : '#ffffff',
    border: isDark ? '1px solid rgba(244,242,238,0.1)' : '1px solid rgba(0,0,0,0.15)',
    borderRadius: 6,
    color: isDark ? '#f4f2ee' : '#141414',
    padding: '10px 12px',
    fontSize: '.76rem',
    boxSizing: 'border-box',
    marginBottom: 10,
    outline: 'none',
    transition: 'border-color .2s',
    fontFamily: 'inherit',
  };

  const secondaryButtonStyle = {
    background: isDark ? 'rgba(244,242,238,0.06)' : 'rgba(0,0,0,0.05)',
    border: isDark ? '1px solid rgba(244,242,238,0.14)' : '1px solid rgba(0,0,0,0.14)',
    color: isDark ? 'rgba(244,242,238,0.85)' : '#2B2A28',
    padding: '9px 13px',
    borderRadius: 7,
    fontSize: '.68rem',
    letterSpacing: '.1em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontWeight: 600,
    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
  };

  const accentButtonStyle = {
    background: '#c8471a',
    border: 'none',
    color: '#ffffff',
    padding: '10px 15px',
    borderRadius: 8,
    fontSize: '.68rem',
    letterSpacing: '.12em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontWeight: 700,
    boxShadow: '0 10px 24px rgba(200,71,26,0.18)',
  };

  const loadOrders = () => {
    if (!user?.email) return;
    setLoading(true);
    setError("");
    const params = new URLSearchParams({ email: user.email });
    const token = localStorage.getItem("bord_token");
    fetch(`${API}/orders/my?${params.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then(r => r.json())
      .then(({ data }) => setOrders(toUpper(data || [])))
      .catch(() => setError("Could not load orders. Please refresh."))
      .finally(() => setLoading(false));
  };
  useEffect(loadOrders, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // When orders load or change, synchronize review status from order.IS_REVIEWED and order.REVIEW_DATA
  useEffect(() => {
    if (!orders?.length) return;
    const patch = {};
    orders.forEach(o => {
      const isRev = o.IS_REVIEWED || o.is_reviewed || Boolean(o.REVIEW_DATA || o.review_data);
      if (isRev) {
        patch[o.ID] = o.REVIEW_DATA || o.review_data || { rating: 5, review: "", reviewed: true };
      }
    });
    if (Object.keys(patch).length > 0) {
      setReviewedOrders(prev => {
        const next = { ...prev, ...patch };
        try { localStorage.setItem("bord_reviewed_orders", JSON.stringify(next)); } catch(_) {}
        return next;
      });
    }
  }, [orders]);

  // Query GET /api/reviews/my to fetch any verified user reviews
  useEffect(() => {
    if (!user?.email && !user?.id) return;
    const params = new URLSearchParams();
    if (user.email) params.set("email", user.email);
    if (user.id) params.set("user_id", user.id);
    fetch(`${API}/reviews/my?${params.toString()}`)
      .then(r => r.ok ? r.json() : { data: [] })
      .then(({ data }) => {
        if (data && data.length > 0) {
          const revMap = {};
          data.forEach(r => {
            const ordId = r.ORDER_ID || r.order_id;
            if (ordId) revMap[ordId] = r;
          });
          setReviewedOrders(prev => {
            const next = { ...prev, ...revMap };
            try { localStorage.setItem("bord_reviewed_orders", JSON.stringify(next)); } catch(_) {}
            return next;
          });
        }
      })
      .catch(() => {});
  }, [API, user?.email, user?.id]);

  const hasReviewed = (order) => {
    if (!order) return false;
    if (reviewSubmitted[order.ID]) return true;
    if (reviewedOrders[order.ID]) return true;
    if (order.IS_REVIEWED || order.is_reviewed) return true;
    if (order.REVIEW_DATA || order.review_data) return true;
    return false;
  };

  const getReview = (order) => {
    if (!order) return null;
    return reviewedOrders[order.ID] || order.REVIEW_DATA || order.review_data || null;
  };

  // Address fetching and management
  const fetchAddresses = useCallback(() => {
    const userId = user?.id || user?.ID;
    if (!userId) return;
    fetch(`${API}/delivery/addresses?user_id=${userId}&_t=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache", ...(localStorage.getItem("bord_token") ? { Authorization: `Bearer ${localStorage.getItem("bord_token")}` } : {}) }
    })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(({ data }) => {
        const list = data || [];
        setSavedAddresses(list);
        try { localStorage.setItem('bord_saved_addresses', JSON.stringify(list)); } catch(_) {}
      })
      .catch(() => {});
  }, [API, user?.id, user?.ID]);

  useEffect(() => {
    fetchAddresses();
    const handleAddrEvt = (e) => {
      if (e.detail && Array.isArray(e.detail)) {
        setSavedAddresses(e.detail);
      }
    };
    window.addEventListener('bord_address_updated', handleAddrEvt);
    return () => window.removeEventListener('bord_address_updated', handleAddrEvt);
  }, [fetchAddresses]);

  const handleOpenAddAddress = () => {
    setEditingAddress(null);
    setAddressForm({
      label: "HOME",
      address: "",
      city: "",
      pincode: "",
      landmark: "",
      is_default: savedAddresses.length === 0
    });
    setAddressError("");
    setAddressSuccess("");
    setIsAddingAddress(true);
  };

  const handleOpenEditAddress = (addr) => {
    setEditingAddress(addr);
    const addrId = addr.id || addr.ID;
    const addrText = addr.address || addr.ADDRESS || "";
    const addrPin = addr.pincode || addr.PINCODE || "";
    const isCurDefault = Boolean(
      (activeLocation && (
        (activeLocation.id && (String(activeLocation.id) === String(addrId) || String(activeLocation.id) === String(addr.id) || String(activeLocation.id) === String(addr.ID))) ||
        (activeLocation.address === addrText && activeLocation.pincode === addrPin)
      )) ||
      (!activeLocation && savedAddresses.length > 0 && (String(savedAddresses[0]?.id || savedAddresses[0]?.ID) === String(addrId)))
    );
    setAddressForm({
      label: (addr.label || addr.LABEL || "HOME").toUpperCase(),
      address: addrText,
      city: addr.city || addr.CITY || "",
      pincode: addrPin,
      landmark: addr.landmark || addr.LANDMARK || "",
      is_default: isCurDefault
    });
    setAddressError("");
    setAddressSuccess("");
    setIsAddingAddress(true);
  };

  const handleSaveAddress = async (e) => {
    if (e) e.preventDefault();
    const userId = user?.id || user?.ID;
    if (!userId) {
      setAddressError("Please log in to save addresses.");
      return;
    }
    const cleanAddress = (addressForm.address || "").trim();
    const cleanCity = (addressForm.city || "").trim();
    const cleanPincode = (addressForm.pincode || "").trim();
    const cleanLandmark = (addressForm.landmark || "").trim();
    const label = (addressForm.label || "HOME").toUpperCase();

    if (!cleanAddress || cleanAddress.length < 5) {
      setAddressError("Please enter a complete street / flat address.");
      return;
    }
    if (!cleanCity || cleanCity.length < 2) {
      setAddressError("Please enter your city.");
      return;
    }
    if (!/^\d{6}$/.test(cleanPincode)) {
      setAddressError("Please enter a valid 6-digit postal pincode.");
      return;
    }

    setAddressSaving(true);
    setAddressError("");
    try {
      let savedRecord = null;
      if (editingAddress?.id || editingAddress?.ID) {
        const id = editingAddress.id || editingAddress.ID;
        const res = await fetch(`${API}/delivery/addresses/${id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...(localStorage.getItem("bord_token") ? { Authorization: `Bearer ${localStorage.getItem("bord_token")}` } : {}) },
          body: JSON.stringify({
            user_id: userId,
            label,
            address: cleanAddress,
            city: cleanCity,
            pincode: cleanPincode,
            landmark: cleanLandmark
          })
        });
        if (!res.ok) throw new Error("Failed to update address");
        const json = await res.json();
        savedRecord = json.data || { ...editingAddress, id, label, address: cleanAddress, city: cleanCity, pincode: cleanPincode, landmark: cleanLandmark };
        setSavedAddresses(prev => {
          const updated = prev.map(a => (String(a.id || a.ID) === String(id)) ? savedRecord : a);
          try { localStorage.setItem('bord_saved_addresses', JSON.stringify(updated)); } catch(_) {}
          try { window.dispatchEvent(new CustomEvent('bord_address_updated', { detail: updated })); } catch(_) {}
          return updated;
        });
        if (addressForm.is_default && onSelectLocation) {
          onSelectLocation(savedRecord);
        }
      } else {
        const res = await fetch(`${API}/delivery/addresses`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(localStorage.getItem("bord_token") ? { Authorization: `Bearer ${localStorage.getItem("bord_token")}` } : {}) },
          body: JSON.stringify({
            user_id: userId,
            label,
            address: cleanAddress,
            city: cleanCity,
            pincode: cleanPincode,
            landmark: cleanLandmark
          })
        });
        if (!res.ok) throw new Error("Failed to save address");
        const json = await res.json();
        savedRecord = json.data || { id: Date.now(), label, address: cleanAddress, city: cleanCity, pincode: cleanPincode, landmark: cleanLandmark };
        setSavedAddresses(prev => {
          const filtered = prev.filter(a => String(a.id || a.ID) !== String(savedRecord.id || savedRecord.ID));
          const updated = [savedRecord, ...filtered].slice(0, 6);
          try { localStorage.setItem('bord_saved_addresses', JSON.stringify(updated)); } catch(_) {}
          try { window.dispatchEvent(new CustomEvent('bord_address_updated', { detail: updated })); } catch(_) {}
          return updated;
        });
        if ((addressForm.is_default || savedAddresses.length === 0) && onSelectLocation) {
          onSelectLocation(savedRecord);
        }
      }
      fetchAddresses();
      setIsAddingAddress(false);
      setEditingAddress(null);
      setAddressSuccess("✓ Address saved successfully!");
      setTimeout(() => setAddressSuccess(""), 4000);
    } catch (err) {
      setAddressError(err.message || "Unable to save address. Please try again.");
    } finally {
      setAddressSaving(false);
    }
  };

  const handleDeleteAddress = async (addrId) => {
    if (!window.confirm("Are you sure you want to remove this delivery address?")) return;
    const userId = user?.id || user?.ID;

    // 1. Immediate optimistic UI update: Remove from state and localStorage instantly
    let remaining = [];
    setSavedAddresses(prev => {
      remaining = prev.filter(a => String(a.id || a.ID) !== String(addrId));
      try { localStorage.setItem('bord_saved_addresses', JSON.stringify(remaining)); } catch(_) {}
      try { window.dispatchEvent(new CustomEvent('bord_address_updated', { detail: remaining })); } catch(_) {}
      return remaining;
    });

    // 2. If active location was the deleted address, reassign or clear immediately
    if (activeLocation && (
      String(activeLocation.id || activeLocation.ID) === String(addrId) ||
      String(activeLocation.address || activeLocation.ADDRESS || '') === String(savedAddresses.find(a => String(a.id || a.ID) === String(addrId))?.address || '')
    )) {
      const nextLoc = remaining.length > 0 ? remaining[0] : null;
      if (onSelectLocation) {
        onSelectLocation(nextLoc);
      } else {
        try {
          if (nextLoc) localStorage.setItem("bord_active_location", JSON.stringify(nextLoc));
          else localStorage.removeItem("bord_active_location");
        } catch(_) {}
      }
    }

    setAddressSuccess("Address removed.");
    setTimeout(() => setAddressSuccess(""), 3000);

    // 3. Perform backend deletion with cache busting
    try {
      const res = await fetch(`${API}/delivery/addresses/${addrId}?user_id=${userId || ''}&_t=${Date.now()}`, {
        method: "DELETE",
        headers: { "Cache-Control": "no-cache", ...(localStorage.getItem("bord_token") ? { Authorization: `Bearer ${localStorage.getItem("bord_token")}` } : {}) }
      });
      if (!res.ok) throw new Error("Failed to delete address");
      // Background sync
      fetchAddresses();
    } catch (err) {
      setAddressError(err.message || "Failed to remove address.");
      // Rollback on error
      fetchAddresses();
    }
  };

  const handleSetActiveAddress = (addr) => {
    if (onSelectLocation) {
      onSelectLocation({
        id: addr.id || addr.ID,
        address: addr.address || addr.ADDRESS,
        city: addr.city || addr.CITY,
        pincode: addr.pincode || addr.PINCODE,
        landmark: addr.landmark || addr.LANDMARK,
        label: addr.label || addr.LABEL || 'HOME'
      });
      try { window.dispatchEvent(new CustomEvent('bord_address_updated', { detail: savedAddresses })); } catch(_) {}
      setAddressSuccess(`✓ Primary delivery address updated to ${addr.label || addr.LABEL || 'Home'} (${addr.city || addr.CITY || ''})`);
      setTimeout(() => setAddressSuccess(""), 3000);
    }
  };

  const reviewableOrders = orders.filter(order => String(order.STATUS || '').toUpperCase() === 'DELIVERED');
  const pendingReviewCount = reviewableOrders.filter(o => !hasReviewed(o)).length;

  const updateReviewDraft = (orderId, patch) => {
    setReviewDrafts(prev => ({ ...prev, [orderId]: { ...(prev[orderId] || {}), ...patch } }));
  };

  const uploadReviewImages = async (orderId, files) => {
    if (!files?.length) return [];
    setReviewUploading(prev => ({ ...prev, [orderId]: true }));
    try {
      const urls = [];
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('image', file);
        const res = await fetch(`${API}/upload`, { method: 'POST', body: formData });
        const data = await res.json();
        if (!res.ok || !data?.url) throw new Error('Image upload failed');
        urls.push(data.url);
      }
      return urls;
    } finally {
      setReviewUploading(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handleFileSelect = (orderId, e) => {
    const files = Array.from(e.target.files || []).slice(0, 5); // max 5 images
    const previews = files.map(f => ({ file: f, preview: URL.createObjectURL(f), name: f.name }));
    updateReviewDraft(orderId, { files, filePreviews: previews });
  };

  const removePreview = (orderId, idx) => {
    const draft = reviewDrafts[orderId] || {};
    const files = [...(draft.files || [])].filter((_, i) => i !== idx);
    const previews = [...(draft.filePreviews || [])].filter((_, i) => i !== idx);
    updateReviewDraft(orderId, { files, filePreviews: previews });
  };

  const handleReviewSubmit = async (order) => {
    const draft = reviewDrafts[order.ID] || {};
    if (!draft.rating) {
      setReviewMessages(prev => ({ ...prev, [order.ID]: { ok: false, text: 'Please choose a star rating first.' } }));
      return;
    }
    setReviewMessages(prev => ({ ...prev, [order.ID]: { ok: null, text: draft.files?.length ? 'Uploading photos…' : 'Submitting review…' } }));
    try {
      const uploadedImages = await uploadReviewImages(order.ID, draft.files || []);
      const itemsList = order.ITEMS || order.items || [];
      const firstItem = itemsList.length > 0 ? itemsList[0] : null;
      const targetProductName = (draft.product_name || firstItem?.name || firstItem?.product_name || '').trim();
      const targetProductId = draft.product_id || firstItem?.product_id || firstItem?.id || null;

      const res = await fetch(`${API}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: order.ID || order.ORDER_ID,
          rating: draft.rating,
          review: draft.review || '',
          images: uploadedImages,
          product_name: targetProductName,
          product_id: targetProductId,
          user_id: user?.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.already_reviewed) {
          const revData = data.data || { rating: draft.rating, review: draft.review, created_at: new Date().toISOString() };
          setReviewedOrders(prev => {
            const next = { ...prev, [order.ID]: revData };
            try { localStorage.setItem("bord_reviewed_orders", JSON.stringify(next)); } catch(_) {}
            return next;
          });
          setReviewSubmitted(prev => ({ ...prev, [order.ID]: true }));
          throw new Error('You have already submitted a review for this order.');
        }
        throw new Error(data.error || 'Review submission failed');
      }
      const savedRev = data.data || { rating: draft.rating, review: draft.review, created_at: new Date().toISOString() };
      setReviewMessages(prev => ({ ...prev, [order.ID]: { ok: true, text: '✓ Review submitted! Thank you for your feedback.' } }));
      setReviewSubmitted(prev => ({ ...prev, [order.ID]: true }));
      setReviewedOrders(prev => {
        const next = { ...prev, [order.ID]: savedRev };
        try { localStorage.setItem("bord_reviewed_orders", JSON.stringify(next)); } catch(_) {}
        return next;
      });
      setReviewDrafts(prev => ({ ...prev, [order.ID]: { rating: 0, review: '', product_name: '', files: [], filePreviews: [] } }));
    } catch (err) {
      setReviewMessages(prev => ({ ...prev, [order.ID]: { ok: false, text: err.message || 'Unable to submit review. Try again.' } }));
    }
  };

  const getDeliveryDraft = (orderId) => deliveryDrafts[orderId] || { open: false, address: '', city: '', pincode: '', landmark: '', lat: '', lng: '', save_address: false, usingSaved: false };

  const updateDeliveryDraft = (orderId, patch) => {
    setDeliveryDrafts(prev => ({ ...prev, [orderId]: { ...getDeliveryDraft(orderId), ...patch } }));
  };

  const handleDeliveryQuote = async (orderId) => {
    const draft = getDeliveryDraft(orderId);
    setDeliveryLoading(prev => ({ ...prev, [orderId]: true }));
    try {
      const res = await fetch(`${API}/delivery/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: draft.address, city: draft.city, pincode: draft.pincode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to quote delivery');
      setDeliveryQuote(prev => ({ ...prev, [orderId]: data.data }));
      setDeliveryMessages(prev => ({ ...prev, [orderId]: { ok: true, text: `Cheapest partner: ${data.data.partner}` } }));
    } catch (err) {
      setDeliveryMessages(prev => ({ ...prev, [orderId]: { ok: false, text: err.message || 'Unable to fetch quote.' } }));
    } finally {
      setDeliveryLoading(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handleLocationLookup = (orderId) => {
    if (!navigator.geolocation) {
      setDeliveryMessages(prev => ({ ...prev, [orderId]: { ok: false, text: 'Location access is not supported in this browser.' } }));
      return;
    }
    navigator.geolocation.getCurrentPosition((pos) => {
      updateDeliveryDraft(orderId, { lat: pos.coords.latitude, lng: pos.coords.longitude });
      setDeliveryMessages(prev => ({ ...prev, [orderId]: { ok: true, text: 'Current location captured for the delivery pin.' } }));
    }, () => {
      setDeliveryMessages(prev => ({ ...prev, [orderId]: { ok: false, text: 'Location permission was denied.' } }));
    }, { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 });
  };

  const handleDeliverySubmit = async (order) => {
    const draft = getDeliveryDraft(order.ID);
    if (!draft.address || !draft.city || !draft.pincode) {
      setDeliveryMessages(prev => ({ ...prev, [order.ID]: { ok: false, text: 'Please add your address, city and pincode.' } }));
      return;
    }
    setDeliveryLoading(prev => ({ ...prev, [order.ID]: true }));
    try {
      const quote = deliveryQuote[order.ID] || {};
      const res = await fetch(`${API}/delivery/details`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('bord_token') ? { Authorization: `Bearer ${localStorage.getItem('bord_token')}` } : {}) },
        body: JSON.stringify({
          order_id: order.ID,
          address: draft.address,
          city: draft.city,
          pincode: draft.pincode,
          landmark: draft.landmark,
          lat: draft.lat,
          lng: draft.lng,
          partner: quote.partner,
          eta: quote.eta,
          charge: quote.charge,
          save_address: draft.save_address,
          user_id: user?.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Delivery details could not be saved');
      setDeliveryMessages(prev => ({ ...prev, [order.ID]: { ok: true, text: `Saved. Cheapest partner: ${data.data?.partner_name || quote.partner || 'BORD Express'}` } }));
      updateDeliveryDraft(order.ID, { open: false });
    } catch (err) {
      setDeliveryMessages(prev => ({ ...prev, [order.ID]: { ok: false, text: err.message || 'Delivery details could not be saved.' } }));
    } finally {
      setDeliveryLoading(prev => ({ ...prev, [order.ID]: false }));
    }
  };

  // Auto-poll every 30 seconds so status updates appear without manual refresh
  useEffect(() => {
    if (!user?.email) return;
    const interval = setInterval(loadOrders, 30000);
    return () => clearInterval(interval);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const statusColor = (s) => {
    if (!s) return "rgba(244,242,238,0.3)"; s = s.toUpperCase();
    if (s === "DELIVERED")   return "#44c864";
    if (s === "CANCELLED")   return "#ff6b6b";
    if (s === "RETURN REQUESTED") return "#f5c842";
    if (s.includes("SHIP") || s.includes("OUT FOR")) return "#c8471a";
    return "rgba(244,242,238,0.5)";
  };
  const statusBg = (s) => {
    if (!s) return "rgba(244,242,238,0.05)"; s = s.toUpperCase();
    if (s === "DELIVERED")   return "rgba(68,200,100,0.08)";
    if (s === "CANCELLED")   return "rgba(255,107,107,0.08)";
    if (s === "RETURN REQUESTED") return "rgba(245,200,66,0.08)";
    if (s.includes("SHIP") || s.includes("OUT FOR")) return "rgba(200,71,26,0.1)";
    return "rgba(244,242,238,0.04)";
  };
  const copyText = (text, key) => {
    navigator.clipboard.writeText(text); setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };
  const canCancel  = (s) => ["PENDING PAYMENT","CONFIRMED","PROCESSING","PACKING"].includes((s||"").toUpperCase());
  const postShip   = (s) => ["SHIPPED","OUT FOR DELIVERY"].includes((s||"").toUpperCase());
  const isDelivered= (s) => (s||"").toUpperCase() === "DELIVERED";
  const isReturn   = (s) => (s||"").toUpperCase() === "RETURN REQUESTED";
  const isCancelled= (s) => (s||"").toUpperCase() === "CANCELLED";

  const deliveredDaysAgo = (deliveredAt, createdAt) => {
    let date = deliveredAt ? new Date(deliveredAt) : null;
    if (!date || isNaN(date.getTime())) {
      if (createdAt) {
        date = new Date(new Date(createdAt).getTime() + 4 * 24 * 60 * 60 * 1000);
      } else {
        return 999;
      }
    }
    return (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
  };

  const submitAction = async () => {
    if (!actionOrder) return;
    setActionLoading(true);
    try {
      const url = `${API}/orders/${actionOrder.id}/${actionOrder.type}`;
      let payload = actionOrder.type === 'return'
        ? { reason: actionReason, item_condition: actionCondition }
        : { reason: actionReason };
      
      if (actionOrder.type === 'return' && actionOrder.order?.DELIVERY_DETAILS) {
        payload.pickup_lat = actionOrder.order.DELIVERY_DETAILS.lat || null;
        payload.pickup_lng = actionOrder.order.DELIVERY_DETAILS.lng || null;
      }

      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        setActionMsg(p => ({...p, [actionOrder.id]: { ok:false, text: data.error || 'Failed.' }}));
      } else {
        let text = actionOrder.type==='cancel' ? '✓ Order cancelled successfully.' : '✓ Return request submitted.';
        if (data.reverse_awb) text += ` AWB: ${data.reverse_awb}.`;
        if (data.pickup_date) text += ` Pickup Date: ${data.pickup_date}.`;
        setActionMsg(p => ({...p, [actionOrder.id]: { ok:true, text }}));
        loadOrders();
      }
    } catch(e) {
      setActionMsg(p => ({...p, [actionOrder.id]: { ok:false, text: 'Network error.' }}));
    }
    setActionLoading(false);
    setActionOrder(null);
    setActionReason("");
    setActionCondition("damaged");
  };

  return (
    <>
      <div style={{minHeight:"100vh",background:bg,color:textColor,fontFamily:"'DM Sans',sans-serif"}}>
      {/* CONFIRM MODAL */}
      {actionOrder && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}
          onClick={e=>{ if(e.target===e.currentTarget){ setActionOrder(null); setActionReason(""); setActionCondition("damaged"); } }}>
          <div style={{background:isDark ? "#131313" : "#FFFFFF",border:`1px solid ${borderColor}`,borderRadius:10,padding:32,maxWidth:420,width:"100%",boxShadow:"0 16px 40px rgba(0,0,0,0.25)"}}>
            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.2rem",letterSpacing:".1em",marginBottom:8,color: actionOrder.type==="cancel" ? (isDark ? "#ff8080" : "#c83232") : (isDark ? "#f5c842" : "#a87100")}}>
              {actionOrder.type==="cancel" ? "Cancel Order" : "Request Return"}
            </div>
            <div style={{fontSize:".82rem",color:textMuted,marginBottom:20,lineHeight:1.6}}>
              {actionOrder.type==="cancel"
                ? "Are you sure? This cannot be undone. Please provide a reason (optional)."
                : "Returns are available for damaged or defective items within 7 days of delivery. Please tell us what happened:"}
            </div>
            {actionOrder.type === 'return' && (
              <div style={{marginBottom:12}}>
                <label style={{display:"block",fontSize:".72rem",letterSpacing:".12em",textTransform:"uppercase",color:textMuted,marginBottom:8}}>Issue Type</label>
                <select value={actionCondition} onChange={e=>setActionCondition(e.target.value)} style={{width:"100%",background:isDark ? "rgba(244,242,238,0.04)" : "#FAFAFA",border:`1px solid ${borderColor}`,borderRadius:6,padding:"10px 14px",color:textColor,fontSize:".82rem",fontFamily:"'DM Sans',sans-serif",boxSizing:"border-box"}}>
                  <option value="damaged" style={{color:"#111"}}>Damaged item</option>
                  <option value="defective" style={{color:"#111"}}>Defective item</option>
                  <option value="wrong_size" style={{color:"#111"}}>Wrong size</option>
                  <option value="other" style={{color:"#111"}}>Other issue</option>
                </select>
                
                {actionOrder.order?.DELIVERY_DETAILS?.lat && actionOrder.order?.DELIVERY_DETAILS?.lng && (
                  <div style={{marginTop: 12}}>
                    <label style={{display:"block",fontSize:".72rem",letterSpacing:".12em",textTransform:"uppercase",color:textMuted,marginBottom:8}}>Pickup Location Confirmation</label>
                    <ReturnPickupMap lat={actionOrder.order.DELIVERY_DETAILS.lat} lng={actionOrder.order.DELIVERY_DETAILS.lng} />
                  </div>
                )}
              </div>
            )}
            <textarea value={actionReason} onChange={e=>setActionReason(e.target.value)}
              placeholder={actionOrder.type==="cancel" ? "Reason for cancellation..." : "Briefly describe the issue..."}
              rows={3} style={{width:"100%",background:isDark ? "rgba(244,242,238,0.04)" : "#FAFAFA",border:`1px solid ${borderColor}`,
                borderRadius:6,padding:"10px 14px",color:textColor,fontSize:".82rem",fontFamily:"'DM Sans',sans-serif",
                resize:"none",outline:"none",boxSizing:"border-box",marginBottom:20}} />
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>{setActionOrder(null);setActionReason("");setActionCondition("damaged");}}
                style={{flex:1,background:isDark ? "rgba(244,242,238,0.06)" : "rgba(0,0,0,0.05)",border:`1px solid ${borderColor}`,color:textMuted,
                  padding:"11px",borderRadius:5,fontSize:".72rem",letterSpacing:".1em",textTransform:"uppercase",cursor:"pointer",fontWeight:600}}>
                Go Back
              </button>
              <button onClick={submitAction} disabled={actionLoading}
                style={{flex:1,background: actionOrder.type==="cancel" ? (isDark ? "rgba(255,80,80,0.15)" : "#d32f2f") : "#c8471a",
                  border:`1px solid ${actionOrder.type==="cancel" ? (isDark ? "rgba(255,80,80,0.4)" : "#d32f2f") : "#c8471a"}`,
                  color: isDark && actionOrder.type==="cancel" ? "#ff8080" : "#ffffff",
                  padding:"11px",borderRadius:5,fontSize:".72rem",letterSpacing:".1em",textTransform:"uppercase",cursor:"pointer",fontWeight:700,opacity:actionLoading ? 0.6 : 1}}>
                {actionLoading ? "..." : actionOrder.type==="cancel" ? "Confirm Cancel" : "Submit Return"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NAV */}
      <div style={{display:"flex",alignItems:"center",gap:16,padding:"16px 24px",borderBottom:`1px solid ${borderColor}`,flexWrap:"wrap"}}>
        <button onClick={onBack} className="nav-user-btn"
          aria-label="Close and return"
          title="Close"
          style={{display:"flex",alignItems:"center",gap:6,borderRadius:999,padding:"8px 16px 8px 12px",cursor:"pointer"}}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          Close
        </button>
        <Logo size="sm" showTagline={false} />
      </div>

      <div style={{maxWidth:760,margin:"0 auto",padding:"40px 20px"}}>
        <div style={{marginBottom:28,display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
          <div>
            <div style={{fontSize:".65rem",letterSpacing:".3em",textTransform:"uppercase",color:"#c8471a",marginBottom:6}}>Account</div>
            <h1 style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"clamp(2rem,5vw,3rem)",letterSpacing:".08em",margin:0}}>My Orders</h1>
            <p style={{fontSize:".78rem",color:textMuted,margin:"6px 0 0"}}>
              Signed in as <strong style={{color:textColor}}>{user?.email}</strong>
            </p>
          </div>
          <button
            onClick={loadOrders}
            disabled={loading}
            style={{display:"flex",alignItems:"center",gap:7,background:isDark ? "rgba(244,242,238,0.04)" : "rgba(0,0,0,0.04)",
              border:`1px solid ${borderColor}`,borderRadius:6,
              color:textMuted,padding:"9px 16px",fontSize:".68rem",
              letterSpacing:".1em",textTransform:"uppercase",cursor:"pointer",
              opacity:loading?0.5:1,transition:"all .2s",marginTop:8}}
            onMouseEnter={e=>{if(!loading){e.currentTarget.style.borderColor="rgba(200,71,26,0.4)";e.currentTarget.style.color="#c8471a";}}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor=borderColor;e.currentTarget.style.color=textMuted;}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              style={{animation:loading?"spin 1s linear infinite":"none"}}>
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
            {loading ? "Refreshing…" : "Refresh Status"}
          </button>
        </div>

        {loading && (
          <div style={{display:"flex",alignItems:"center",gap:12,color:textMuted,fontSize:".82rem",padding:"40px 0"}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{animation:"spin 1s linear infinite"}}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            Loading your orders…
          </div>
        )}
        {error && <div style={{color:"#ff8080",padding:16,background:"rgba(255,80,80,0.08)",borderRadius:8,fontSize:".82rem"}}>{error}</div>}

        {!loading && !error && (
          <div style={{display:"flex",gap:10,marginBottom:24,flexWrap:"wrap"}}>
            <button onClick={() => { setActiveTab('orders'); if (onNavigate) onNavigate('myorders', null, { tab: 'orders' }); }} style={tabButtonStyle(activeTab === 'orders')}>
              {activeTab === 'orders' ? '★' : '☆'} My Orders ({orders.length})
            </button>
            <button onClick={() => { setActiveTab('addresses'); if (onNavigate) onNavigate('myorders', null, { tab: 'addresses' }); }} style={tabButtonStyle(activeTab === 'addresses')}>
              {activeTab === 'addresses' ? '★' : '☆'} Saved Addresses ({savedAddresses.length})
            </button>
            <button onClick={() => { setActiveTab('reviews'); if (onNavigate) onNavigate('myorders', null, { tab: 'reviews' }); }} style={tabButtonStyle(activeTab === 'reviews')}>
              {activeTab === 'reviews' ? '★' : '☆'} Reviews {pendingReviewCount > 0 ? `(${pendingReviewCount} pending)` : (reviewableOrders.length > 0 ? `(${reviewableOrders.length})` : '')}
            </button>
          </div>
        )}

        {!loading && !error && activeTab === "addresses" && (
          <div style={{ display: "grid", gap: 20 }}>
            {/* Header / Actions bar */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
              padding: "16px 20px",
              background: cardBg,
              border: `1px solid ${borderColor}`,
              borderRadius: 10
            }}>
              <div>
                <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: "1.25rem", letterSpacing: ".08em", color: textColor }}>
                  Saved Delivery Addresses
                </div>
                <div style={{ fontSize: ".72rem", color: textMuted, marginTop: 2 }}>
                  Manage delivery addresses for effortless one-tap checkout. (Max 6 addresses)
                </div>
              </div>

              {savedAddresses.length < 6 && !isAddingAddress && (
                <button
                  onClick={handleOpenAddAddress}
                  style={{
                    ...accentButtonStyle,
                    padding: "9px 18px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: ".7rem"
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Add New Address
                </button>
              )}
            </div>

            {/* Notification messages */}
            {addressSuccess && (
              <div style={{
                padding: "12px 16px",
                background: "rgba(68,200,100,0.08)",
                border: "1px solid rgba(68,200,100,0.25)",
                borderRadius: 8,
                color: "#44c864",
                fontSize: ".78rem",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 8
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                {addressSuccess}
              </div>
            )}

            {addressError && (
              <div style={{
                padding: "12px 16px",
                background: "rgba(255,80,80,0.08)",
                border: "1px solid rgba(255,80,80,0.25)",
                borderRadius: 8,
                color: "#ff8080",
                fontSize: ".78rem",
                display: "flex",
                alignItems: "center",
                gap: 8
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {addressError}
              </div>
            )}

            {/* Add / Edit Address Form */}
            {isAddingAddress && (
              <div style={{
                padding: 24,
                background: cardBg,
                border: "1px solid rgba(200,71,26,0.3)",
                borderRadius: 10,
                boxShadow: "0 10px 30px rgba(0,0,0,0.06)"
              }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 18,
                  paddingBottom: 12,
                  borderBottom: `1px solid ${borderColor}`
                }}>
                  <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: "1.1rem", letterSpacing: ".08em", color: textColor }}>
                    {editingAddress ? "Edit Delivery Address" : "Add New Delivery Address"}
                  </div>
                  <button
                    onClick={() => { setIsAddingAddress(false); setEditingAddress(null); setAddressError(""); }}
                    style={{ background: "none", border: "none", color: textMuted, cursor: "pointer", padding: 4 }}
                  >
                    ✕
                  </button>
                </div>

                <form onSubmit={handleSaveAddress}>
                  {/* Tag label selector: HOME | WORK | OTHER */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: ".62rem", letterSpacing: ".15em", textTransform: "uppercase", color: textMuted, marginBottom: 8, fontWeight: 600 }}>
                      Address Type
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      {["HOME", "WORK", "OTHER"].map(tag => {
                        const isSel = (addressForm.label || "HOME").toUpperCase() === tag;
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => setAddressForm(f => ({ ...f, label: tag }))}
                            style={{
                              padding: "7px 16px",
                              borderRadius: 6,
                              fontSize: ".68rem",
                              fontWeight: 600,
                              letterSpacing: ".08em",
                              cursor: "pointer",
                              transition: "all .15s",
                              border: isSel ? "1px solid #c8471a" : `1px solid ${borderColor}`,
                              background: isSel ? "rgba(200,71,26,0.12)" : (isDark ? "rgba(244,242,238,0.04)" : "#fff"),
                              color: isSel ? "#c8471a" : textColor
                            }}
                          >
                            {tag === "HOME" ? "🏠 Home" : (tag === "WORK" ? "🏢 Work" : "📍 Other")}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Street Address */}
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontSize: ".62rem", letterSpacing: ".15em", textTransform: "uppercase", color: textMuted, marginBottom: 6, fontWeight: 600 }}>
                      Flat, House No., Building, Street *
                    </label>
                    <textarea
                      required
                      rows={2}
                      value={addressForm.address}
                      onChange={e => setAddressForm(f => ({ ...f, address: e.target.value }))}
                      placeholder="e.g. Flat 402, Royale Meadows, 12th Main Rd, Indiranagar"
                      style={{ ...fieldStyle, resize: "vertical", minHeight: 60 }}
                    />
                  </div>

                  {/* Landmark */}
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontSize: ".62rem", letterSpacing: ".15em", textTransform: "uppercase", color: textMuted, marginBottom: 6, fontWeight: 600 }}>
                      Landmark (Optional)
                    </label>
                    <input
                      type="text"
                      value={addressForm.landmark}
                      onChange={e => setAddressForm(f => ({ ...f, landmark: e.target.value }))}
                      placeholder="e.g. Opposite BDA Complex"
                      style={fieldStyle}
                    />
                  </div>

                  {/* City and Pincode */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                    <div>
                      <label style={{ display: "block", fontSize: ".62rem", letterSpacing: ".15em", textTransform: "uppercase", color: textMuted, marginBottom: 6, fontWeight: 600 }}>
                        City *
                      </label>
                      <input
                        type="text"
                        required
                        value={addressForm.city}
                        onChange={e => setAddressForm(f => ({ ...f, city: e.target.value }))}
                        placeholder="e.g. Bengaluru"
                        style={fieldStyle}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: ".62rem", letterSpacing: ".15em", textTransform: "uppercase", color: textMuted, marginBottom: 6, fontWeight: 600 }}>
                        Pincode (6 digits) *
                      </label>
                      <input
                        type="text"
                        required
                        maxLength={6}
                        value={addressForm.pincode}
                        onChange={e => setAddressForm(f => ({ ...f, pincode: e.target.value.replace(/\D/g, "") }))}
                        placeholder="e.g. 560038"
                        style={fieldStyle}
                      />
                    </div>
                  </div>

                  {/* Set as active delivery location checkbox */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
                    <input
                      type="checkbox"
                      id="addr-is-default"
                      checked={addressForm.is_default}
                      onChange={e => setAddressForm(f => ({ ...f, is_default: e.target.checked }))}
                      style={{ cursor: "pointer", accentColor: "#c8471a" }}
                    />
                    <label htmlFor="addr-is-default" style={{ fontSize: ".74rem", color: textColor, cursor: "pointer" }}>
                      Set as primary delivery address for orders
                    </label>
                  </div>

                  {/* Form buttons */}
                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      onClick={() => { setIsAddingAddress(false); setEditingAddress(null); setAddressError(""); }}
                      style={{ ...secondaryButtonStyle, padding: "9px 18px" }}
                      disabled={addressSaving}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{ ...accentButtonStyle, padding: "9px 22px", display: "inline-flex", alignItems: "center", gap: 6 }}
                      disabled={addressSaving}
                    >
                      {addressSaving ? "Saving…" : (editingAddress ? "Update Address" : "Save Address")}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Saved addresses list / cards */}
            {savedAddresses.length === 0 && !isAddingAddress ? (
              <div style={{
                padding: 48,
                background: cardBg,
                border: `1px dashed ${borderColor}`,
                borderRadius: 10,
                textAlign: "center"
              }}>
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke={textSubtle} strokeWidth="1.2" style={{ marginBottom: 12 }}>
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
                </svg>
                <div style={{ fontSize: ".95rem", color: textColor, fontWeight: 600, marginBottom: 6 }}>
                  No saved addresses found
                </div>
                <div style={{ fontSize: ".76rem", color: textMuted, maxWidth: 360, margin: "0 auto 20px auto", lineHeight: 1.5 }}>
                  Save your home, work or studio address for rapid 1-click checkout and automated courier routing.
                </div>
                <button
                  onClick={handleOpenAddAddress}
                  style={{ ...accentButtonStyle, padding: "10px 22px" }}
                >
                  + Add Your First Address
                </button>
              </div>
            ) : (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: 16
              }}>
                {savedAddresses.map((addr, idx) => {
                  const addrLabel = (addr.label || addr.LABEL || "HOME").toUpperCase();
                  const addrText = addr.address || addr.ADDRESS || "";
                  const addrPin = addr.pincode || addr.PINCODE || "";
                  const addrId = addr.id || addr.ID;
                  const isDefault = Boolean(
                    (activeLocation && (
                      (activeLocation.id && (activeLocation.id === addrId || activeLocation.id === addr.id || activeLocation.id === addr.ID)) ||
                      (activeLocation.address === addrText && activeLocation.pincode === addrPin)
                    )) ||
                    (!activeLocation && idx === 0)
                  );

                  return (
                    <div
                      key={addr.id || addr.ID}
                      style={{
                        background: cardBg,
                        border: isDefault ? "1px solid rgba(68,200,100,0.45)" : `1px solid ${borderColor}`,
                        borderRadius: 10,
                        padding: 18,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        transition: "all .2s",
                        boxShadow: isDefault ? "0 4px 18px rgba(68,200,100,0.08)" : "none"
                      }}
                    >
                      <div>
                        {/* Tag & Default Badge */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                          <span style={{
                            fontSize: ".6rem",
                            fontWeight: 700,
                            letterSpacing: ".12em",
                            textTransform: "uppercase",
                            padding: "3px 8px",
                            borderRadius: 4,
                            background: addrLabel === "HOME" ? "rgba(200,71,26,0.1)" : (addrLabel === "WORK" ? "rgba(46,76,255,0.1)" : "rgba(100,100,100,0.1)"),
                            color: addrLabel === "HOME" ? "#c8471a" : (addrLabel === "WORK" ? "#4a6fff" : textMuted),
                            border: `1px solid ${addrLabel === "HOME" ? "rgba(200,71,26,0.2)" : (addrLabel === "WORK" ? "rgba(46,76,255,0.2)" : borderColor)}`
                          }}>
                            {addrLabel === "HOME" ? "🏠 Home" : (addrLabel === "WORK" ? "🏢 Work" : "📍 Other")}
                          </span>

                          {isDefault && (
                            <span style={{
                              fontSize: ".6rem",
                              fontWeight: 700,
                              letterSpacing: ".06em",
                              padding: "3px 8px",
                              borderRadius: 20,
                              background: "rgba(68,200,100,0.12)",
                              color: "#44c864",
                              border: "1px solid rgba(68,200,100,0.3)",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4
                            }}>
                              ✓ Primary Address
                            </span>
                          )}
                        </div>

                        {/* Recipient / Name */}
                        <div style={{ fontSize: ".84rem", fontWeight: 700, color: textColor, marginBottom: 4 }}>
                          {user?.name || user?.NAME || "Customer"}
                        </div>

                        {/* Address Details */}
                        <div style={{ fontSize: ".76rem", color: textColor, lineHeight: 1.5, marginBottom: 6 }}>
                          {addrText}
                        </div>

                        {(addr.landmark || addr.LANDMARK) && (
                          <div style={{ fontSize: ".7rem", color: textMuted, marginBottom: 6 }}>
                            <span style={{ opacity: 0.7 }}>Landmark: </span>{addr.landmark || addr.LANDMARK}
                          </div>
                        )}

                        <div style={{ fontSize: ".72rem", color: textMuted, fontWeight: 600 }}>
                          {(addr.city || addr.CITY || "").trim()}, PIN {(addr.pincode || addr.PINCODE || "").trim()}
                        </div>
                      </div>

                      {/* Card Action buttons */}
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        marginTop: 16,
                        paddingTop: 12,
                        borderTop: `1px solid ${borderSubtle}`
                      }}>
                        {!isDefault ? (
                          <button
                            onClick={() => handleSetActiveAddress(addr)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#c8471a",
                              fontSize: ".68rem",
                              fontWeight: 700,
                              letterSpacing: ".06em",
                              textTransform: "uppercase",
                              cursor: "pointer",
                              padding: 0
                            }}
                          >
                            Set as Primary
                          </button>
                        ) : (
                          <span style={{ fontSize: ".68rem", color: "#44c864", fontWeight: 600 }}>
                            Active Delivery
                          </span>
                        )}

                        <div style={{ display: "flex", gap: 12 }}>
                          <button
                            onClick={() => handleOpenEditAddress(addr)}
                            style={{
                              background: "none",
                              border: "none",
                              color: textMuted,
                              fontSize: ".68rem",
                              fontWeight: 600,
                              cursor: "pointer",
                              padding: 0
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteAddress(addr.id || addr.ID)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#ff6b6b",
                              fontSize: ".68rem",
                              fontWeight: 600,
                              cursor: "pointer",
                              padding: 0
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {!loading && !error && activeTab === "reviews" && (
          <div style={{ display: "grid", gap: 18 }}>
            {reviewableOrders.length === 0 ? (
              <div style={{ padding: 32, background: cardBg, border: `1px dashed ${borderColor}`, borderRadius: 8, color: textMuted, textAlign: "center" }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={textSubtle} strokeWidth="1.2" style={{marginBottom:12}}>
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
                <div style={{fontSize:".82rem",marginBottom:6,color:textColor,fontWeight:600}}>No delivered orders yet</div>
                <div style={{fontSize:".72rem",color:textMuted}}>Reviews will appear here once your orders are delivered.</div>
              </div>
            ) : (
              reviewableOrders.map((order) => {
                const draft = reviewDrafts[order.ID] || { rating: 0, review: "", product_name: "", files: [], filePreviews: [] };
                const msg = reviewMessages[order.ID];
                const hov = starHover[order.ID] || 0;
                const isAlreadyRev = hasReviewed(order);
                const revDetails = getReview(order);

                return (
                  <div key={order.ID} style={{ border: `1px solid ${borderColor}`, borderRadius: 10, padding: 20, background: cardBg, transition: "border-color .2s" }}>
                    {/* Order header */}
                    <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                      <div>
                        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: "1.1rem", letterSpacing: ".1em", color: textColor }}>
                          {order.ORDER_ID || `ORD${String(order.ID).padStart(6, "0")}`}
                        </div>
                        <div style={{ fontSize: ".72rem", color: textMuted, marginTop: 2 }}>
                          {order.CREATED_AT ? new Date(order.CREATED_AT).toLocaleDateString("en-IN", {day:"numeric",month:"short",year:"numeric"}) : ""} · ₹{Number(order.TOTAL_AMOUNT||0).toLocaleString()}
                        </div>
                      </div>
                      <div style={{ fontSize: ".68rem", padding: "4px 10px", borderRadius: 4, background: "rgba(68,200,100,0.1)", color: "#44c864", border: "1px solid rgba(68,200,100,0.2)", alignSelf: "flex-start", fontWeight: 600 }}>✓ Delivered</div>
                    </div>

                    {isAlreadyRev ? (
                      <div style={{
                        padding: "16px 18px",
                        background: isDark ? "rgba(68,200,100,0.06)" : "rgba(68,200,100,0.05)",
                        border: "1px solid rgba(68,200,100,0.2)",
                        borderRadius: 8
                      }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <div style={{ display: "flex", gap: 2, color: "#f5c842", fontSize: "1.2rem", lineHeight: 1 }}>
                              {[1, 2, 3, 4, 5].map((s) => (
                                <span key={s}>{s <= (Number(revDetails?.RATING || revDetails?.rating || 5)) ? "★" : "☆"}</span>
                              ))}
                            </div>
                            <span style={{ fontSize: ".72rem", fontWeight: 700, color: "#f5c842" }}>
                              {["Poor", "Fair", "Good", "Great", "Excellent"][(Number(revDetails?.RATING || revDetails?.rating || 5)) - 1] || "Great"}
                            </span>
                          </div>
                          <span style={{
                            fontSize: ".66rem",
                            padding: "3px 10px",
                            borderRadius: 20,
                            background: "rgba(68,200,100,0.12)",
                            color: "#44c864",
                            fontWeight: 600,
                            border: "1px solid rgba(68,200,100,0.25)",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4
                          }}>
                            ✓ Review Submitted
                          </span>
                        </div>
                        {(revDetails?.REVIEW || revDetails?.review) && (
                          <div style={{ fontSize: ".82rem", color: textColor, fontStyle: "italic", lineHeight: 1.5, marginBottom: 8 }}>
                            &ldquo;{revDetails?.REVIEW || revDetails?.review}&rdquo;
                          </div>
                        )}
                        <div style={{ fontSize: ".68rem", color: textMuted }}>
                          Verified Purchase review recorded. Thank you for your feedback!
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Star rating — interactive with hover */}
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: ".6rem", letterSpacing: ".2em", textTransform: "uppercase", color: textMuted, marginBottom: 8, fontWeight: 600 }}>Your Rating *</div>
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            {[1, 2, 3, 4, 5].map((star) => {
                              const filled = star <= (hov || draft.rating);
                              return (
                                <button
                                  key={star}
                                  onMouseEnter={() => setStarHover(p => ({...p, [order.ID]: star}))}
                                  onMouseLeave={() => setStarHover(p => ({...p, [order.ID]: 0}))}
                                  onClick={() => updateReviewDraft(order.ID, { rating: star })}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: "1.6rem",
                                    lineHeight: 1,
                                    padding: "0 2px",
                                    color: filled ? "#f5c842" : (isDark ? "rgba(244,242,238,0.2)" : "rgba(0,0,0,0.18)"),
                                    transform: hov >= star ? "scale(1.2)" : "scale(1)",
                                    transition: "all .15s ease",
                                    filter: filled ? "drop-shadow(0 2px 6px rgba(245,200,66,0.4))" : "none",
                                  }}
                                >
                                  ★
                                </button>
                              );
                            })}
                            {draft.rating > 0 && (
                              <span style={{ fontSize: ".7rem", color: "#f5c842", marginLeft: 8, fontWeight: 600 }}>
                                {["Poor","Fair","Good","Great","Excellent"][draft.rating - 1]}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Product info banner */}
                        {(() => {
                          const itemsList = order.ITEMS || order.items || [];
                          const itemNames = itemsList.map(it => it.name || it.product_name).filter(Boolean).join(', ');
                          if (!itemNames) return null;
                          return (
                            <div style={{ fontSize: ".72rem", color: textMuted, marginBottom: 8 }}>
                              Purchased: <strong style={{ color: "#c8471a" }}>{itemNames}</strong>
                            </div>
                          );
                        })()}

                        {/* Product name field */}
                        <input
                          value={draft.product_name !== undefined ? draft.product_name : (order.ITEMS?.[0]?.name || order.ITEMS?.[0]?.product_name || "")}
                          onChange={(e) => updateReviewDraft(order.ID, { product_name: e.target.value })}
                          placeholder="Product name (e.g. Beige El Classico)"
                          style={{ ...fieldStyle, marginBottom: 10 }}
                        />

                        {/* Review text */}
                        <textarea
                          value={draft.review || ""}
                          onChange={(e) => updateReviewDraft(order.ID, { review: e.target.value })}
                          placeholder="Share your experience — fit, quality, delivery time…"
                          rows={3}
                          style={{ ...fieldStyle, minHeight: 80, resize: "vertical", marginBottom: 10 }}
                        />

                        {/* Photo upload */}
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: ".6rem", letterSpacing: ".14em", textTransform: "uppercase", color: textMuted, marginBottom: 6, fontWeight: 600 }}>Add Photos (up to 5)</div>
                          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", background: isDark ? "rgba(244,242,238,0.05)" : "rgba(0,0,0,0.03)", border: `1px dashed ${borderColor}`, borderRadius: 6, padding: "10px 14px", fontSize: ".7rem", color: textMuted, transition: "all .2s" }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(200,71,26,0.4)"; e.currentTarget.style.color = "#c8471a"; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = borderColor; e.currentTarget.style.color = textMuted; }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                            Upload Photos
                            <input type="file" accept="image/*" multiple onChange={(e) => handleFileSelect(order.ID, e)} style={{ display: "none" }} />
                          </label>
                        </div>

                        {/* Image preview thumbnails */}
                        {draft.filePreviews && draft.filePreviews.length > 0 && (
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                            {draft.filePreviews.map((p, idx) => (
                              <div key={idx} style={{ position: "relative", width: 70, height: 70, borderRadius: 6, overflow: "hidden", border: `1px solid ${borderColor}` }}>
                                <img src={p.preview} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                <button
                                  onClick={() => removePreview(order.ID, idx)}
                                  style={{ position: "absolute", top: 3, right: 3, width: 18, height: 18, borderRadius: "50%", background: "rgba(0,0,0,0.7)", border: "none", color: "#fff", fontSize: ".7rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
                                >×</button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Submit button */}
                        <button
                          onClick={() => handleReviewSubmit(order)}
                          disabled={reviewUploading[order.ID] || !draft.rating}
                          style={{
                            ...accentButtonStyle,
                            opacity: (!draft.rating || reviewUploading[order.ID]) ? 0.5 : 1,
                            cursor: (!draft.rating || reviewUploading[order.ID]) ? "not-allowed" : "pointer",
                            padding: "12px 20px",
                            fontSize: ".72rem",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          {reviewUploading[order.ID] ? (
                            <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{animation:"spin 1s linear infinite"}}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg> Uploading photos…</>
                          ) : (
                            <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> Submit Review</>
                          )}
                        </button>
                        {!draft.rating && <div style={{ fontSize: ".65rem", color: textMuted, marginTop: 6 }}>★ Select a star rating to submit</div>}
                        {msg && <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 6, fontSize: ".74rem", color: msg.ok ? "#44c864" : msg.ok === null ? textMuted : "#ff8080", background: msg.ok ? "rgba(68,200,100,0.06)" : msg.ok === null ? (isDark ? "rgba(244,242,238,0.04)" : "rgba(0,0,0,0.03)") : "rgba(255,80,80,0.06)", border: `1px solid ${msg.ok ? "rgba(68,200,100,0.15)" : msg.ok === null ? borderColor : "rgba(255,80,80,0.15)"}`}}>{msg.text}</div>}
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {!loading && !error && activeTab === 'orders' && orders.length === 0 && (
          <div style={{textAlign:"center",padding:"80px 0"}}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={textSubtle} strokeWidth="1" style={{marginBottom:16}}><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
            <div style={{fontSize:"1rem",color:textColor,fontWeight:600,marginBottom:8}}>No orders yet</div>
            <div style={{fontSize:".78rem",color:textMuted}}>Your orders will appear here once you make a purchase.</div>
            <button onClick={onBack} style={{marginTop:24,background:"#c8471a",border:"none",color:"#ffffff",padding:"12px 28px",borderRadius:3,fontSize:".72rem",letterSpacing:".15em",textTransform:"uppercase",cursor:"pointer",fontWeight:700}}>
              Shop Now →
            </button>
          </div>
        )}

        {activeTab === 'orders' && orders.map(order => {
          const isExp   = expanded === order.ID;
          // eslint-disable-next-line no-unused-vars
          const status  = (order.STATUS||"").toUpperCase();
          const date    = order.CREATED_AT ? new Date(order.CREATED_AT).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}) : "";
          const ordId   = order.ORDER_ID || ("ORD"+String(order.ID).padStart(6,"0"));
          const msg     = actionMsg[order.ID];
          const daysAgo = deliveredDaysAgo(order.DELIVERED_AT, order.CREATED_AT);
          const returnDaysLeft = Math.max(0, Math.ceil(7 - daysAgo));

          return (
            <div key={order.ID} style={{border:"1px solid",borderRadius:8,marginBottom:12,overflow:"hidden",transition:"border-color .2s",
              borderColor: isExp ? "rgba(200,71,26,0.3)" : borderColor, background: cardBg}}>

              {/* HEADER */}
              <div onClick={()=>setExpanded(isExp?null:order.ID)}
                style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px",
                  cursor:"pointer",background:isExp?(isDark?"rgba(200,71,26,0.08)":"rgba(200,71,26,0.05)"):"transparent",gap:12,flexWrap:"wrap"}}>
                <div style={{display:"flex",flexDirection:"column",gap:4}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1rem",letterSpacing:".1em",color:textColor}}>{ordId}</span>
                    <span style={{fontSize:".58rem",padding:"3px 8px",borderRadius:3,letterSpacing:".1em",textTransform:"uppercase",
                      background:statusBg(order.STATUS),color:statusColor(order.STATUS),border:`1px solid ${statusColor(order.STATUS)}30`}}>
                      {order.STATUS}
                    </span>
                  </div>
                  <div style={{fontSize:".72rem",color:textMuted}}>{date} · ₹{Number(order.TOTAL_AMOUNT).toLocaleString()}</div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  {isDelivered(order.STATUS) && (
                    hasReviewed(order) ? (
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "6px 14px",
                          borderRadius: 20,
                          background: isDark ? "rgba(68,200,100,0.1)" : "rgba(68,200,100,0.08)",
                          border: "1px solid rgba(68,200,100,0.25)",
                          color: "#44c864",
                          fontSize: ".66rem",
                          fontWeight: 600,
                          letterSpacing: ".06em",
                          textTransform: "uppercase"
                        }}
                      >
                        <span style={{ color: "#f5c842" }}>★</span>
                        <span>{getReview(order)?.RATING || getReview(order)?.rating || 5}/5 Reviewed</span>
                      </div>
                    ) : (
                      <button onClick={(e)=>{e.stopPropagation(); setActiveTab('reviews');}} style={{...accentButtonStyle, padding:'8px 12px', boxShadow:'0 8px 20px rgba(200,71,26,0.16)'}}>
                        Leave a review
                      </button>
                    )
                  )}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={textSubtle} strokeWidth="2"
                  style={{transition:"transform .3s",transform:isExp?"rotate(180deg)":"rotate(0deg)",flexShrink:0}}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </div>
            </div>

              {/* EXPANDED */}
              {isExp && (
                <div style={{borderTop:`1px solid ${borderColor}`,padding:"20px"}}>

                  {/* ── AMAZON-STYLE STATUS TRACKER ── */}
                  {!isCancelled(order.STATUS) && !isReturn(order.STATUS) && (() => {
                    const TRACK_STEPS = [
                      { key:"CONFIRMED",        label:"Ordered",          icon:"✓" },
                      { key:"PROCESSING",       label:"Processing",       icon:"⚙" },
                      { key:"PACKING",          label:"Packed",           icon:"📦" },
                      { key:"SHIPPED",          label:"Shipped",          icon:"🚚" },
                      { key:"OUT FOR DELIVERY", label:"Out for Delivery", icon:"📍" },
                      { key:"DELIVERED",        label:"Delivered",        icon:"🏠" },
                    ];
                    const rawStatus = (order.STATUS||"").toUpperCase();
                    // Find which step is active (0-based)
                    const activeIdx = (() => {
                      const idx = TRACK_STEPS.findIndex(s => s.key === rawStatus);
                      if (idx >= 0) return idx;
                      if (rawStatus === "PENDING PAYMENT" || rawStatus === "PENDING_PAYMENT") return -1;
                      return 0;
                    })();

                    const statusLabels = {
                      "PENDING PAYMENT": "Awaiting Payment",
                      "CONFIRMED":       "Order confirmed — being prepared",
                      "PROCESSING":      "Your order is being processed",
                      "PACKING":         "Your order is being packed",
                      "SHIPPED":         "On the way! Your order has been shipped",
                      "OUT FOR DELIVERY":"Out for delivery — arriving today!",
                      "DELIVERED":       "Delivered! Enjoy your order 🎉",
                    };
                    const statusMsg = statusLabels[rawStatus] || order.STATUS;
                    const isPending = rawStatus === "PENDING PAYMENT" || rawStatus === "PENDING_PAYMENT";

                    return (
                      <div style={{marginBottom:28}}>
                        {/* Header */}
                        <div style={{marginBottom:18}}>
                          <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.4rem",letterSpacing:".06em",color:textColor,marginBottom:4}}>
                            {isPending ? "Awaiting Payment" : TRACK_STEPS[activeIdx]?.label || order.STATUS}
                          </div>
                          <div style={{fontSize:".78rem",color:textMuted,lineHeight:1.5}}>{statusMsg}</div>
                        </div>

                        {/* Progress Bar Track */}
                        {!isPending && (
                          <div style={{position:"relative",padding:"0 0 32px"}}>
                            {/* Background line */}
                            <div style={{position:"absolute",top:16,left:"8%",right:"8%",height:3,background:lineBg,borderRadius:2}}/>
                            {/* Filled line */}
                            <div style={{
                              position:"absolute",top:16,left:"8%",
                              width: activeIdx <= 0 ? "0%" : activeIdx >= TRACK_STEPS.length-1 ? "84%" : `${(activeIdx / (TRACK_STEPS.length-1)) * 84}%`,
                              height:3,background:"linear-gradient(90deg,#c8471a,#e85d20)",borderRadius:2,
                              transition:"width .6s ease"
                            }}/>
                            {/* Steps */}
                            <div style={{display:"flex",justifyContent:"space-between",position:"relative",zIndex:1}}>
                              {TRACK_STEPS.map((step, i) => {
                                const done   = i <= activeIdx;
                                const active = i === activeIdx;
                                return (
                                  <div key={step.key} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8,flex:1}}>
                                    {/* Circle */}
                                    <div style={{
                                      width:32,height:32,borderRadius:"50%",
                                      display:"flex",alignItems:"center",justifyContent:"center",
                                      fontSize: active ? ".85rem" : ".72rem",
                                      fontWeight:700,
                                      background: active ? "#c8471a"
                                                : done   ? "rgba(200,71,26,0.18)"
                                                :          (isDark ? "rgba(244,242,238,0.05)" : "rgba(0,0,0,0.04)"),
                                      border: active ? "2.5px solid #c8471a"
                                            : done   ? "2px solid rgba(200,71,26,0.5)"
                                            :          `2px solid ${borderColor}`,
                                      color: active ? "#ffffff" : done ? (isDark ? "#f4f2ee" : "#c8471a") : textSubtle,
                                      boxShadow: active ? "0 0 16px rgba(200,71,26,0.4)" : "none",
                                      transition:"all .4s ease",
                                      flexShrink:0,
                                    }}>
                                      {done ? (active ? step.icon : "✓") : ""}
                                    </div>
                                    {/* Label */}
                                    <div style={{
                                      fontSize:".55rem",textAlign:"center",lineHeight:1.3,
                                      fontWeight: active ? 700 : 400,
                                      color: active ? "#c8471a"
                                           : done   ? (isDark ? "rgba(244,242,238,0.75)" : "#2B2A28")
                                           :          textSubtle,
                                      maxWidth:52,
                                    }}>
                                      {step.label}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Pending Payment special state */}
                        {isPending && (
                          <div style={{background:isDark?"rgba(245,200,66,0.06)":"rgba(245,200,66,0.12)",border:"1px solid rgba(245,200,66,0.28)",borderRadius:8,padding:"14px 18px",fontSize:".78rem",color:isDark?"rgba(244,242,238,0.8)":"#7A5A00",lineHeight:1.6}}>
                            🔒 Payment not yet confirmed. If you've already paid, please contact us with your Order ID.
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* CANCELLED/RETURN BANNER */}
                  {(isCancelled(order.STATUS)||isReturn(order.STATUS)) && (
                    <div style={{padding:"12px 16px",borderRadius:6,marginBottom:16,
                      background:isCancelled(order.STATUS)?(isDark?"rgba(255,107,107,0.08)":"rgba(255,80,80,0.1)"): (isDark?"rgba(245,200,66,0.08)":"rgba(245,200,66,0.14)"),
                      border:`1px solid ${isCancelled(order.STATUS)?(isDark?"rgba(255,107,107,0.2)":"rgba(255,80,80,0.3)"): (isDark?"rgba(245,200,66,0.2)":"rgba(245,200,66,0.35)")}`,
                      color:isCancelled(order.STATUS)?(isDark?"#ff8080":"#d32f2f"):(isDark?"#f5c842":"#8c6500"),fontSize:".8rem",lineHeight:1.6}}>
                      {isCancelled(order.STATUS)
                        ? <><strong>Order Cancelled</strong>{order.CANCEL_REASON?` — ${order.CANCEL_REASON}`:""}</>
                        : <><strong>Return Requested</strong>{order.RETURN_REASON?` — ${order.RETURN_REASON}`:""}</>
                      }
                    </div>
                  )}

                  {/* TRACKING CARD */}
                  {order.TRACKING_NUMBER && !isCancelled(order.STATUS) && (
                    <div style={{background:isDark?"rgba(200,71,26,0.06)":"rgba(200,71,26,0.04)",border:`1px solid ${isDark?"rgba(200,71,26,0.18)":"rgba(200,71,26,0.25)"}`,borderRadius:6,padding:"16px 18px",marginBottom:16}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#c8471a" strokeWidth="2"><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                        <span style={{fontSize:".65rem",letterSpacing:".15em",textTransform:"uppercase",color:"#c8471a"}}>Shipment Details</span>
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px 20px",marginBottom:12,flexWrap:"wrap"}}>
                        <div>
                          <div style={{fontSize:".57rem",color:textMuted,letterSpacing:".1em",textTransform:"uppercase",marginBottom:3}}>Carrier</div>
                          <div style={{fontSize:".82rem",color:textColor,fontWeight:500}}>{order.TRACKING_CARRIER||"Courier Partner"}</div>
                        </div>
                        <div>
                          <div style={{fontSize:".57rem",color:textMuted,letterSpacing:".1em",textTransform:"uppercase",marginBottom:3}}>Consignment No.</div>
                          <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
                            <span style={{fontSize:".82rem",fontFamily:"monospace",color:textColor,fontWeight:600}}>{order.TRACKING_NUMBER}</span>
                            <button onClick={()=>copyText(order.TRACKING_NUMBER,order.ID+"trk")}
                              onMouseEnter={e=>{if(copied!==order.ID+"trk"){e.currentTarget.style.background="rgba(200,71,26,0.15)";e.currentTarget.style.transform="scale(1.05)";}}}
                              onMouseLeave={e=>{if(copied!==order.ID+"trk"){e.currentTarget.style.background="none";e.currentTarget.style.transform="scale(1)";}}}
                              onMouseDown={e=>e.currentTarget.style.transform="scale(0.95)"}
                              onMouseUp={e=>e.currentTarget.style.transform="scale(1.05)"}
                              style={{display:"inline-flex",alignItems:"center",gap:4,background:"none",
                                border:`1px solid ${copied===order.ID+"trk"?"rgba(68,200,100,0.4)":"rgba(200,71,26,0.3)"}`,
                                color:copied===order.ID+"trk"?"#44c864":"#c8471a",
                                borderRadius:4,padding:"3px 9px",fontSize:".58rem",cursor:"pointer",
                                letterSpacing:".1em",transition:"all .2s cubic-bezier(.23,1,.32,1)"}}>
                              {copied===order.ID+"trk"
                                ?<><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>Copied!</>
                                :<><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>Copy</>
                              }
                            </button>
                          </div>
                        </div>
                      </div>
                      {order.TRACKING_URL && (
                        <a href={order.TRACKING_URL} target="_blank" rel="noreferrer"
                          style={{display:"inline-flex",alignItems:"center",gap:6,background:"#c8471a",color:"#ffffff",textDecoration:"none",
                            fontSize:".65rem",letterSpacing:".12em",textTransform:"uppercase",padding:"9px 16px",borderRadius:3,fontWeight:600}}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
                          Track on {order.TRACKING_CARRIER||"Carrier"} Website
                        </a>
                      )}
                    </div>
                  )}

                  {/* WAITING FOR SHIPPING */}
                  {!order.TRACKING_NUMBER && !isCancelled(order.STATUS) && !isDelivered(order.STATUS) && (
                    <div style={{display:"flex",gap:10,padding:"12px 16px",background:isDark?"rgba(244,242,238,0.03)":"rgba(0,0,0,0.03)",borderRadius:6,marginBottom:16,
                      fontSize:".76rem",color:textMuted,border:`1px solid ${borderColor}`}}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{flexShrink:0,marginTop:2}}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      Tracking details will appear once your order is shipped.
                    </div>
                  )}

                  {/* ORDER INFO */}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px 20px",fontSize:".76rem",marginBottom:20}}>
                    <div>
                      <div style={{color:textMuted,fontSize:".57rem",letterSpacing:".1em",textTransform:"uppercase",marginBottom:3}}>Delivery Address</div>
                      <div style={{color:textColor,lineHeight:1.6}}>{order.ADDRESS}</div>
                    </div>
                    <div>
                      <div style={{color:textMuted,fontSize:".57rem",letterSpacing:".1em",textTransform:"uppercase",marginBottom:3}}>Payment</div>
                      <div style={{color:textColor,marginBottom:3}}>{order.PAYMENT_METHOD}</div>
                      <div style={{color:"#c8471a",fontWeight:600}}>₹ {Number(order.TOTAL_AMOUNT).toLocaleString()}</div>
                    </div>
                  </div>

                  {/* DELIVERY & RETURN FLOW — Only available while order is unfulfilled */}
                  {canCancel(order.STATUS) && !isCancelled(order.STATUS) && !isReturn(order.STATUS) && !isDelivered(order.STATUS) && (
                    <div style={{border:`1px solid ${borderColor}`,borderRadius:10,padding:18,marginBottom:16,background:isDark?"rgba(244,242,238,0.03)":"rgba(0,0,0,0.02)"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#c8471a" strokeWidth="2"><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1rem",letterSpacing:".1em",color:textColor}}>Delivery Details</div>
                      </div>
                      <div style={{fontSize:".72rem",color:textMuted,lineHeight:1.6,marginBottom:14}}>
                        Update delivery address and find the cheapest shipping partner for your order.
                      </div>

                      {/* Saved address quick-fill */}
                      {savedAddresses.length > 0 && !getDeliveryDraft(order.ID).open && (
                        <div style={{marginBottom:10}}>
                          <div style={{fontSize:".6rem",letterSpacing:".14em",textTransform:"uppercase",color:textMuted,marginBottom:6}}>Saved Addresses</div>
                          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                            {savedAddresses.slice(0,5).map((addr, i) => {
                              const icon = addr.label === "home" ? "🏠" : addr.label === "work" ? "💼" : "📍";
                              return (
                                <button key={i} onClick={() => updateDeliveryDraft(order.ID, { open: true, address: addr.address || '', city: addr.city || '', pincode: addr.pincode || '', landmark: addr.landmark || '', lat: addr.lat || '', lng: addr.lng || '', save_address: false, usingSaved: true })}
                                  style={{background:isDark?"rgba(200,71,26,0.08)":"rgba(200,71,26,0.06)",border:"1px solid rgba(200,71,26,0.22)",borderRadius:6,padding:"6px 10px",fontSize:".65rem",color:"#c8471a",cursor:"pointer",textAlign:"left",maxWidth:220,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:5}}>
                                  <span>{icon}</span>
                                  <span style={{fontWeight:600,textTransform:"capitalize"}}>{addr.label || 'Saved'}:</span>
                                  <span>{[addr.address, addr.city].filter(Boolean).join(', ')}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:10}}>
                        <button
                          onClick={() => updateDeliveryDraft(order.ID, { open: !getDeliveryDraft(order.ID).open, address: order.ADDRESS || '', city: '', pincode: '', landmark: '', lat: '', lng: '', save_address: false })}
                          style={{...secondaryButtonStyle, display:"flex", alignItems:"center", gap:7}}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                          {getDeliveryDraft(order.ID).open ? 'Close form' : 'Edit delivery address'}
                        </button>
                        <button
                          onClick={() => handleDeliveryQuote(order.ID)}
                          disabled={deliveryLoading[order.ID]}
                          style={{...secondaryButtonStyle, display:"flex", alignItems:"center", gap:7, opacity: deliveryLoading[order.ID] ? 0.7 : 1}}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                          {deliveryLoading[order.ID] ? 'Checking…' : 'Find cheapest partner'}
                        </button>
                      </div>

                      {deliveryQuote[order.ID] && (
                        <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:isDark?"rgba(200,71,26,0.06)":"rgba(200,71,26,0.04)",border:`1px solid ${isDark?"rgba(200,71,26,0.18)":"rgba(200,71,26,0.25)"}`,borderRadius:6,marginBottom:10}}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#c8471a" strokeWidth="2"><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                          <div style={{flex:1,fontSize:".72rem"}}>
                            <span style={{color:textMuted}}>Cheapest: </span>
                            <strong style={{color:textColor}}>{deliveryQuote[order.ID].partner}</strong>
                            <span style={{color:textSubtle,margin:"0 6px"}}>·</span>
                            <span style={{color:textMuted}}>{deliveryQuote[order.ID].eta}</span>
                            <span style={{color:textSubtle,margin:"0 6px"}}>·</span>
                            <strong style={{color:"#c8471a"}}>₹{deliveryQuote[order.ID].charge}</strong>
                          </div>
                        </div>
                      )}

                      {getDeliveryDraft(order.ID).open && (
                        <div style={{display:"grid",gap:10,marginTop:4,padding:"14px",background:isDark?"rgba(244,242,238,0.02)":"rgba(0,0,0,0.02)",border:`1px solid ${borderColor}`,borderRadius:8}}>
                          <div style={{fontSize:".6rem",letterSpacing:".14em",textTransform:"uppercase",color:textMuted,marginBottom:2}}>Delivery Address</div>
                          <input value={getDeliveryDraft(order.ID).address} onChange={e => updateDeliveryDraft(order.ID, { address: e.target.value })} placeholder="Full street address" style={fieldStyle} />
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                            <input value={getDeliveryDraft(order.ID).city} onChange={e => updateDeliveryDraft(order.ID, { city: e.target.value })} placeholder="City" style={fieldStyle} />
                            <input value={getDeliveryDraft(order.ID).pincode} onChange={e => updateDeliveryDraft(order.ID, { pincode: e.target.value })} placeholder="Pincode" style={fieldStyle} />
                          </div>
                          <input value={getDeliveryDraft(order.ID).landmark} onChange={e => updateDeliveryDraft(order.ID, { landmark: e.target.value })} placeholder="Landmark (optional)" style={fieldStyle} />
                          {(getDeliveryDraft(order.ID).lat || getDeliveryDraft(order.ID).lng) && (
                            <div style={{fontSize:".65rem",color:textMuted,display:"flex",alignItems:"center",gap:6}}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                              Location pinned: {Number(getDeliveryDraft(order.ID).lat).toFixed(4)}, {Number(getDeliveryDraft(order.ID).lng).toFixed(4)}
                            </div>
                          )}
                          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                            <button onClick={() => handleLocationLookup(order.ID)} style={{...secondaryButtonStyle, display:"flex", alignItems:"center", gap:7, fontSize:".65rem"}}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                              Use my location
                            </button>
                            <label style={{display:"inline-flex",alignItems:"center",gap:7,fontSize:".7rem",color:textMuted,cursor:"pointer"}}>
                              <input type="checkbox" checked={getDeliveryDraft(order.ID).save_address || false} onChange={e => updateDeliveryDraft(order.ID, { save_address: e.target.checked })} style={{accentColor:"#c8471a"}} />
                              Save this address for future orders
                            </label>
                          </div>
                          <div style={{display:"flex",gap:8,marginTop:4}}>
                            <button
                              onClick={() => handleDeliverySubmit(order)}
                              disabled={deliveryLoading[order.ID]}
                              style={{...accentButtonStyle, flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:8, opacity: deliveryLoading[order.ID] ? 0.7 : 1}}
                            >
                              {deliveryLoading[order.ID] ? 'Saving…' : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg> Save delivery details</>}
                            </button>
                          </div>
                        </div>
                      )}
                      {deliveryMessages[order.ID] && (
                        <div style={{marginTop:10,padding:"8px 12px",borderRadius:6,fontSize:".74rem",color:deliveryMessages[order.ID].ok ? '#44c864' : '#ff8080',background:deliveryMessages[order.ID].ok ? "rgba(68,200,100,0.06)" : "rgba(255,80,80,0.06)",border:`1px solid ${deliveryMessages[order.ID].ok ? "rgba(68,200,100,0.15)" : "rgba(255,80,80,0.15)"}`}}>
                          {deliveryMessages[order.ID].text}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ACTION MESSAGE */}
                  {msg && (
                    <div style={{padding:"10px 14px",borderRadius:6,marginBottom:16,fontSize:".78rem",
                      background:msg.ok?"rgba(68,200,100,0.08)":"rgba(255,80,80,0.08)",
                      border:`1px solid ${msg.ok?"rgba(68,200,100,0.2)":"rgba(255,80,80,0.2)"}`,
                      color:msg.ok?"#44c864":"#ff8080"}}>
                      {msg.text}
                    </div>
                  )}

                  {/* ── CANCEL / RETURN ACTIONS ── */}
                  {!isCancelled(order.STATUS) && !isReturn(order.STATUS) && (
                    <div style={{borderTop:`1px solid ${borderColor}`,paddingTop:16}}>

                      {/* CANCEL */}
                      {canCancel(order.STATUS) && (
                        <div style={{marginBottom:12}}>
                          <button
                            onClick={()=>setActionOrder({id:order.ID, type:"cancel", order})}
                            onMouseEnter={e=>{e.currentTarget.style.borderColor="rgba(255,80,80,0.5)";e.currentTarget.style.color=isDark?"#ff8080":"#b71c1c";}}
                            onMouseLeave={e=>{e.currentTarget.style.borderColor=isDark?"rgba(255,80,80,0.2)":"rgba(255,80,80,0.3)";e.currentTarget.style.color=isDark?"rgba(255,107,107,0.8)":"#d32f2f";}}
                            style={{display:"inline-flex",alignItems:"center",gap:7,background:isDark?"rgba(255,80,80,0.06)":"rgba(255,80,80,0.08)",
                              border:`1px solid ${isDark?"rgba(255,80,80,0.2)":"rgba(255,80,80,0.3)"}`,color:isDark?"rgba(255,107,107,0.8)":"#d32f2f",
                              padding:"9px 18px",borderRadius:5,fontSize:".68rem",letterSpacing:".12em",
                              textTransform:"uppercase",cursor:"pointer",transition:"all .2s",fontWeight:600}}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                            Cancel Order
                          </button>
                        </div>
                      )}

                      {/* POST-SHIP CANCEL BLOCKED */}
                      {postShip(order.STATUS) && (
                        <div style={{display:"flex",alignItems:"center",gap:8,padding:"9px 14px",background:isDark?"rgba(255,80,80,0.04)":"rgba(255,80,80,0.06)",
                          border:`1px solid ${isDark?"rgba(255,80,80,0.12)":"rgba(255,80,80,0.2)"}`,borderRadius:5,marginBottom:12,fontSize:".72rem",color:isDark?"rgba(255,107,107,0.7)":"#c83232"}}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                          Can't cancel — order is already {order.STATUS?.toLowerCase()}.
                        </div>
                      )}

                      {/* RETURN — only for DELIVERED within 7 days for damaged items */}
                      {isDelivered(order.STATUS) && (
                        <div>
                          {returnDaysLeft > 0 ? (
                            <div>
                              <button
                                onClick={()=>setActionOrder({id:order.ID, type:"return", order})}
                                onMouseEnter={e=>{e.currentTarget.style.borderColor="rgba(245,200,66,0.5)";e.currentTarget.style.color=isDark?"#ffd84d":"#724c00";}}
                                onMouseLeave={e=>{e.currentTarget.style.borderColor=isDark?"rgba(245,200,66,0.2)":"rgba(245,200,66,0.35)";e.currentTarget.style.color=isDark?"rgba(245,200,66,0.85)":"#946300";}}
                                style={{display:"inline-flex",alignItems:"center",gap:7,background:isDark?"rgba(245,200,66,0.06)":"rgba(245,200,66,0.12)",
                                  border:`1px solid ${isDark?"rgba(245,200,66,0.2)":"rgba(245,200,66,0.35)"}`,color:isDark?"rgba(245,200,66,0.85)":"#946300",
                                  padding:"9px 18px",borderRadius:5,fontSize:".68rem",letterSpacing:".12em",
                                  textTransform:"uppercase",cursor:"pointer",transition:"all .2s",marginBottom:6,fontWeight:600}}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/></svg>
                                Request Return
                              </button>
                              <div style={{fontSize:".65rem",color:textMuted,display:"flex",alignItems:"center",gap:6}}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                Return window closes in <strong style={{color:isDark?"#f5c842":"#a87100"}}>{returnDaysLeft} day{returnDaysLeft!==1?"s":""}</strong>
                              </div>
                            </div>
                          ) : (
                            <div style={{display:"flex",alignItems:"center",gap:8,padding:"9px 14px",background:isDark?"rgba(244,242,238,0.03)":"rgba(0,0,0,0.03)",
                              border:`1px solid ${borderColor}`,borderRadius:5,fontSize:".72rem",color:textMuted}}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                              Return window has expired (7 days after delivery).
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </>
  );
}

// ── REUSABLE MOBILE MENU DRAWER ───────────────────────────────────────────────
function MobileMenuDrawer({
  isOpen,
  onClose,
  theme,
  setTheme,
  vibe,
  toggleVibe,
  navigate,
  user,
  handleLogout,
  wishlist = [],
  setSelectedProduct,
  setWishlistOpen,
  storeSettings = {},
}) {
  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add("bords-menu-open");
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.classList.remove("bords-menu-open");
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      className="bords-mobile-menu-overlay"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        zIndex: 999999,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "flex-end",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Navigation Menu"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bords-mobile-menu-sheet"
        style={{
          background: theme === "light" ? "rgba(244, 242, 238, 0.96)" : "rgba(17, 17, 17, 0.96)",
          backdropFilter: "blur(24px) saturate(190%)",
          WebkitBackdropFilter: "blur(24px) saturate(190%)",
          borderTop: theme === "light" ? "1px solid rgba(0,0,0,0.1)" : "1px solid rgba(255,255,255,0.12)",
          borderLeft: theme === "light" ? "1px solid rgba(0,0,0,0.1)" : "1px solid rgba(255,255,255,0.12)",
          width: "100%",
          maxWidth: 420,
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: "24px 20px 32px",
          animation: "fadeUp .3s cubic-bezier(0.16, 1, 0.3, 1)",
          boxShadow: theme === "light" ? "0 -12px 40px rgba(0,0,0,0.12)" : "0 -12px 40px rgba(0,0,0,0.5)",
          boxSizing: "border-box",
        }}
      >
        {/* Drawer Header with Logo & Big Cross Button */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: 16,
            marginBottom: 16,
            borderBottom: theme === "light" ? "1px solid rgba(0,0,0,0.08)" : "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <Logo size="sm" showTagline={false} />
          </div>
          <button
            onClick={onClose}
            aria-label="Close menu"
            title="Close menu"
            style={{
              background: theme === "light" ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.1)",
              border: theme === "light" ? "1px solid rgba(0,0,0,0.12)" : "1px solid rgba(255,255,255,0.18)",
              color: theme === "light" ? "#1A1A1A" : "#FFFFFF",
              width: 44,
              height: 44,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "transform 0.15s ease, background 0.2s ease",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Scrollable Nav Content */}
        <div style={{ overflowY: "auto", flex: 1, paddingRight: 4 }}>
          {/* User Profile Card (Compact, Sleek & Precise) */}
          {user ? (
            <div style={{
              marginBottom: 16,
              padding: "10px 12px",
              background: theme === "light" ? "rgba(200,71,26,0.04)" : "rgba(200,71,26,0.08)",
              border: theme === "light" ? "1px solid rgba(200,71,26,0.14)" : "1px solid rgba(200,71,26,0.22)",
              borderRadius: 8,
            }}>
              {/* Row: Avatar + Name + Sign Out */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                  <div style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    background: "#c8471a",
                    color: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: ".75rem",
                    fontWeight: 700,
                    letterSpacing: ".02em",
                  }}>
                    {(user.name || user.email || "?")[0].toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: ".82rem",
                      fontWeight: 600,
                      color: theme === "light" ? "#1A1A1A" : "#F5F3EE",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.2
                    }}>
                      {user.name || "Member"}
                    </div>
                    <div style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: ".6rem",
                      color: theme === "light" ? "rgba(0,0,0,0.48)" : "rgba(245,243,238,0.5)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      marginTop: 1
                    }}>
                      {user.email}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => { onClose(); handleLogout(); }}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#c8471a",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: ".62rem",
                    fontWeight: 600,
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                    padding: "3px 6px",
                    flexShrink: 0,
                    opacity: 0.85
                  }}
                  title="Sign Out"
                >
                  Sign Out
                </button>
              </div>

              {/* Quick Action Grid: Orders, Addresses, Wishlist */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                <button
                  onClick={() => { onClose(); navigate("myorders", null, { tab: "orders" }); }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 5,
                    background: theme === "light" ? "#FFFFFF" : "rgba(255,255,255,0.06)",
                    border: theme === "light" ? "1px solid #E8E5DE" : "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 6,
                    padding: "7px 4px",
                    color: theme === "light" ? "#1A1A1A" : "#F5F3EE",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: ".62rem",
                    fontWeight: 600,
                    letterSpacing: ".04em",
                    cursor: "pointer",
                    textTransform: "uppercase"
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
                  Orders
                </button>
                <button
                  onClick={() => { onClose(); navigate("myorders", null, { tab: "addresses" }); }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 5,
                    background: theme === "light" ? "#FFFFFF" : "rgba(255,255,255,0.06)",
                    border: theme === "light" ? "1px solid #E8E5DE" : "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 6,
                    padding: "7px 4px",
                    color: theme === "light" ? "#1A1A1A" : "#F5F3EE",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: ".62rem",
                    fontWeight: 600,
                    letterSpacing: ".04em",
                    cursor: "pointer",
                    textTransform: "uppercase"
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  Addresses
                </button>
                <button
                  onClick={() => { onClose(); setWishlistOpen(true); }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 5,
                    background: theme === "light" ? "#FFFFFF" : "rgba(255,255,255,0.06)",
                    border: theme === "light" ? "1px solid #E8E5DE" : "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 6,
                    padding: "7px 4px",
                    color: theme === "light" ? "#1A1A1A" : "#F5F3EE",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: ".62rem",
                    fontWeight: 600,
                    letterSpacing: ".04em",
                    cursor: "pointer",
                    textTransform: "uppercase"
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill={wishlist.length > 0 ? "#c8471a" : "none"} stroke={wishlist.length > 0 ? "#c8471a" : "currentColor"} strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                  Wishlist{wishlist.length > 0 ? ` (${wishlist.length})` : ""}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: 14, display: "flex", gap: 6 }}>
              <button
                onClick={() => { onClose(); navigate("auth"); }}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  background: theme === "light" ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.07)",
                  border: theme === "light" ? "1px solid rgba(0,0,0,0.1)" : "1px solid rgba(255,255,255,0.14)",
                  borderRadius: 7,
                  padding: "9px 12px",
                  color: theme === "light" ? "#1A1A1A" : "#FFFFFF",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: ".7rem",
                  fontWeight: 600,
                  letterSpacing: ".08em",
                  textTransform: "uppercase",
                  cursor: "pointer"
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                Sign In / Register
              </button>
              {wishlist.length > 0 && (
                <button
                  onClick={() => { onClose(); setWishlistOpen(true); }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    background: theme === "light" ? "#FFFFFF" : "rgba(255,255,255,0.06)",
                    border: theme === "light" ? "1px solid #E8E5DE" : "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 7,
                    padding: "9px 12px",
                    color: "#c8471a",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: ".7rem",
                    fontWeight: 600,
                    letterSpacing: ".04em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                    flexShrink: 0
                  }}
                  title="View Wishlist"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="#c8471a" stroke="#c8471a" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                  {wishlist.length}
                </button>
              )}
            </div>
          )}

          {/* Nav Links */}
          <div style={{ display: "flex", flexDirection: "column", gap: 0, marginBottom: 16 }}>
            {[
              { label: "Shop All Drops", action: () => { onClose(); navigate("shop"); } },
              { label: "🎴 Cards Vault", action: () => { onClose(); navigate("cards"); } },
              { label: "📖 Lore of SAS", action: () => { onClose(); navigate("comics"); } },
              { label: "Our Fabrics", action: () => { onClose(); navigate("our-fabrics"); } },
              { label: "Sustainability", action: () => { onClose(); navigate("sustainability"); } },
              { label: "Care Guide", action: () => { onClose(); navigate("care"); } },
              ...(storeSettings.show_fabric_certifications === "1" ? [{ label: "Certifications", action: () => { onClose(); navigate("certifications"); } }] : []),
              { label: "Journal", action: () => { onClose(); navigate("journal"); } },
              { label: "New Arrivals", href: "#arrivals", action: () => { onClose(); navigate("home"); } },
            ].map((item, idx) => (
              <button
                key={idx}
                onClick={item.action}
                style={{
                  background: "none",
                  border: "none",
                  padding: "14px 0",
                  textAlign: "left",
                  cursor: "pointer",
                  color: theme === "light" ? "#1A1A1A" : "#F5F3EE",
                  borderBottom: theme === "light" ? "1px solid #E8E5DE" : "1px solid rgba(245,243,238,0.08)",
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "1.35rem",
                  fontWeight: 500,
                  letterSpacing: ".04em",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>{item.label}</span>
                <span style={{ fontSize: "1rem", opacity: 0.4 }}>→</span>
              </button>
            ))}
          </div>

          {/* Vibe Mode — Loud vs Quiet toggle */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 14, marginBottom: 14, borderBottom: theme === "light" ? "1px solid #E8E5DE" : "1px solid rgba(245,243,238,0.08)" }}>
            <span style={{ color: theme === "light" ? "#1A1A1A" : "#F5F3EE", fontFamily: "'DM Sans', sans-serif", fontSize: ".75rem", letterSpacing: ".12em", textTransform: "uppercase", fontWeight: 600 }}>Vibe Mode</span>
            <button
              onClick={toggleVibe}
              style={{
                padding: '6px 16px',
                borderRadius: 999,
                border: vibe === 'loud'
                  ? '1.5px solid #0D0D0D'
                  : (theme === 'dark' ? '1.5px solid rgba(181, 214, 159, 0.5)' : '1.5px solid #4A5D3A'),
                background: vibe === 'loud'
                  ? '#D4FF3F'
                  : (theme === 'dark' ? 'rgba(74, 93, 58, 0.35)' : 'rgba(74, 93, 58, 0.12)'),
                color: vibe === 'loud'
                  ? '#0D0D0D'
                  : (theme === 'dark' ? '#B5D69F' : '#2F4222'),
                fontFamily: "var(--font-stamp, 'Archivo Black', sans-serif)",
                fontSize: '.7rem',
                fontWeight: 800,
                letterSpacing: '.07em',
                cursor: 'pointer',
                boxShadow: vibe === 'loud' ? '2px 2px 0 #0D0D0D' : 'none',
              }}
            >
              {vibe === 'loud' ? '⚡ LOUD' : '🌿 QUIET'}
            </button>
          </div>

          {/* Appearance — Light / Dark toggle */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 8 }}>
            <span style={{ color: theme === "light" ? "#1A1A1A" : "#F5F3EE", fontFamily: "'DM Sans', sans-serif", fontSize: ".75rem", letterSpacing: ".12em", textTransform: "uppercase", fontWeight: 600 }}>Appearance</span>
            <div className={`theme-switch compact ${theme}`} onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} role="button" aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`} title="Toggle Light/Dark Mode">
              <div className="theme-switch-handle">
                {theme === 'dark'
                  ? <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" style={{ stroke: '#1a1a1a' }}><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
                  : <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" style={{ stroke: '#c8471a' }}><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}




// ── UNIFIED LUXURY PAGE SUSPENSE FALLBACK ────────────────────────────────────
function PageFallback({ theme = "light", label = "Loading" }) {
  const isDark = theme === "dark";
  const bg = isDark ? "#0F0F0F" : "#FAFAF7";
  const text = isDark ? "#F5F3EE" : "#1A1A1A";
  const border = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: bg,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        userSelect: "none"
      }}
    >
      <div
        style={{
          fontFamily: "'Bebas Neue', 'Archivo Black', sans-serif",
          fontSize: "2rem",
          letterSpacing: ".18em",
          color: text,
          lineHeight: 1
        }}
      >
        BORDS
      </div>
      <div
        style={{
          width: 50,
          height: 1.5,
          background: border,
          overflow: "hidden",
          position: "relative"
        }}
      >
        <div
          style={{
            width: "50%",
            height: "100%",
            background: "#c8471a",
            animation: "shimmer 1.1s infinite ease-in-out"
          }}
        />
      </div>
    </div>
  );
}

function GlobalToast({ toast }) {
  if (!toast) return null;
  const isWarn = toast.type === "warning" || toast.type === "error";
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        top: "72px",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 999999,
        display: "inline-flex",
        alignItems: "center",
        gap: "10px",
        padding: "10px 22px",
        borderRadius: "30px",
        fontSize: "0.82rem",
        fontWeight: 700,
        letterSpacing: "0.02em",
        boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
        background: isWarn ? "#180C09" : "#111111",
        color: isWarn ? "#FF7A59" : "#FFFFFF",
        border: isWarn ? "1.5px solid rgba(255, 122, 89, 0.5)" : "1px solid rgba(255, 255, 255, 0.25)",
        animation: "fadeIn 0.2s ease-out",
        maxWidth: "min(460px, calc(100vw - 32px))",
        textAlign: "center",
        pointerEvents: "none"
      }}
    >
      {isWarn ? (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
      <span>{toast.message}</span>
    </div>
  );
}

export default function BordApp() {
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("bord_theme") || "light"; } catch(_) { return "light"; }
  });
  const [appToast, setAppToast] = useState(null); // { message, type: 'info'|'warning'|'error'|'success' }
  const showAppToast = useCallback((message, type = "info") => {
    setAppToast({ message, type });
    setTimeout(() => {
      setAppToast((cur) => (cur?.message === message ? null : cur));
    }, 3200);
  }, []);
  useEffect(() => {
    const isDark = theme === "dark";
    document.body.classList.toggle("dark", isDark);
    document.body.classList.toggle("light", !isDark);
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.background = isDark ? "#0F0F0F" : "#FAFAF7";
    localStorage.setItem("bord_theme", theme);
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) metaTheme.setAttribute("content", isDark ? "#0F0F0F" : "#FAFAF7");
  }, [theme]);

  const [products, setProducts]     = useState(() => getCachedProducts()); // instant from cache
  const [splashDone, setSplashDone] = useState(true);
  const [cart, setCart]             = useState([]);
  const [storeSettings, setStoreSettings] = useState(() => {
    const cached = getCachedSettings();
    return {
      shipping_fee: Number(cached?.shipping_fee ?? 99),
      free_shipping_above: Number(cached?.free_shipping_above ?? 1499),
      pay_card: "0", pay_upi_apps: "1", pay_upi_qr: "1", pay_cod: "1",
      show_fabric_certifications: cached?.show_fabric_certifications ?? "0",
      location_enabled: cached?.location_enabled !== undefined ? String(cached.location_enabled) : "1",
      pdp_show_coupons: cached?.pdp_show_coupons !== undefined ? String(cached.pdp_show_coupons) : "1",
      pdp_show_reviews: cached?.pdp_show_reviews !== undefined ? String(cached.pdp_show_reviews) : "1",
      pdp_show_measurements: cached?.pdp_show_measurements !== undefined ? String(cached.pdp_show_measurements) : "1",
      pdp_show_pincode: cached?.pdp_show_pincode !== undefined ? String(cached.pdp_show_pincode) : "1",
      pdp_show_trust_markers: cached?.pdp_show_trust_markers !== undefined ? String(cached.pdp_show_trust_markers) : "1",
    };
  });
  const [downtimeActive, setDowntimeActive] = useState(() => {
    const cached = getCachedSettings();
    if (!cached) return false;
    const dtEnabled = cached.downtime_enabled === "1";
    const dtUntil   = cached.downtime_until || "";
    const dtExpired = dtUntil && new Date(dtUntil) <= new Date();
    return dtEnabled && !dtExpired;
  });
  const [downtimeUntil,  setDowntimeUntil]  = useState(() => {
    const cached = getCachedSettings();
    return cached?.downtime_until || "";
  });
  const [downtimeMsg,    setDowntimeMsg]    = useState(() => {
    const cached = getCachedSettings();
    return cached?.downtime_message || "We're updating the store. Back soon!";
  });
  const isAdminBypass = new URLSearchParams(window.location.search).get("admin") === "1";

  // Location Module state (Amazon/Flipkart style header delivery location)
  const [activeLocation, setActiveLocation] = useState(() => {
    try {
      const raw = localStorage.getItem("bord_active_location");
      return raw ? JSON.parse(raw) : null;
    } catch(_) { return null; }
  });
  const [locationExpanded, setLocationExpanded] = useState(false);

  const handleSelectLocation = (loc) => {
    setActiveLocation(loc);
    try { localStorage.setItem("bord_active_location", JSON.stringify(loc)); } catch(_) {}
    setLocationExpanded(false);
  };

  const [footerSettings, setFooterSettings] = useState(() => {
    const cached = getCachedSettings();
    return {
      social_instagram: "",
      social_pinterest: "",
      social_youtube: "",
      social_whatsapp: "",
      help_shipping_returns: "",
      help_contact_email: "bordtrynew@gmail.com",
      location_enabled: cached?.location_enabled !== undefined ? String(cached.location_enabled) : "1",
    };
  });
  const [dropSettings, setDropSettings] = useState(() => {
    const cached = getCachedSettings();
    return {
      loud_drop_label:    cached?.loud_drop_label    || "",
      loud_drop_subtitle: cached?.loud_drop_subtitle || "",
      loud_drop_end_date: cached?.loud_drop_end_date || "",
      loud_drop_products: cached?.loud_drop_products || "",
      loud_drop_series:   cached?.loud_drop_series   || "",
      loud_drop_styles:   cached?.loud_drop_styles   || "",
    };
  });
  const [cartOpen, setCartOpen]     = useState(false);
  const [selectedCollectibleCard, setSelectedCollectibleCard] = useState(() => {
    try {
      const saved = localStorage.getItem("bords_selected_card");
      return saved ? JSON.parse(saved) : null;
    } catch (_) { return null; }
  });
  const [spinModalOpen, setSpinModalOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [wishlist, setWishlist]     = useState(() => { try { return JSON.parse(localStorage.getItem("bord_wishlist") || "[]"); } catch(_) { return []; } });
  const [wishlistOpen,  setWishlistOpen]  = useState(false);
  const [wishlistHover, setWishlistHover] = useState(false);
  const [userMenuOpen,  setUserMenuOpen]  = useState(false);
  useEffect(() => {
    if (!userMenuOpen) return;
    const closeMenu = () => setUserMenuOpen(false);
    window.addEventListener("click", closeMenu);
    return () => window.removeEventListener("click", closeMenu);
  }, [userMenuOpen]);
  const [annBarItems, setAnnBarItems] = useState([
    "🔥 Secure Checkout", "↩ Easy 7-Day Returns", "🚚 Free Ship Over ₹999", "⚡ Same-Day Processing", "😊 1000+ Happy Customers"
  ]);
  const [trackTab, setTrackTab]     = useState(false);
  const [tickerItems, setTickerItems] = useState(() => {
    // Load ticker from cache instantly — no flash of default text
    const cached = getCachedSettings();
    if (cached?.ticker) return [...cached.ticker, ...cached.ticker];
    const fallback = ["Free Shipping Over ₹1499","New Drop — Shadow Series","Limited Qty Available","Use Code BORDS15 for 15% Off"];
    return [...fallback, ...fallback];
  });
  const [activeCategory, setActiveCategory] = useState(() => {
    const p = typeof window !== 'undefined' ? window.location.pathname : '';
    if (p === "/shop/tees") return "tops";
    if (p === "/shop/bottoms") return "bottoms";
    if (p === "/shop/accessories") return "accessories";
    return "all";
  });
  const [fabricFilter, setFabricFilter] = useState("all");
  const [showSizeGuideGlobal, setShowSizeGuideGlobal] = useState(false);
  const [catSections,     setCatSections]     = useState({ tops:true, bottoms:true, accessories:true });
  const [prevPage, setPrevPage]     = useState("home");
  const [ordersInitialTab, setOrdersInitialTab] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const path = window.location.pathname;
      if (path === "/addresses" || path === "/saved-addresses" || params.get("tab") === "addresses") return "addresses";
      if (path === "/reviews" || params.get("tab") === "reviews") return "reviews";
    } catch (_) {}
    return "orders";
  });
  const [page, setPage]             = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("reset"))  return "auth"; // password reset link
    const path = window.location.pathname;
    if (path === "/track" || path.startsWith("/track/")) return "track";
    if (path === "/checkout" || path === "/cart") return "checkout";
    if (path === "/auth")        return "auth";
    if (path === "/myorders" || path === "/addresses" || path === "/saved-addresses" || path === "/reviews") return "myorders";
    if (path === "/admin")       return "admin";
    if (path === "/favorites")   return "favorites";
    if (path === "/our-fabrics") return "our-fabrics";
    if (path === "/sustainability") return "sustainability";
    if (path === "/care")        return "care";
    if (path === "/certifications") return "certifications";
    if (path === "/journal" || path.startsWith("/journal/")) return "journal";
    if (path === "/about")       return "about";
    if (path === "/contact")     return "contact";
    if (path === "/shop" || path.startsWith("/shop/")) return "shop";
    if (path === "/cards" || path.startsWith("/cards/") || path === "/lore" || path.startsWith("/lore/") || params.get("page") === "cards" || params.get("page") === "lore" || params.get("card") || params.get("code") || params.get("yodha") || params.get("lore")) return "cards";
    if (path === "/comics" || path.startsWith("/comics/") || path === "/comic" || path === "/stories") return "comics";
    if (path.startsWith("/collections/")) return "collection";
    if (path.startsWith("/product/") || path.startsWith("/products/")) return "product";
    return "home";
  });

  const pageRef = useRef(page);
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // ── Site-wide SEO & Canonical Management ──────────────────────────────────
  useEffect(() => {


    if (page === "home") {
      setPageMeta({
        title: "BORDS | Oversized Streetwear & Heavyweight T-Shirts India",
        description: "Shop BORDS — Indian streetwear made for people bored of routine designs. Discover heavyweight oversized T-shirts and contemporary everyday styles. Free shipping across India.",
        canonical: "https://www.bords.co.in/",
      });
      injectFAQSchema(HOMEPAGE_FAQS);
    } else {
      removeFAQSchema();
      if (page === "shop") {
        setPageMeta({
          title: "Shop All Streetwear | Heavyweight & Oversized Tees | BORDS",
          description: "Explore the full BORDS collection. Premium heavyweight 240 GSM oversized tees, streetwear drops, and modern essentials.",
          canonical: "https://www.bords.co.in/shop",
        });
      } else if (page === "our-fabrics") {
      setPageMeta({
        title: "Our Fabrics | 240 GSM Organic Cotton & Craftsmanship | BORDS",
        description: "Learn about the heavyweight fabric, GSM specifications, and ethical craftsmanship behind every BORDS streetwear garment.",
        canonical: "https://www.bords.co.in/our-fabrics",
      });
    } else if (page === "sustainability") {
      setPageMeta({
        title: "Sustainability & Transparency | BORDS",
        description: "Our commitment to sustainable production, certified organic cotton, and radical transparency.",
        canonical: "https://www.bords.co.in/sustainability",
      });
    } else if (page === "about") {
      setPageMeta({
        title: "About BORDS | Bored of Routine Designs",
        description: "The story behind BORDS — Bangalore-born streetwear brand built for people bored of routine designs.",
        canonical: "https://www.bords.co.in/about",
      });
    } else if (page === "contact") {
      setPageMeta({
        title: "Contact BORDS | Customer Support Bangalore",
        description: "Get in touch with the BORDS team for order inquiries, support, or collaboration.",
        canonical: "https://www.bords.co.in/contact",
      });
      } else if (page === "checkout" || page === "auth" || page === "admin" || page === "myorders") {
        setPageMeta({
          title: "BORDS",
          noindex: true,
          canonical: "https://www.bords.co.in/",
        });
      }
    }
    // Note: 'product' page sets its own dynamic SEO in ProductDetailPage
  }, [page]);

  // ── Vibe Mode (Section 4B): Persistent Site-wide Control (Loud vs Quiet) ──
  const [vibe, setVibe] = useState(() => {
    try {
      if (typeof window !== "undefined") {
        if ('scrollRestoration' in window.history) {
          window.history.scrollRestoration = 'manual';
        }
        if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          return "quiet";
        }
        const saved = localStorage.getItem("bords_vibe");
        if (saved === "loud" || saved === "quiet") return saved;
      }
    } catch (_) {}
    return "quiet";
  });
  const [vibeTransition, setVibeTransition] = useState(null);

  const toggleVibe = () => {
    setVibe(v => {
      const next = v === "loud" ? "quiet" : "loud";
      try { localStorage.setItem("bords_vibe", next); } catch (_) {}
      setVibeTransition(next);
      setTimeout(() => setVibeTransition(null), 1400);
      return next;
    });
  };

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.body.classList.toggle("vibe-loud", vibe === "loud");
      document.body.classList.toggle("vibe-quiet", vibe === "quiet");
      document.documentElement.setAttribute("data-vibe", vibe);
    }
  }, [vibe]);

  useEffect(() => {
    try {
      const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      const handler = (e) => {
        if (e.matches) {
          setVibe("quiet");
          localStorage.setItem("bords_vibe", "quiet");
        }
      };
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    } catch (_) {}
  }, []);

  // ── Creator & UTM Referral Attribution (Phase 22) ──────────────────────────
  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref") || params.get("creator") || "";
      const source = params.get("utm_source") || "";
      const medium = params.get("utm_medium") || "";
      const campaign = params.get("utm_campaign") || "";
      const content = params.get("utm_content") || "";
      const term = params.get("utm_term") || "";

      if (ref || source || medium || campaign) {
        const utmRecord = {
          ref: ref || campaign,
          source: source || (ref ? "creator_link" : "organic"),
          medium: medium || (ref ? "creator" : "referral"),
          campaign: campaign || ref,
          content,
          term,
          landed_at: new Date().toISOString(),
          landing_page: window.location.pathname,
        };
        localStorage.setItem("bords_utm_params", JSON.stringify(utmRecord));

        // Fire custom analytics event if creator code exists
        if (utmRecord.ref) {
          try {
            const { analytics } = require("../lib/seo");
            analytics.event("creator_click", {
              creator_code: utmRecord.ref,
              source: utmRecord.source,
              medium: utmRecord.medium,
            });
          } catch (_) {}
        }
      }
    } catch (_) {}
  }, []);

  // ── Khadi 7-Piece Capsule Waitlist Modal State ──
  const [waitlistModalOpen, setWaitlistModalOpen] = useState(false);
  const [waitlistProduct, setWaitlistProduct] = useState(null);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedComicCard, setSelectedComicCard] = useState(null);
  const [email, setEmail]           = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [subError,    setSubError]    = useState("");
  const [subLoading, setSubLoading] = useState(false);
  const [openFaq, setOpenFaq]       = useState(null);
  const [dbStatus, setDbStatus]     = useState("connecting");
  const [isMobile, setIsMobile]     = useState(() => window.innerWidth <= 900);
  const [menuOpen, setMenuOpen]     = useState(false);
  const [collections, setCollections] = useState(() => getCachedCollections());
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [user, setUser] = useState(() => getStoredUser());

  // ── Scroll to top on every page or product transition (CRITICAL UX FIX) ──
  const selectedProductId = selectedProduct?.ID ?? selectedProduct?.id;
  useEffect(() => {
    const resetScroll = () => {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      } catch (_) {
        try { window.scrollTo(0, 0); } catch (__) {}
      }
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    };

    // Stage 1: Synchronous immediate reset
    resetScroll();

    // Stage 2: Next frame after DOM mount
    const rafId = requestAnimationFrame(resetScroll);

    // Stage 3: Small timeout to defeat any asynchronous mobile browser scroll restoration
    const timerId = setTimeout(resetScroll, 60);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timerId);
    };
  }, [page, selectedProductId]);



  const [savedAddresses, setSavedAddresses] = useState([]);

  useEffect(() => {
    if (!user?.id) { setSavedAddresses([]); return; }
    fetch(`${API}/delivery/addresses?user_id=${user.id}&_t=${Date.now()}`, { cache: "no-store", headers: { ...(localStorage.getItem("bord_token") ? { Authorization: `Bearer ${localStorage.getItem("bord_token")}` } : {}) } })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(({ data }) => setSavedAddresses(data || []))
      .catch(() => {});

    const handleAddrEvt = (e) => {
      if (e.detail && Array.isArray(e.detail)) {
        setSavedAddresses(e.detail);
      }
    };
    window.addEventListener('bord_address_updated', handleAddrEvt);
    return () => window.removeEventListener('bord_address_updated', handleAddrEvt);
  }, [user]);

  const [heroContent, setHeroContent] = useState(() => {
    const cached = getCachedSettings();
    return {
      // slide 1 — Brand Callout
      hero_1_title1: cached?.hero_1_title1 || "TRY",
      hero_1_title2: cached?.hero_1_title2 || "NEW",
      hero_1_subtitle: cached?.hero_1_subtitle || "Bored of Routine Designs, cut for people who want the quiet confidence of authentic heavyweight streetwear.",
      hero_1_tag: cached?.hero_1_tag || "SS 26",
      hero_1_overline: cached?.hero_1_overline || "The Class You Need",
      hero_1_mood: cached?.hero_1_mood || "Editorial Drop",
      hero_1_material: cached?.hero_1_material || "Tailored Streetwear",
      // slide 2 — Primary Indian Streetwear Heritage
      hero_2_title1: cached?.hero_2_title1 || "INDIAN",
      hero_2_title2: cached?.hero_2_title2 || "STREETWEAR",
      hero_2_subtitle: cached?.hero_2_subtitle || "Indian streetwear for people bored of routine designs. Heavyweight 240 GSM oversized T-shirts crafted in Bangalore.",
      hero_2_tag: cached?.hero_2_tag || "240 GSM",
      hero_2_overline: cached?.hero_2_overline || "Bangalore Streetwear",
      hero_2_mood: cached?.hero_2_mood || "Heavyweight 240 GSM",
      hero_2_material: cached?.hero_2_material || "Oversized Fit",
      // slide 3 — Minimal Drop
      hero_3_title1: cached?.hero_3_title1 || "BLANK",
      hero_3_title2: cached?.hero_3_title2 || "STATE",
      hero_3_subtitle: cached?.hero_3_subtitle || "Stripped to essence, styled for presence. No loud logos, just heavyweight cuts that make the room remember you.",
      hero_3_tag: cached?.hero_3_tag || "CORE",
      hero_3_overline: cached?.hero_3_overline || "Everyday Form",
      hero_3_mood: cached?.hero_3_mood || "Pure Expression",
      hero_3_material: cached?.hero_3_material || "Limited Drop",
    };
  });
  const [storyContent, setStoryContent] = useState({
    story_heading: "Built on the Edge of Everything",
    story_body:    "BORDS — Bored of Routine Designs. Born from the belief that everyday wear deserves the same intention as high fashion. Every piece lives in the tension between precision tailoring and effortless comfort. No noise. Just form.",
    story_year:    "2019",
  });

  const heroSlides = [1, 2, 3].map(n => ({
    title1:   heroContent[`hero_${n}_title1`],
    title2:   heroContent[`hero_${n}_title2`],
    subtitle: heroContent[`hero_${n}_subtitle`],
    tag:      heroContent[`hero_${n}_tag`],
    overline: heroContent[`hero_${n}_overline`],
    mood:     heroContent[`hero_${n}_mood`],
    material: heroContent[`hero_${n}_material`],
  }));
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    if (page !== "home") return;
    const int = setInterval(() => setHeroIndex(i => (i + 1) % heroSlides.length), isMobile ? 6500 : 5500);
    return () => clearInterval(int);
  }, [heroSlides.length, isMobile, page]);

  const handleLogout = useCallback(() => {
    clearStoredSession();
    localStorage.removeItem("bord_guest_cart");
    setUser(null);
    setCart([]);
  }, []);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("bord_token");
    if (!token) {
      handleLogout();
      return;
    }
    // Sync logged in user profile to Crisp chat
    if (typeof window !== "undefined" && window.$crisp) {
      const rawName = (user.name || user.username || (user.email ? user.email.split("@")[0] : "Member")).trim();
      const cleanUsername = rawName.replace(/^@+/, "");
      window.$crisp.push(["set", "user:nickname", [cleanUsername]]);
      if (user.email) window.$crisp.push(["set", "user:email", [user.email]]);
      if (user.phone) window.$crisp.push(["set", "user:phone", [user.phone]]);
    }
  }, [user, handleLogout]);

  // Sync products to window for real-time Crisp chat availability queries
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.__BORDS_PRODUCTS__ = products;
    }
  }, [products]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Fetch collections helper — caches to localStorage
  const fetchCollections = useCallback(() => {
    fetch(`${API}/collections?_=${Date.now()}`)
      .then(r => r.ok ? r.json() : { data: [] })
      .then(({ data }) => {
        if (data && data.length > 0) {
          const freshCols = toUpper(data).map(col => ({
            ...col,
            IS_ACTIVE: Number(col.IS_ACTIVE) !== 0 ? 1 : 0,
          }));
          setCollections(freshCols);
          setCachedCollections(freshCols);
        }
      })
      .catch(err => console.warn('Collections fetch failed:', err.message));
  }, []);

  // Fetch products helper — saves to localStorage for instant next-visit load
  const fetchProducts = useCallback(() => {
    api.getProducts()
      .then(({ data }) => {
        const fresh = data || [];
        if (fresh.length > 0) {
          setProducts(fresh);
          setCachedProducts(fresh);
          if (typeof window !== "undefined") window.__BORDS_PRODUCTS__ = fresh;
        }
        setDbStatus("live");
        setSplashDone(true);
      })
      .catch(() => { setDbStatus("offline"); setSplashDone(true); });
  }, []);

  // Fetch on mount — instant first paint, revalidate in background
  useEffect(() => {
    // Dismiss initial loader & splash screen immediately, record session
    try {
      if (typeof window !== "undefined") {
        sessionStorage.setItem("bord_splash_seen", "1");
        sessionStorage.setItem("bords_session_started", "1");
        document.documentElement.classList.add("bords-mounted");
        const staticShell = document.getElementById("bords-static-shell");
        if (staticShell && staticShell.parentNode) {
          staticShell.parentNode.removeChild(staticShell);
        }
      }
    } catch(_) {}
    setSplashDone(true);

    const initLoader = typeof document !== "undefined" ? document.getElementById("bords-initial-loader") : null;
    if (initLoader) {
      initLoader.classList.add("fade-out");
      setTimeout(() => {
        try {
          if (initLoader && initLoader.parentNode) {
            initLoader.parentNode.removeChild(initLoader);
          }
        } catch(_) {}
      }, 400);
    }

    // Wake Render immediately (fire-and-forget)
    fetch(`${API.replace("/api","")}/ping`).catch(()=>{});

    // Schedule background revalidation so initial paint is instant without CPU contention
    const tProd = setTimeout(() => {
      api.getProducts().then(({ data }) => {
        const fresh = data || [];
        if (fresh.length > 0) {
          setProducts(fresh);
          setCachedProducts(fresh);
          if (typeof window !== "undefined") window.__BORDS_PRODUCTS__ = fresh;
        }
        setDbStatus("live");
        if (fresh.length > 0 && (window.location.pathname.startsWith("/product/") || window.location.pathname.startsWith("/products/"))) {
          const state = window.history.state;
          const reqId = getProductIdFromPath(window.location.pathname) || state?.productId;
          const match = fresh.find((p) =>
            Number(p.ID ?? p.id) === Number(reqId) ||
            p.slug === reqId ||
            `bords-${(p.NAME || p.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}` === reqId
          );
          if (match) setSelectedProduct(match);
        }
      }).catch(() => { setDbStatus("offline"); });
    }, 40);

    const tCol = setTimeout(() => {
      fetchCollections();
    }, 80);

    return () => {
      clearTimeout(tProd);
      clearTimeout(tCol);
    };
  }, [fetchCollections]);

  // Prefetch critical screens (PDP, Checkout) during idle time for 0ms user interaction response
  useEffect(() => {
    const prefetchKeyRoutes = () => {
      try {
        import("./NoberoProductScreen");
        import("./CheckoutPage");
      } catch (_) {}
    };
    if (typeof window !== "undefined") {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(prefetchKeyRoutes, { timeout: 2000 });
      } else {
        setTimeout(prefetchKeyRoutes, 1000);
      }
    }
  }, []);

  // Re-fetch whenever the tab/window regains focus if not fetched within 2 minutes
  useEffect(() => {
    let focusTimer;
    let lastRefreshed = Date.now();
    const onFocus = () => {
      if (pageRef.current === "admin") return;
      if (Date.now() - lastRefreshed < 120000) return;
      clearTimeout(focusTimer);
      focusTimer = setTimeout(() => {
        lastRefreshed = Date.now();
        fetchProducts();
        fetchCollections();
      }, isMobile ? 2000 : 800);
    };
    window.addEventListener("focus", onFocus);
    return () => {
      clearTimeout(focusTimer);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchProducts, fetchCollections, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  // URL-aware navigation — keeps URL in sync so refresh lands on correct page
  // ── Build ticker from live settings + active coupons ───────────────────────
  // ── Fetch ALL settings + rebuild ticker + category toggles ────────────────
  const refreshSettings = useCallback(() => {
    Promise.all([
      fetch(`${API}/settings`).then(r=>r.ok?r.json():{data:{}}).catch(()=>({data:{}})),
      fetch(`${API}/coupons/active`).then(r=>r.ok?r.json():{data:[]}).catch(()=>({data:[]}))
    ]).then(([settingsRes, couponsRes]) => {
      const s = settingsRes.data || {};
      if (settingsRes.data && Object.keys(settingsRes.data).length > 0) {
        setCachedSettings(settingsRes.data);
      }

      // Update category toggles
      setCatSections({
        tops:        s.section_tops        !== "0",
        bottoms:     s.section_bottoms     !== "0",
        accessories: s.section_accessories !== "0",
      });

      // ── Load hero slide content ──────────────────────────────────────────
      const heroKeys = {};
      [1,2,3].forEach(n => {
        ["title1","title2","subtitle","tag","overline","mood","material"].forEach(f => {
          const k = `hero_${n}_${f}`;
          if (s[k] !== undefined && s[k] !== "") heroKeys[k] = s[k];
        });
      });
      if (Object.keys(heroKeys).length > 0) {
        setHeroContent(prev => ({ ...prev, ...heroKeys }));
      }

      // ── Load story content ───────────────────────────────────────────────
      const storyUpdate = {};
      if (s.story_heading !== undefined && s.story_heading !== "") storyUpdate.story_heading = s.story_heading;
      if (s.story_body    !== undefined && s.story_body    !== "") storyUpdate.story_body    = s.story_body;
      if (s.story_year    !== undefined && s.story_year    !== "") storyUpdate.story_year    = s.story_year;
      if (Object.keys(storyUpdate).length > 0) {
        setStoryContent(prev => ({ ...prev, ...storyUpdate }));
      }

      // ── Propagate shipping + payment settings to CheckoutPage ──
      const locEnabled = s.location_enabled !== undefined ? String(s.location_enabled) : "1";
      setStoreSettings(prev => {
        const next = {
          shipping_fee:        Number(s.shipping_fee        ?? 99),
          free_shipping_above: Number(s.free_shipping_above ?? 1499),
          pay_card:     s.pay_card     ?? "0",
          pay_upi_apps: s.pay_upi_apps ?? "1",
          pay_upi_qr:   s.pay_upi_qr   ?? "1",
          pay_cod:      s.pay_cod      ?? "1",
          show_fabric_certifications: s.show_fabric_certifications !== undefined ? s.show_fabric_certifications : "0",
          location_enabled:    locEnabled,
          pdp_show_coupons:    s.pdp_show_coupons !== undefined ? String(s.pdp_show_coupons) : "1",
          pdp_show_reviews:    s.pdp_show_reviews !== undefined ? String(s.pdp_show_reviews) : "1",
          pdp_show_measurements: s.pdp_show_measurements !== undefined ? String(s.pdp_show_measurements) : "1",
          pdp_show_pincode:    s.pdp_show_pincode !== undefined ? String(s.pdp_show_pincode) : "1",
          pdp_show_trust_markers: s.pdp_show_trust_markers !== undefined ? String(s.pdp_show_trust_markers) : "1",
        };
        const isSame = prev && Object.keys(next).every(k => prev[k] === next[k]);
        return isSame ? prev : next;
      });

      // ── Propagate Follow & Help links ──
      setFooterSettings(prev => {
        const next = {
          social_instagram:      s.social_instagram      || "",
          social_pinterest:      s.social_pinterest      || "",
          social_youtube:        s.social_youtube        || "",
          social_whatsapp:       s.social_whatsapp       || "",
          help_shipping_returns: s.help_shipping_returns || "",
          help_contact_email:    s.help_contact_email    || "bordtrynew@gmail.com",
          location_enabled:      locEnabled,
        };
        const isSame = prev && Object.keys(next).every(k => prev[k] === next[k]);
        return isSame ? prev : next;
      });

      // ── Propagate Loud Mode drop banner settings ──
      setDropSettings(prev => {
        const next = {
          loud_drop_label:    s.loud_drop_label    || "",
          loud_drop_subtitle: s.loud_drop_subtitle || "",
          loud_drop_end_date: s.loud_drop_end_date || "",
          loud_drop_products: s.loud_drop_products || "",
          loud_drop_series:   s.loud_drop_series   || "",
          loud_drop_styles:   s.loud_drop_styles   || "",
        };
        const isSame = prev && Object.keys(next).every(k => prev[k] === next[k]);
        return isSame ? prev : next;
      });

      // Rebuild ticker slides
      const freeAbove    = Number(s.free_shipping_above || 1499);
      const customSlides = [s.ticker_1, s.ticker_2, s.ticker_3].filter(v => v && v.trim());
      const base = [
        `Free Shipping Over ₹${freeAbove.toLocaleString()}`,
        ...(customSlides.length > 0 ? customSlides : ["New Drop — Shadow Series", "Limited Qty Available"]),
      ];
      const coupons = toUpper(couponsRes.data || []).map(cp =>
        `Use Code ${cp.CODE} for ${cp.DISCOUNT_PCT}% Off${cp.MIN_ORDER ? " on orders above ₹" + Number(cp.MIN_ORDER).toLocaleString() : ""}`
      );
      const items = [...base, ...coupons];
      setTickerItems([...items, ...items]);
      // Cache for instant next load
      setCachedSettings({
        ticker: items,
        downtime_enabled: s.downtime_enabled,
        downtime_until: s.downtime_until,
        downtime_message: s.downtime_message,
        location_enabled: s.location_enabled,
        shipping_fee: s.shipping_fee,
        free_shipping_above: s.free_shipping_above,
        show_fabric_certifications: s.show_fabric_certifications,
        loud_drop_label:    s.loud_drop_label,
        loud_drop_subtitle: s.loud_drop_subtitle,
        loud_drop_end_date: s.loud_drop_end_date,
        loud_drop_products: s.loud_drop_products,
        loud_drop_series:   s.loud_drop_series,
        loud_drop_styles:   s.loud_drop_styles,
      });

      // Rebuild announcement bar from settings
      const annKeys = [s.annbar_1, s.annbar_2, s.annbar_3, s.annbar_4, s.annbar_5].filter(v => v && v.trim());
      if (annKeys.length > 0) setAnnBarItems(annKeys);

      // ── Downtime mode ──────────────────────────────────────────────────────
      const dtEnabled = s.downtime_enabled === "1";
      const dtUntil   = s.downtime_until || "";
      const dtExpired = dtUntil && new Date(dtUntil) <= new Date();
      setDowntimeActive(dtEnabled && !dtExpired);
      setDowntimeUntil(dtUntil);
      setDowntimeMsg(s.downtime_message || "We're updating the store. Back soon!");
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Run after initial paint completes — zero contention on first render
    const initialTimer = setTimeout(refreshSettings, 120);

    // Update on tab focus (switching back from admin panel)
    let settingsFocusTimer;
    const onSettingsFocus = () => {
      if (pageRef.current === "admin") return;
      clearTimeout(settingsFocusTimer);
      settingsFocusTimer = setTimeout(refreshSettings, isMobile ? 1500 : 400);
    };
    window.addEventListener("focus", onSettingsFocus);

    // Poll every 60 seconds — ticker/settings stay live
    const poll = setInterval(refreshSettings, 60000);

    // ── Admin save events → instant update in BordApp ──────────────────────
    const onSettingsSave    = () => refreshSettings();
    const onProductsSave    = () => fetchProducts();
    const onCollectionsSave = () => fetchCollections();

    window.addEventListener("bords-settings-saved",    onSettingsSave);
    window.addEventListener("bords-products-saved",    onProductsSave);
    window.addEventListener("bords-collections-saved", onCollectionsSave);

    return () => {
      clearTimeout(initialTimer);
      clearTimeout(settingsFocusTimer);
      window.removeEventListener("focus", onSettingsFocus);
      window.removeEventListener("bords-settings-saved",    onSettingsSave);
      window.removeEventListener("bords-products-saved",    onProductsSave);
      window.removeEventListener("bords-collections-saved", onCollectionsSave);
      clearInterval(poll);
    };
  }, [refreshSettings, fetchProducts, fetchCollections, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigate = (newPage, product = null, extra = null) => {
    const pathMap = {
      home: "/", shop: "/shop", checkout: "/checkout", auth: "/auth", admin: "/admin",
      myorders: "/myorders", favorites: "/favorites", track: "/track",
      collection: "/collections",
      // ── Content pages ─────────────────────────────────────────────────────
      "our-fabrics":    "/our-fabrics",
      sustainability:   "/sustainability",
      care:             "/care",
      certifications:   "/certifications",
      journal:          "/journal",
      about:            "/about",
      contact:          "/contact",
      cards:            "/cards",
      comics:           "/comics",
      stories:          "/comics",
    };
    const colId = extra ? (extra.ID ?? extra.id) : null;
    let newPath;
    if (newPage === "addresses") {
      newPage = "myorders";
      extra = { ...(extra || {}), tab: "addresses" };
    }
    if (newPage === "myorders") {
      const targetTab = extra?.tab || "orders";
      setOrdersInitialTab(targetTab);
      newPath = targetTab === "addresses" ? "/addresses" : (targetTab === "reviews" ? "/reviews" : "/myorders");
    }
    else if (newPage === "track") {
      const orderNum = extra?.orderNumber || (typeof extra === "string" ? extra : null);
      newPath = orderNum ? `/track/${encodeURIComponent(orderNum)}` : "/track";
    }
    else if (newPage === "product" && (product?.ID ?? product?.id)) {
      // Prefer slug-based URL; fall back to numeric ID for compatibility
      const pName = product.NAME || product.name || '';
      const slug  = product.slug || (pName ? `bords-${pName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}` : null);
      newPath = slug ? `/products/${slug}` : `/product/${product.ID ?? product.id}`;
    }
    else if (newPage === "collection") {
      const slug = extra?.slug || (extra?.NAME || extra?.name ? (extra.NAME || extra.name).toLowerCase().replace(/[^a-z0-9]+/g, '-') : (colId || "oversized-t-shirts"));
      newPath = `/collections/${slug}`;
    }
    else if (newPage === "shop") {
      if (extra?.category && extra.category !== 'all') {
        const catSlug = extra.category === "tops" ? "tees" : extra.category;
        newPath = `/shop/${catSlug}`;
      } else {
        newPath = "/shop";
      }
    }
    else if (newPage === "journal") {
      const slug = extra?.slug || (typeof extra === "string" ? extra : null);
      newPath = slug ? `/journal/${slug}` : "/journal";
    }
    else newPath = pathMap[newPage] || "/";

    window.history.pushState({
      page: newPage,
      ordersTab: extra?.tab,
      productId: product ? (product.ID ?? product.id) : null,
      collectionId: colId,
      category: extra?.category,
      prevPage: page
    }, "", newPath);

    // ── GA4: fire page_view on every navigation ──────────────────────────────
    try {
      const { analytics } = require('../lib/seo');
      analytics.pageView(document.title, window.location.href);
    } catch (_) {}

    if (product) setSelectedProduct(product);
    else if (newPage !== "product") setSelectedProduct(null);
    if (newPage === "comics") setSelectedComicCard(extra || null);
    if (newPage === "collection" && extra) setSelectedCollection(extra);
    else if (extra && (extra.ID !== undefined || extra.id !== undefined) && !extra.category) setSelectedCollection(extra);
    if (extra?.category) setActiveCategory(extra.category);
    if (extra?.fabricTag) setFabricFilter(extra.fabricTag);
    if (newPage !== "auth") setPrevPage(page);
    setPage(newPage);
    // ── Scroll to top on every navigation (CRITICAL: prevents product page from
    // mounting mid-scroll, which was causing Buy Now to appear before product image)
    try { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); } catch (_) {
      try { window.scrollTo(0, 0); } catch (__) {}
    }
  };

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    window.__bordsNavigate = (targetPage, prod = null, ext = null) => navigateRef.current?.(targetPage, prod, ext);
    return () => { try { delete window.__bordsNavigate; } catch (_) {} };
  }, []);

  const handleAdminExit = useCallback(() => {
    fetchProducts();
    fetchCollections();
    refreshSettings();
    navigateRef.current?.("home");
  }, [fetchProducts, fetchCollections, refreshSettings]);

  // Prefetch interactive views when browser is idle to avoid Suspense fallback flicker
  useEffect(() => {
    const prefetchTimer = setTimeout(() => {
      import("./cards/CardsVaultPage");
      import("./comics/SoloLevelingComicReader");
      import("./AdminPanel");
    }, 2500);
    return () => clearTimeout(prefetchTimer);
  }, []);

  const handleLogin = (u) => {
    setUser(u);
    const dest = prevPage && prevPage !== "auth" ? prevPage : "home";
    navigate(dest);
  };

  // Handle browser back/forward buttons
  useEffect(() => {
    const onPop = (e) => {
      const state = e.state;
      const path = window.location.pathname;
      if (!state) {
        setSelectedProduct(null);
        setSelectedCollection(null);
        if (path === "/" || path === "") setPage("home");
        else if (path === "/cards" || path.startsWith("/cards/")) setPage("cards");
        else if (path === "/comics" || path.startsWith("/comics/") || path === "/stories") setPage("comics");
        else if (path === "/shop" || path.startsWith("/shop/")) setPage("shop");
        else if (path.startsWith("/products/") || path.startsWith("/product/")) setPage("product");
        else if (path.startsWith("/collections/")) setPage("collection");
        else setPage("home");
        return;
      }

      let targetPage = state.page;
      if (!targetPage) {
        if (path === "/" || path === "") targetPage = "home";
        else if (path === "/cards" || path.startsWith("/cards/")) targetPage = "cards";
        else if (path === "/comics" || path.startsWith("/comics/") || path === "/stories") targetPage = "comics";
        else if (path === "/shop" || path.startsWith("/shop/")) targetPage = "shop";
        else if (path.startsWith("/products/") || path.startsWith("/product/")) targetPage = "product";
        else if (path.startsWith("/collections/")) targetPage = "collection";
        else targetPage = "home";
      }

      if (targetPage !== "product") setSelectedProduct(null);
      if (targetPage !== "collection") {
        setSelectedCollection(null);
      } else if (state.collectionId) {
        const allCols = [...(collections.length > 0 ? collections : []), ...DEFAULT_COLLECTIONS];
        const target = String(state.collectionId).toLowerCase();
        const found = allCols.find(c =>
          String(c.slug || '').toLowerCase() === target ||
          String(c.ID ?? c.id).toLowerCase() === target ||
          (c.NAME || c.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-') === target
        );
        if (found) setSelectedCollection(found);
      }
      if (state.ordersTab) setOrdersInitialTab(state.ordersTab);
      else if (path === "/addresses" || path === "/saved-addresses") setOrdersInitialTab("addresses");
      else if (path === "/reviews") setOrdersInitialTab("reviews");
      else if (path === "/myorders") setOrdersInitialTab("orders");
      if (path === "/shop/tees") setActiveCategory("tops");
      else if (path === "/shop/bottoms") setActiveCategory("bottoms");
      else if (path === "/shop/accessories") setActiveCategory("accessories");
      else if (path === "/shop") setActiveCategory("all");
      else if (path.startsWith("/track")) targetPage = "track";
      setPage(targetPage);
      // Scroll to top on back/forward navigation
      try { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); } catch (_) {
        try { window.scrollTo(0, 0); } catch (__) {}
      }
    };

    window.addEventListener("popstate", onPop);
    // Set initial history state so back button works from first page.
    // IMPORTANT: preserve ?reset= query param so AuthPage can read the token.
    const preserveSearch = window.location.search.includes("reset=") ? window.location.search : "";
    window.history.replaceState({ page, productId: selectedProduct?.ID || getProductIdFromPath(window.location.pathname) || null }, "", window.location.pathname + preserveSearch);
    return () => window.removeEventListener("popstate", onPop);
  }, [collections]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Mobile Gesture Navigation: Left-to-Right = Back, Right-to-Left = Forward ──
  const handleSwipeBack = useCallback(() => {
    if (page === "product") {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        navigate(prevPage || "home");
      }
    } else if (page !== "home") {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        navigate("home");
      }
    } else if (window.history.length > 1) {
      window.history.back();
    }
  }, [page, prevPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSwipeForward = useCallback(() => {
    window.history.forward();
  }, []);

  useMobileSwipeNav({
    enabled: isMobile,
    onSwipeBack: handleSwipeBack,
    onSwipeForward: handleSwipeForward,
    canGoBack: page !== "home" || (typeof window !== "undefined" && window.history.length > 1),
    canGoForward: true,
  });

  // ── Global Keyboard Shortcut: Ctrl + Shift + A to open Admin Panel ──────────
  useEffect(() => {
    const onKeyShortcut = (e) => {
      // Trigger on Ctrl + Shift + A (or Cmd + Shift + A on macOS)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === "A" || e.key === "a" || e.code === "KeyA")) {
        e.preventDefault();
        navigate("admin");
      }
    };
    window.addEventListener("keydown", onKeyShortcut);
    return () => window.removeEventListener("keydown", onKeyShortcut);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (page !== "product" || selectedProduct || products.length === 0) return;
    const reqId = getProductIdFromPath(window.location.pathname) || window.history.state?.productId;
    if (!reqId) return;
    const match = products.find((p) =>
      Number(p.ID ?? p.id) === Number(reqId) ||
      p.slug === reqId ||
      `bords-${(p.NAME || p.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}` === reqId
    );
    if (match) setSelectedProduct(match);
  }, [page, products, selectedProduct]);

  useEffect(() => {
    if (page !== "collection" || selectedCollection) return;
    const path = window.location.pathname;
    const matchId = path.split("/collections/")[1] || window.history.state?.collectionId;
    if (!matchId) return;
    const cleanId = decodeURIComponent(matchId).replace(/\/+$/, "").toLowerCase();
    const allCols = [...(collections.length > 0 ? collections : []), ...DEFAULT_COLLECTIONS];
    const match = allCols.find(col =>
      String(col.slug || '').toLowerCase() === cleanId ||
      String(col.ID ?? col.id).toLowerCase() === cleanId ||
      (col.NAME || col.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-') === cleanId
    );
    if (match) setSelectedCollection(match);
  }, [page, collections, selectedCollection]);

  const LS_KEY = "bord_guest_cart";
  const getGuestCart  = () => { try { return JSON.parse(localStorage.getItem(LS_KEY)) || []; } catch(_) { return []; } };
  const saveGuestCart = (c) => localStorage.setItem(LS_KEY, JSON.stringify(c));
  const clearGuestCart = () => localStorage.removeItem(LS_KEY);

  // Load cart on mount and on login/logout
  useEffect(() => {
    if (user) {
      // Logged in: load from DB
      return scheduleIdleWork(() => api.getCart(user.id).then(({ data }) => {
        if (data && data.length) {
          setCart(data.map(r => ({ ...r, ID: r.ID ?? r.id })));
        } else {
          // Merge any guest cart items into DB cart
          const guest = getGuestCart();
          if (guest.length) {
            Promise.all(guest.map(i => api.addToCart({ product_id: i.product_id, name: i.name, price: i.price, size: i.size, qty: i.qty }, user.id)))
              .then(() => api.getCart(user.id))
              .then(({ data: merged }) => { setCart((merged||[]).map(r => ({ ...r, ID: r.ID ?? r.id }))); clearGuestCart(); })
              .catch(() => {});
          }
        }
      }).catch(() => {}), isMobile ? 1800 : 700);
    } else {
      // Guest: load from localStorage
      setCart(getGuestCart());
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reveal animation — re-run whenever page, products, or active category changes
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.08 }
    );
    // requestAnimationFrame waits until React has finished painting the newly
    // filtered cards into the DOM before we start observing them.
    // Without this, querySelectorAll runs before the new cards exist.
    let raf;
    const cancelObserve = scheduleIdleWork(() => {
      raf = requestAnimationFrame(() => {
      document.querySelectorAll(".reveal:not(.visible)").forEach(el => io.observe(el));
      });
    }, isMobile ? 900 : 250);
    return () => { cancelObserve(); if (raf) cancelAnimationFrame(raf); io.disconnect(); };
  }, [products, page, collections, activeCategory, isMobile, vibe]);

  // When the user switches category tabs or vibe, ensure cards on screen stay visible
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      document
        .querySelectorAll(".arrivals-scroll .reveal, .shop-page-container .reveal")
        .forEach(el => el.classList.add("visible"));
    });
    return () => cancelAnimationFrame(raf);
  }, [activeCategory, vibe]);

  const handleAddToCart = async (product, size, qtyToAdd = 1) => {
    const prodId = product.ID ?? product.id;
    const finalSize = size || "One Size";
    const maxStock = getProductSizeStock(product, finalSize);

    if (maxStock <= 0) {
      showAppToast(`Size ${finalSize} is currently sold out.`, "warning");
      return { success: false, reason: "sold_out" };
    }

    const currentInCart = cart.reduce((acc, c) => {
      const cProdId = c.product_id ?? c.PRODUCT_ID;
      const cSize = c.size ?? c.SIZE ?? c.item_size;
      if (String(cProdId) === String(prodId) && String(cSize).toUpperCase() === String(finalSize).toUpperCase()) {
        return acc + Number(c.qty ?? c.QTY ?? 1);
      }
      return acc;
    }, 0);

    if (currentInCart >= maxStock) {
      showAppToast(`Only ${maxStock} pieces available in size ${finalSize}. You already have ${currentInCart} in your bag.`, "warning");
      return { success: false, reason: "max_stock_reached", maxStock, currentInCart };
    }

    let actualAddQty = Number(qtyToAdd) || 1;
    let reachedMax = false;
    if (currentInCart + actualAddQty > maxStock) {
      actualAddQty = maxStock - currentInCart;
      reachedMax = true;
      showAppToast(`Only ${maxStock} pieces available in size ${finalSize}. Added ${actualAddQty} to bag.`, "warning");
    } else if (currentInCart + actualAddQty === maxStock) {
      reachedMax = true;
      showAppToast(`Added to bag! Reached maximum available stock (${maxStock} pieces) for size ${finalSize}.`, "info");
    } else {
      showAppToast(`Added ${actualAddQty > 1 ? actualAddQty + ' pieces' : ''} to bag!`, "info");
    }

    if (vibe === "loud") triggerConfetti();
    const item = { product_id: prodId, name: product.NAME ?? product.name, price: product.PRICE ?? product.price, size: finalSize, qty: actualAddQty };
    if (user) {
      // Logged-in: save to DB, then refresh
      try {
        await api.addToCart(item, user.id);
        const { data } = await api.getCart(user.id);
        setCart((data||[]).map(r => ({ ...r, ID: r.ID ?? r.id })));
      } catch(_) {
        setCart(prev => {
          const existing = prev.find(c => String(c.product_id ?? c.PRODUCT_ID) === String(prodId) && String(c.size ?? c.SIZE ?? c.item_size).toUpperCase() === String(finalSize).toUpperCase());
          return existing
            ? prev.map(c => (String(c.product_id ?? c.PRODUCT_ID) === String(prodId) && String(c.size ?? c.SIZE ?? c.item_size).toUpperCase() === String(finalSize).toUpperCase())
                ? { ...c, qty: Math.min(maxStock, (Number(c.qty || c.QTY || 1)) + actualAddQty), QTY: Math.min(maxStock, (Number(c.qty || c.QTY || 1)) + actualAddQty) }
                : c)
            : [...prev, { ...item, ID: Date.now() }];
        });
      }
    } else {
      // Guest: save to localStorage
      const updated = getGuestCart();
      const idx = updated.findIndex(c => String(c.product_id ?? c.PRODUCT_ID) === String(prodId) && String(c.size ?? c.SIZE ?? c.item_size).toUpperCase() === String(finalSize).toUpperCase());
      if (idx >= 0) {
        const curQ = Number(updated[idx].qty || updated[idx].QTY || 1);
        const nextQ = Math.min(maxStock, curQ + actualAddQty);
        updated[idx].qty = nextQ;
        updated[idx].QTY = nextQ;
      } else {
        updated.push({ ...item, ID: Date.now(), qty: actualAddQty, QTY: actualAddQty });
      }
      saveGuestCart(updated);
      setCart(updated);
    }
    // ── GA4: add_to_cart event ───────────────────────────────────────────────
    try {
      const { analytics } = require('../lib/seo');
      analytics.addToCart(
        { id: prodId, name: product.NAME ?? product.name, price: product.PRICE ?? product.price, category: product.CATEGORY ?? product.category },
        finalSize,
        actualAddQty
      );
    } catch (_) {}

    // Prompt 3-chance card spin reward modal upon adding item to bag
    if (!selectedCollectibleCard) {
      setTimeout(() => setSpinModalOpen(true), 600);
    }

    return { success: true, added: actualAddQty, reachedMax };
  };

  const handleRemoveFromCart = async (id) => {
    if (user) {
      setCart(prev => prev.filter(c => c.ID !== id && c.id !== id));
      try { await api.removeFromCart(id, user.id); } catch(_) {}
    } else {
      const updated = getGuestCart().filter(c => c.ID !== id);
      saveGuestCart(updated);
      setCart(updated);
    }
  };

  const handleUpdateQty = async (id, newQty) => {
    if (newQty <= 0) {
      await handleRemoveFromCart(id);
      return;
    }

    const cartItem = cart.find(c => c.ID === id || c.id === id);
    let targetQty = newQty;
    if (cartItem) {
      const prodId = cartItem.product_id ?? cartItem.PRODUCT_ID;
      const itemSize = cartItem.size ?? cartItem.SIZE ?? cartItem.item_size;
      const matchedProd = products.find(p => String(p.ID ?? p.id) === String(prodId));
      if (matchedProd) {
        const maxStock = getProductSizeStock(matchedProd, itemSize);
        if (newQty > maxStock) {
          targetQty = maxStock;
          showAppToast(`Only ${maxStock} pieces available in size ${itemSize}.`, "warning");
        }
      }
    }

    if (user) {
      // Logged-in: update DB
      try {
        await api.updateCartQty(id, targetQty, user.id);
        const { data } = await api.getCart(user.id);
        setCart((data||[]).map(r => ({ ...r, ID: r.ID ?? r.id })));
      } catch(_) {
        setCart(prev => prev.map(c => (c.ID === id || c.id === id) ? { ...c, QTY: targetQty, qty: targetQty } : c));
      }
    } else {
      // Guest: update localStorage
      const updated = getGuestCart();
      const idx = updated.findIndex(c => c.ID === id);
      if (idx >= 0) {
        updated[idx].qty = targetQty;
        updated[idx].QTY = targetQty;
        saveGuestCart(updated);
        setCart(updated);
      }
    }
  };

  const handleSubscribe = async () => {
    setSubError("");
    if (!email.trim()) { setSubError("Please enter your email."); return; }
    // Accept any valid email (user@domain.tld) — not just @gmail.com
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) { setSubError("Please enter a valid email address."); return; }
    setSubLoading(true);
    try {
      const res  = await fetch(`${API}/newsletter/subscribe`, { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ email: email.trim().toLowerCase() }) });
      const data = await res.json();
      // Check 1: message string (case-insensitive, catches both 'Already subscribed' and 'already_subscribed')
      if ((data.message || "").toLowerCase().includes("already")) { setSubError("You're already subscribed!"); return; }
      // Check 2: HTTP status — new subscriptions return 201; duplicates return 200
      if (res.status === 200 && !data.error) { setSubError("You're already subscribed!"); return; }
      if (!res.ok) { setSubError(data.error || "Something went wrong."); return; }
      setSubscribed(true);
    } catch(_) { setSubError("Could not connect. Please try again."); }
    finally { setSubLoading(false); }
  };


  const cartTotal = cart.reduce((sum, c) => sum + (c.PRICE || c.price) * (c.QTY || c.qty || 1), 0);
  const activeProducts = useMemo(
    () => products.filter(p => p.IS_ACTIVE !== 0 && p.IS_ACTIVE !== false),
    [products]
  );
  const loudProductsList = useMemo(() => {
    if (dropSettings.loud_drop_end_date && new Date(dropSettings.loud_drop_end_date).getTime() <= Date.now()) {
      return [];
    }
    if (!dropSettings.loud_drop_products || !dropSettings.loud_drop_products.trim()) return [];
    try {
      const parsed = JSON.parse(dropSettings.loud_drop_products);
      if (Array.isArray(parsed)) {
        return parsed.filter(p => {
          if (!p || !p.name || !p.name.trim()) return false;
          if (p.saleEnd && new Date(p.saleEnd).getTime() <= Date.now()) return false;
          return true;
        });
      }
    } catch (_) {
      return dropSettings.loud_drop_products.split(',').map(n => n.trim()).filter(Boolean).map(n => ({ name: n, pct: '' }));
    }
    return [];
  }, [dropSettings.loud_drop_products, dropSettings.loud_drop_end_date]);

  // Vibe Mode product isolation:
  // Products added to Loud Mode appear ONLY in Loud Mode; non-loud products appear ONLY in Quiet Mode
  const vibeProducts = useMemo(() => {
    const isProductInLoud = (p) => {
      if (!p || !loudProductsList || loudProductsList.length === 0) return false;
      const pName = (p.NAME || p.name || "").toLowerCase().trim();
      if (!pName) return false;
      return loudProductsList.some(item => {
        if (!item || !item.name) return false;
        const iName = item.name.toLowerCase().trim();
        return iName === pName || pName.includes(iName) || iName.includes(pName);
      });
    };

    return activeProducts.filter(p => {
      const inLoud = isProductInLoud(p);
      return vibe === "loud" ? inLoud : !inLoud;
    });
  }, [activeProducts, vibe, loudProductsList]);

  const displayedProducts = useMemo(
    () => vibeProducts.filter(p => {
      if (activeCategory === "all") return true;
      if (activeCategory === "accessories") return (p.CATEGORY || "").toLowerCase().includes("accessor");
      return (p.CATEGORY || "").toLowerCase().includes(activeCategory.slice(0, -1));
    }),
    [vibeProducts, activeCategory]
  );
  const categoryCounts = useMemo(() => ({
    all: vibeProducts.length,
    tops: vibeProducts.filter(p => (p.CATEGORY || "").toLowerCase().includes("top")).length,
    bottoms: vibeProducts.filter(p => (p.CATEGORY || "").toLowerCase().includes("bottom")).length,
    accessories: vibeProducts.filter(p => (p.CATEGORY || "").toLowerCase().includes("accessor")).length,
  }), [vibeProducts]);
  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return [];
    const isHeavyweightQuery = query.includes("240") || query.includes("gsm") || query.includes("heavyweight") || query.includes("oversized") || query.includes("streetwear");
    return vibeProducts
      .filter(p => {
        const name = (p.NAME || "").toLowerCase();
        const cat = (p.CATEGORY || "").toLowerCase();
        const desc = (p.DESCRIPTION || "").toLowerCase();
        const tag = (p.FABRIC_TAG || "").toLowerCase();
        if (name.includes(query) || cat.includes(query) || desc.includes(query) || tag.includes(query)) return true;
        if (isHeavyweightQuery && (cat.includes("top") || name.includes("tee") || name.includes("classic") || name.includes("oversized"))) return true;
        return false;
      })
      .slice(0, 8);
  }, [vibeProducts, searchQuery]);

  // ── DOWNTIME SCREEN (blocks public visitors; admin bypass via ?admin=1) ───
  if (downtimeActive && !isAdminBypass && page !== "admin") {
    return <DowntimeScreen until={downtimeUntil} message={downtimeMsg} />;
  }

  // ── CUSTOMER TRACKING PAGE (/track/:orderNumber) ───────────────────────────
  if (page === "track") return (
    <Suspense fallback={<PageFallback theme={theme} label="Loading Tracking" />}>
      <OrderTrackingPage onGoHome={() => navigate("home")} />
    </Suspense>
  );

  // ── CHECKOUT PAGE ──────────────────────────────────────────────────────────
  if (page === "checkout") return (
    <Suspense fallback={<PageFallback theme={theme} label="Loading Checkout" />}>
    <CheckoutPage
      cart={cart}
      storeSettings={storeSettings}
      onBack={() => { setTrackTab(false); navigate("home"); }}
      onGoHome={() => {
        setTrackTab(false);
        navigate("home");
        setTimeout(() => document.getElementById("arrivals")?.scrollIntoView({ behavior: "smooth" }), 120);
      }}
      onOrderComplete={() => { setCart([]); if (user) api.clearCart(user.id).catch(()=>{}); else localStorage.removeItem("bord_guest_cart"); }}
      user={user}
      activeLocation={activeLocation}
      onLoginRequired={() => navigate("auth")}
      defaultTab={trackTab ? "track" : "checkout"}
      onEnsureCheckoutTab={() => setTrackTab(false)}
      locationEnabled={storeSettings.location_enabled ?? footerSettings.location_enabled}
      selectedCard={selectedCollectibleCard}
      onSelectCard={(c) => setSelectedCollectibleCard(c)}
    />
    </Suspense>
  );

  // ── PRODUCT DETAIL PAGE ───────────────────────────────────────────────────
  if (page === "product" && !selectedProduct) return (
    <PageFallback theme={theme} label="Loading Product" />
  );

  if (page === "product" && selectedProduct) return (
    <>
      <style>{CSS}</style>
      <GlobalToast toast={appToast} />
      <ProductDetailPage
        product={selectedProduct}
        onBack={() => {
          setSelectedProduct(null);
          if (prevPage === "shop") {
            navigate("shop");
          } else if (prevPage === "collection" && selectedCollection) {
            navigate("collection", null, selectedCollection);
          } else if (prevPage === "favorites") {
            navigate("favorites");
          } else {
            navigate("home");
          }
        }}
        onAddToCart={(product, size, qty = 1) => handleAddToCart(product, size, qty)}
        cartCount={cart.reduce((s,c) => s + (c.QTY||c.qty||1), 0)}
        cart={cart}
        showAppToast={showAppToast}
        onCartOpen={() => { setSelectedProduct(null); navigate("home"); setTimeout(() => setCartOpen(true), 100); }}
        wishlist={wishlist}
        onToggleWishlist={(prod) => { const isIn = wishlist.some(w=>w.ID===prod.ID); const next = isIn ? wishlist.filter(w=>w.ID!==prod.ID) : [...wishlist, prod]; setWishlist(next); localStorage.setItem("bord_wishlist", JSON.stringify(next)); }}
        onCheckout={() => navigate("checkout")}
        theme={theme}
        prevPage={prevPage}
        selectedCollection={selectedCollection}
        onGoHome={() => { setSelectedProduct(null); navigate("home"); }}
        onNavigate={navigate}
        showCertifications={storeSettings.show_fabric_certifications === "1"}
        vibe={vibe}
        loudProducts={loudProductsList}
        storeSettings={storeSettings}
      />
    </>
  );

  // ── MY ORDERS PAGE ───────────────────────────────────────────────────────────
  if (page === "myorders") return (
    <>
      <style>{CSS}</style>
      {!isMobile && <Cursor />}
      <MyOrdersPage
        user={user}
        onBack={() => navigate(prevPage || "home")}
        onTrack={(id) => navigate("checkout")}
        API={API}
        STATUS_STEPS={STATUS_STEPS}
        prevPage={prevPage}
        theme={theme}
        initialTab={ordersInitialTab}
        activeLocation={activeLocation}
        onSelectLocation={handleSelectLocation}
        onNavigate={navigate}
      />
    </>
  );

  // ── ADMIN PAGE ─────────────────────────────────────────────────────────────
  if (page === "admin") return (
    <Suspense fallback={<PageFallback theme={theme} label="Loading Admin" />}>
      <AdminPanel onExit={handleAdminExit} />
    </Suspense>
  );

  // ── AUTH PAGE ──────────────────────────────────────────────────────────────
  if (page === "auth") return (
    <Suspense fallback={<PageFallback theme={theme} label="Loading" />}>
    <AuthPage onBack={() => navigate("home")} onLogin={handleLogin} />
    </Suspense>
  );

  // ── FAVORITES PAGE ─────────────────────────────────────────────────────────
  if (page === "favorites") return (
    <>
      <style>{CSS}</style>
      <GlobalToast toast={appToast} />
      {!isMobile && <Cursor />}
      <FavoritesPage
        wishlist={wishlist}
        onBack={() => navigate("home")}
        onAddToCart={handleAddToCart}
        onViewProduct={(prod) => navigate("product", prod)}
        onToggleWishlist={(prod) => {
          const isIn = wishlist.some(w => w.ID === prod.ID);
          const next = isIn ? wishlist.filter(w => w.ID !== prod.ID) : [...wishlist, prod];
          setWishlist(next);
          localStorage.setItem("bord_wishlist", JSON.stringify(next));
        }}
        theme={theme}
        onCheckout={() => navigate("checkout")}
        vibe={vibe}
        showCertifications={storeSettings.show_fabric_certifications === "1"}
        loudProducts={loudProductsList}
        cart={cart}
        showAppToast={showAppToast}
      />
      <MobileMenuDrawer
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        theme={theme}
        setTheme={setTheme}
        vibe={vibe}
        toggleVibe={toggleVibe}
        navigate={navigate}
        user={user}
        handleLogout={handleLogout}
        wishlist={wishlist}
        setSelectedProduct={setSelectedProduct}
        setWishlistOpen={setWishlistOpen}
        storeSettings={storeSettings}
      />
    </>
  );

  // ── SUSTAINABLE RELAUNCH PAGES ────────────────────────────────────────────
  if (page === "our-fabrics") return (
    <Suspense fallback={<PageFallback theme={theme} />}>
      <OurFabricsPage onBack={() => navigate(prevPage || "home")} onNavigate={navigate} />
    </Suspense>
  );

  if (page === "sustainability") return (
    <Suspense fallback={<PageFallback theme={theme} />}>
      <SustainabilityPage onBack={() => navigate(prevPage || "home")} onNavigate={navigate} />
    </Suspense>
  );

  if (page === "certifications") return (
    <Suspense fallback={<PageFallback theme={theme} />}>
      <CertificationsPage onBack={() => navigate(prevPage || "home")} onNavigate={navigate} showCertifications={storeSettings.show_fabric_certifications === "1"} />
    </Suspense>
  );

  if (page === "care") return (
    <Suspense fallback={<PageFallback theme={theme} />}>
      <CarePage onBack={() => navigate(prevPage || "home")} onNavigate={navigate} />
    </Suspense>
  );

  if (page === "journal") return (
    <Suspense fallback={<PageFallback theme={theme} />}>
      <JournalPage onBack={() => navigate(prevPage || "home")} onNavigate={navigate} products={products} />
    </Suspense>
  );

  if (page === "about") return (
    <Suspense fallback={<PageFallback theme={theme} />}>
      <AboutPage onBack={() => navigate(prevPage || "home")} onNavigate={navigate} />
    </Suspense>
  );

  if (page === "contact") return (
    <Suspense fallback={<PageFallback theme={theme} />}>
      <ContactPage onBack={() => navigate(prevPage || "home")} onNavigate={navigate} />
    </Suspense>
  );

  // ── BORDS COLLECTIBLE CARDS VAULT ─────────────────────────────────────────
  if (page === "cards") return (
    <Suspense fallback={<PageFallback theme={theme} label="Loading Cards Vault" />}>
      <CardsVaultPage
        theme={theme}
        onOpenComic={(card) => navigate("comics", null, card)}
        onShopProduct={(prodId) => {
          const p = products.find(x => x.id === prodId || x.ID === prodId);
          if (p) navigate("product", p);
          else navigate("shop");
        }}
        onBackToStore={() => {
          if (prevPage && prevPage !== "cards" && prevPage !== "comics") {
            navigate(prevPage);
          } else {
            navigate("home");
          }
        }}
      />
    </Suspense>
  );

  // ── SOLO LEVELING STYLE COMIC WEBTOON ─────────────────────────────────────
  if (page === "comics") return (
    <Suspense fallback={<PageFallback theme={theme} label="Loading Webtoon" />}>
      <SoloLevelingComicReader
        theme={theme}
        selectedCard={selectedComicCard}
        onSelectCard={(c) => setSelectedComicCard(c)}
        onBackToVault={() => navigate("cards")}
        onBackToStore={() => {
          if (prevPage && prevPage !== "cards" && prevPage !== "comics") {
            navigate(prevPage);
          } else {
            navigate("home");
          }
        }}
        onShopProduct={(prodId) => {
          const p = products.find(x => x.id === prodId || x.ID === prodId);
          if (p) navigate("product", p);
          else navigate("shop");
        }}
      />
    </Suspense>
  );

  // ── SHOP ALL PAGE (Section 3 & 4B) ──────────────────────────────────────────
  if (page === "shop") return (
    <>
      <style>{CSS}</style>
      <GlobalToast toast={appToast} />
      {!isMobile && <Cursor />}
      <ShopPage
        products={vibeProducts}
        onAddToCart={handleAddToCart}
        onViewProduct={(prod) => navigate("product", prod)}
        wishlist={wishlist}
        onToggleWishlist={(prod) => {
          const isIn = wishlist.some(w => w.ID === prod.ID);
          const next = isIn ? wishlist.filter(w => w.ID !== prod.ID) : [...wishlist, prod];
          setWishlist(next);
          localStorage.setItem("bord_wishlist", JSON.stringify(next));
        }}
        onBack={() => navigate(prevPage || "home")}
        onNavigate={navigate}
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        fabricFilter={fabricFilter}
        setFabricFilter={setFabricFilter}
        onOpenWaitlist={(prod) => { setWaitlistProduct(prod); setWaitlistModalOpen(true); }}
        theme={theme}
        isMobile={isMobile}
        onCheckout={() => navigate("checkout")}
        vibe={vibe}
        showCertifications={storeSettings.show_fabric_certifications === "1"}
        loudProducts={loudProductsList}
        onToggleVibe={toggleVibe}
        onGoHome={() => navigate("home")}
        onOpenMenu={() => setMenuOpen(true)}
        cartCount={cart.reduce((s, c) => s + (c.QTY || c.qty || 1), 0)}
        cart={cart}
        showAppToast={showAppToast}
        onOpenCart={() => { navigate("home"); setTimeout(() => setCartOpen(true), 100); }}
      />
      <MobileMenuDrawer
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        theme={theme}
        setTheme={setTheme}
        vibe={vibe}
        toggleVibe={toggleVibe}
        navigate={navigate}
        user={user}
        handleLogout={handleLogout}
        wishlist={wishlist}
        setSelectedProduct={setSelectedProduct}
        setWishlistOpen={setWishlistOpen}
        storeSettings={storeSettings}
      />
    </>
  );

  // ── COLLECTION PAGE ────────────────────────────────────────────────────────
  if (page === "collection") {
    const colToRender = selectedCollection || (() => {
      const path = window.location.pathname;
      const matchId = path.split("/collections/")[1] || window.history.state?.collectionId;
      const cleanId = matchId ? decodeURIComponent(matchId).replace(/\/+$/, "").toLowerCase() : null;
      const allCols = [...(collections.length > 0 ? collections : []), ...DEFAULT_COLLECTIONS];
      if (cleanId) {
        const found = allCols.find(col =>
          String(col.slug || '').toLowerCase() === cleanId ||
          String(col.ID ?? col.id).toLowerCase() === cleanId ||
          (col.NAME || col.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-') === cleanId
        );
        if (found) return found;
      }
      return allCols[0];
    })();

    return (
      <>
        <style>{CSS}</style>
        <GlobalToast toast={appToast} />
        {!isMobile && <Cursor />}
        <CollectionPage
          collection={colToRender}
          products={activeProducts.length > 0 ? activeProducts : products}
          onBack={() => navigate("home")}
          onAddToCart={handleAddToCart}
          onViewProduct={(prod) => navigate("product", prod)}
          wishlist={wishlist}
          onToggleWishlist={(prod) => {
            const isIn = wishlist.some(w => w.ID === prod.ID);
            const next = isIn ? wishlist.filter(w => w.ID !== prod.ID) : [...wishlist, prod];
            setWishlist(next);
            localStorage.setItem("bord_wishlist", JSON.stringify(next));
          }}
          theme={theme}
          onCheckout={() => navigate("checkout")}
          vibe={vibe}
          showCertifications={storeSettings.show_fabric_certifications === "1"}
          loudProducts={loudProductsList}
          cart={cart}
          showAppToast={showAppToast}
        />
        <MobileMenuDrawer
          isOpen={menuOpen}
          onClose={() => setMenuOpen(false)}
          theme={theme}
          setTheme={setTheme}
          vibe={vibe}
          toggleVibe={toggleVibe}
          navigate={navigate}
          user={user}
          handleLogout={handleLogout}
          wishlist={wishlist}
          setSelectedProduct={setSelectedProduct}
          setWishlistOpen={setWishlistOpen}
          storeSettings={storeSettings}
        />
      </>
    );
  }

  // ── HOME PAGE ──────────────────────────────────────────────────────────────
  return (
    <>
      <style>{CSS}</style>
      <GlobalToast toast={appToast} />
      {!isMobile && <Cursor />}

      {/* ── FULL-SCREEN HIGH-IMPACT VIBE TRANSITION SHUTTER ── */}
      {vibeTransition && (
        <div className={`vibe-switch-shutter vibe-shutter-${vibeTransition}`}>
          <div className="vibe-shutter-content">
            <div className="vibe-shutter-badge">
              {vibeTransition === "loud" ? "⚡ CHARGED STREETWEAR" : "✦ ROOTED LUXURY"}
            </div>
            <div className="vibe-shutter-title">
              {vibeTransition === "loud" ? "LOUD MODE ACTIVATED" : "QUIET MODE ACTIVATED"}
            </div>
            <div className="vibe-shutter-desc">
              {vibeTransition === "loud"
                ? "HYPE CAPSULE DROPS // NEO-BRUTALIST CUTS // CHARGED VIBE"
                : "MINIMALIST SILHOUETTES // RAW TEXTURES // TIMELESS FORM"}
            </div>
          </div>
        </div>
      )}

      {/* ── Branded Splash Screen — only rendered if explicitly requested ── */}
      {!splashDone && (
        <div className="bords-splash">
          <div className="bords-splash__inner">
            <Logo size="lg" showTagline={true} forceDark={theme === "dark"} />
            <div className="bords-splash__bar">
              <div className="bords-splash__bar-fill"/>
            </div>
            <div className="bords-splash__tagline">BORED OF ROUTINE DESIGNS · TRY NEW</div>
          </div>
        </div>
      )}
      {/* ── GLOBAL SIZE GUIDE MODAL ── */}
      {showSizeGuideGlobal && (() => {
        const sgIsDark = theme !== 'light';
        const sgBg      = sgIsDark ? '#111111' : '#FFFFFF';
        const sgBorder  = sgIsDark ? 'rgba(244,242,238,0.1)' : '#E8E5DE';
        const sgHdrBdr  = sgIsDark ? 'rgba(244,242,238,0.07)' : '#E8E5DE';
        const sgText    = sgIsDark ? '#f4f2ee' : '#1A1A1A';
        const sgMuted   = sgIsDark ? 'rgba(244,242,238,0.4)' : '#6B6965';
        const sgRowBg   = sgIsDark ? 'rgba(244,242,238,0.03)' : 'rgba(0,0,0,0.02)';
        const sgRowBdr  = sgIsDark ? 'rgba(244,242,238,0.06)' : 'rgba(0,0,0,0.07)';
        const sgRowMuted= sgIsDark ? 'rgba(244,242,238,0.45)' : '#6B6965';
        const sgCloseBg = sgIsDark ? 'rgba(244,242,238,0.06)' : 'rgba(0,0,0,0.04)';
        const sgCloseBdr= sgIsDark ? 'rgba(244,242,238,0.1)' : 'rgba(0,0,0,0.1)';
        const sgCloseClr= sgIsDark ? 'rgba(244,242,238,0.5)' : 'rgba(0,0,0,0.4)';
        const sgInfoBg  = sgIsDark ? 'rgba(200,71,26,0.05)' : 'rgba(200,71,26,0.06)';
        const sgInfoTxt = sgIsDark ? 'rgba(244,242,238,0.45)' : '#6B6965';
        return (
          <div onClick={()=>setShowSizeGuideGlobal(false)}
            style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:9999,
              display:"flex",alignItems:"center",justifyContent:"center",padding:"20px",
              backdropFilter:"blur(8px)"}}>
            <div onClick={e=>e.stopPropagation()}
              style={{background:sgBg,border:`1px solid ${sgBorder}`,borderRadius:12,
                maxWidth:760,width:"100%",maxHeight:"90vh",overflowY:"auto",boxShadow:"0 12px 40px rgba(0,0,0,0.25)"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                padding:"20px 28px",borderBottom:`1px solid ${sgHdrBdr}`}}>
                <div>
                  <div style={{fontSize:".58rem",letterSpacing:".3em",textTransform:"uppercase",color:"#c8471a",marginBottom:4}}>BORDS</div>
                  <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.6rem",letterSpacing:".1em",color:sgText}}>Size Guide</div>
                </div>
                <button onClick={()=>setShowSizeGuideGlobal(false)}
                  style={{background:sgCloseBg,border:`1px solid ${sgCloseBdr}`,
                    color:sgCloseClr,borderRadius:6,width:36,height:36,cursor:"pointer",
                    display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1.1rem",flexShrink:0}}>✕</button>
              </div>
              <div style={{padding:"28px"}}>
                <div style={{fontSize:".72rem",color:sgMuted,marginBottom:24}}>
                  * Measurements refer to body, not garment.
                </div>
                <div style={{display:"grid",gridTemplateColumns:isMobile ? "1fr" : "1fr 1fr",gap:24}}>
                  <div>
                    <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1rem",letterSpacing:".15em",color:"#c8471a",marginBottom:16}}>Pants Conversion</div>
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {[["71-75 cm","Pant Size 30"],["76-80 cm","Pant Size 32"],["81-85 cm","Pant Size 34"],
                        ["86-90 cm","Pant Size 36"],["91-95 cm","Pant Size 38"]].map(([waist,size],i)=>(
                        <div key={i} style={{display:"flex",alignItems:"center",background:sgRowBg,
                          border:`1px solid ${sgRowBdr}`,borderRadius:6,padding:"10px 14px"}}>
                          <span style={{fontSize:".7rem",color:sgRowMuted,flex:1}}>Body Waist {waist}</span>
                          <span style={{color:"rgba(200,71,26,0.6)",margin:"0 10px"}}>{String.fromCharCode(8594)}</span>
                          <span style={{fontSize:".72rem",fontWeight:600,color:sgText,letterSpacing:".06em",textTransform:"uppercase"}}>{size}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1rem",letterSpacing:".15em",color:"#c8471a",marginBottom:16}}>Shirt Conversion</div>
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {[["88-92 cm","Shirt Size S"],["93-97 cm","Shirt Size M"],["98-103 cm","Shirt Size L"],
                        ["104-108 cm","Shirt Size 40"],["109-114 cm","Shirt Size XL"]].map(([chest,size],i)=>(
                        <div key={i} style={{display:"flex",alignItems:"center",background:sgRowBg,
                          border:`1px solid ${sgRowBdr}`,borderRadius:6,padding:"10px 14px"}}>
                          <span style={{fontSize:".7rem",color:sgRowMuted,flex:1}}>Body Chest {chest}</span>
                          <span style={{color:"rgba(200,71,26,0.6)",margin:"0 10px"}}>{String.fromCharCode(8594)}</span>
                          <span style={{fontSize:".72rem",fontWeight:600,color:sgText,letterSpacing:".06em",textTransform:"uppercase"}}>{size}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{marginTop:20,padding:"12px 16px",background:sgInfoBg,
                  border:"1px solid rgba(200,71,26,0.15)",borderRadius:6,fontSize:".7rem",
                  color:sgInfoTxt,display:"flex",alignItems:"center",gap:8}}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#c8471a" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
                  When between sizes, we recommend sizing up for a more comfortable fit.
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* db-badge removed — not customer-facing */}

      {/* ── ANNOUNCEMENT BAR ── */}
      <div className="ann-bar">
        <div className="ann-bar-inner">
          {[...annBarItems, ...annBarItems].map((item, i) => (
            <span key={i} className="ann-bar-item">{item}</span>
          ))}
        </div>
      </div>

      <nav className="nav-glass">
        {isMobile ? (
          <button
            onClick={() => setMenuOpen(prev => !prev)}
            className="nav-icon-btn"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            title={menuOpen ? "Close menu" : "Open menu"}
            style={{ width: 44, height: 44, minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {menuOpen ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            )}
          </button>
        ) : (
          <button onClick={() => navigate("home")} style={{background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",padding:0}} aria-label="BORDS — Go to home">
            <Logo size="sm" showTagline={false} />
          </button>
        )}
        {/* Centered logo — MOBILE ONLY (desktop logo sits on the left) */}
        {isMobile && (
          <button
            onClick={() => navigate("home")}
            style={{position:"absolute",left:"50%",transform:"translateX(-50%)",background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",padding:0,zIndex:1,maxWidth:"calc(100vw - 220px)",overflow:"hidden"}}
            aria-label="BORDS — Go to home"
          >
            <Logo size="sm" showTagline={false} />
          </button>
        )}
        {!isMobile && (
          <ul className="nav-links">
            <li><a href="/shop" onClick={(e) => { e.preventDefault(); navigate("shop"); }}>Shop</a></li>
            <li><a href="/cards" onClick={(e) => { e.preventDefault(); navigate("cards"); }} style={{ whiteSpace: "nowrap", fontWeight: 700, color: "var(--bord-accent, #c8471a)" }}>Cards</a></li>
            <li><a href="/comics" onClick={(e) => { e.preventDefault(); navigate("comics"); }} style={{ whiteSpace: "nowrap", fontWeight: 700 }}>Comics</a></li>
            <li><a href="/our-fabrics" onClick={(e) => { e.preventDefault(); navigate("our-fabrics"); }}>Fabrics</a></li>
            <li><a href="/sustainability" onClick={(e) => { e.preventDefault(); navigate("sustainability"); }}>Sustainability</a></li>
            <li><a href="/journal" onClick={(e) => { e.preventDefault(); navigate("journal"); }}>Journal</a></li>
            <li><a href="#arrivals">Arrivals</a></li>
          </ul>
        )}
        <div className="nav-right">
          {/* Vibe Mode Toggle — Desktop only (on mobile, accessible in hamburger menu to prevent header congestion) */}
          {!isMobile && (
            <button
              onClick={toggleVibe}
              className="vibe-toggle-btn"
              title={`Vibe: ${vibe === 'loud' ? 'Loud Mode (Gen Z)' : 'Quiet Mode (Minimal)'} — Click to switch`}
              aria-label={`Toggle Vibe Mode: Currently ${vibe}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '5px 12px',
                borderRadius: 999,
                border: vibe === 'loud'
                  ? '1.5px solid #0D0D0D'
                  : (theme === 'dark' ? '1.5px solid rgba(181, 214, 159, 0.5)' : '1.5px solid #4A5D3A'),
                background: vibe === 'loud'
                  ? '#D4FF3F'
                  : (theme === 'dark' ? 'rgba(74, 93, 58, 0.35)' : 'rgba(74, 93, 58, 0.12)'),
                color: vibe === 'loud'
                  ? '#0D0D0D'
                  : (theme === 'dark' ? '#B5D69F' : '#2F4222'),
                fontFamily: "var(--font-stamp, 'Archivo Black', sans-serif)",
                fontSize: '.65rem',
                fontWeight: 800,
                letterSpacing: '.07em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                transition: 'all .18s ease',
                boxShadow: vibe === 'loud' ? '2px 2px 0 #0D0D0D' : 'none',
              }}
            >
              {vibe === 'loud' ? '⚡ LOUD' : '🌿 QUIET'}
            </button>
          )}

          {!isMobile && (
            <div className={`theme-switch ${theme}`} onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} role="button" aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`} title="Toggle Light/Dark Mode">
              <div className="theme-switch-handle">
                {theme === 'dark'
                  ? <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" style={{stroke:'#1a1a1a'}}><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
                  : <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" style={{stroke:'#c8471a'}}><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                }
              </div>
            </div>
          )}
          {/* Functional search button — stays visible outside the dropdown on mobile */}
          <button className="nav-icon-btn" onClick={() => setSearchOpen(true)} aria-label="Search products" title="Search">
            <svg className="nav-icon" viewBox="0 0 24 24" strokeWidth="1.5"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
          </button>
          {!isMobile && (
            <div
              className="nav-heart-container"
              style={{position:"relative", display:"inline-block"}}
              onMouseEnter={() => setWishlistHover(true)}
              onMouseLeave={() => setWishlistHover(false)}
            >
              <button className="nav-icon-btn nav-heart-btn" onClick={() => setWishlistOpen(true)} aria-label="Wishlist" title="Wishlist" style={{position:"relative"}}>
                <svg className="nav-icon" viewBox="0 0 24 24" strokeWidth="1.5"
                  style={{fill: wishlist.length > 0 ? "#c8471a" : "none", stroke: wishlist.length > 0 ? "#c8471a" : (theme === 'dark' ? "#FAF9F6" : "currentColor"), transition:"all .3s"}}>
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
                {wishlist.length > 0 && (
                  <span className="wishlist-count">{wishlist.length}</span>
                )}
              </button>
              {wishlistHover && wishlist.length > 0 && (
                <div className="wishlist-menu-dropdown">
                  <div className="wishlist-menu-title">Favorites</div>
                  <div className="wishlist-menu-items">
                    {wishlist.map(w => (
                      <div key={w.ID} className="wishlist-menu-item" onClick={() => { setSelectedProduct(w); navigate("product", w); setWishlistHover(false); }}>
                        {w.NAME}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {!isMobile && (
            user ? (
              <div
                className="nav-user-container"
                style={{ position: "relative", display: "inline-block" }}
                onMouseEnter={() => setUserMenuOpen(true)}
                onMouseLeave={() => setUserMenuOpen(false)}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); setUserMenuOpen(prev => !prev); }}
                  className="nav-user-btn nav-profile-pill"
                  title={`Account: ${user.name || user.email}`}
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                >
                  <span className="nav-profile-avatar">
                    {(user.name || user.email || "?")[0].toUpperCase()}
                  </span>
                  <span className="nav-profile-name">
                    {(user.name || user.email || "").split(" ")[0]}
                  </span>
                  <svg
                    className={`nav-profile-caret ${userMenuOpen ? "open" : ""}`}
                    width="9"
                    height="9"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {userMenuOpen && (
                  <div className="user-profile-dropdown" role="menu">
                    <div className="user-profile-dropdown-header">
                      <div className="user-profile-dropdown-avatar">
                        {(user.name || user.email || "?")[0].toUpperCase()}
                      </div>
                      <div className="user-profile-dropdown-info">
                        <div className="user-profile-dropdown-name">{user.name || "Member"}</div>
                        <div className="user-profile-dropdown-email">{user.email}</div>
                      </div>
                    </div>
                    <div className="user-profile-dropdown-divider" />
                    <div className="user-profile-dropdown-items">
                      <button
                        className="user-profile-dropdown-item"
                        role="menuitem"
                        onClick={() => { setUserMenuOpen(false); setOrdersInitialTab("orders"); navigate("myorders", null, { tab: "orders" }); }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/>
                        </svg>
                        <span>My Orders</span>
                      </button>

                      <button
                        className="user-profile-dropdown-item"
                        role="menuitem"
                        onClick={() => { setUserMenuOpen(false); setOrdersInitialTab("addresses"); navigate("myorders", null, { tab: "addresses" }); }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                        </svg>
                        <span>Saved Addresses</span>
                      </button>

                      <button
                        className="user-profile-dropdown-item"
                        role="menuitem"
                        onClick={() => { setUserMenuOpen(false); setWishlistOpen(true); }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill={wishlist.length > 0 ? "#c8471a" : "none"} stroke={wishlist.length > 0 ? "#c8471a" : "currentColor"} strokeWidth="2">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                        </svg>
                        <span>My Favorites</span>
                        {wishlist.length > 0 && (
                          <span className="user-profile-badge">{wishlist.length}</span>
                        )}
                      </button>

                      <div className="user-profile-dropdown-divider" />

                      <button
                        className="user-profile-dropdown-item user-profile-signout"
                        role="menuitem"
                        onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                        </svg>
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button onClick={() => navigate("auth")} className="nav-user-btn nav-signin-pill" title="Sign In">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                <span className="nav-btn-text" style={{ fontSize: ".62rem", fontWeight: 600, letterSpacing: ".08em" }}>Sign In</span>
              </button>
            )
          )}
          <div className="cart-bubble" onClick={() => setCartOpen(true)} role="button" aria-label="Open shopping bag">
            <svg className="nav-icon" viewBox="0 0 24 24" strokeWidth="1.5"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
            <div className="cart-count">{cart.reduce((s, c) => s + (c.QTY || c.qty || 1), 0)}</div>
          </div>
        </div>
      </nav>
      {/* Deliver-to location strip */}
      {String(storeSettings.location_enabled ?? footerSettings.location_enabled) !== "0" && String(storeSettings.location_enabled ?? footerSettings.location_enabled) !== "false" && (
        <>
          <div
            onClick={() => setLocationExpanded(!locationExpanded)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 20px', cursor: 'pointer',
              background: 'var(--bord-surface, #f8f8f5)',
              borderBottom: '1px solid var(--bord-border, rgba(0,0,0,0.06))',
              fontFamily: "'DM Sans', sans-serif",
              transition: 'background 0.2s',
              position: 'relative', zIndex: 90,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FC8019" strokeWidth="2.5" strokeLinecap="round">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
              <circle cx="12" cy="9" r="3"/>
            </svg>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: 'var(--bord-muted, #93959F)', fontWeight: 500 }}>Deliver to</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--bord-fg, #282C3F)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {activeLocation?.city || activeLocation?.address || 'Select your location'}
              </div>
            </div>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--bord-muted, #93959F)" strokeWidth="2" style={{ transform: locationExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>

          {locationExpanded && (
            <LocationSearchModal
              isOpen={locationExpanded}
              onClose={() => setLocationExpanded(false)}
              onSelectAddress={handleSelectLocation}
              currentAddress={activeLocation}
            />
          )}
        </>
      )}


      {/* ── SEARCH OVERLAY ── */}
      {searchOpen && (
        <div className="search-overlay" onClick={e => { if(e.target===e.currentTarget) setSearchOpen(false); }}>
          <div className="search-overlay-panel">
            <div className="search-overlay-header">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{opacity:.5}}><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
              <input
                autoFocus
                className="search-overlay-input"
                placeholder="Search products, collections…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => { if(e.key==="Escape") setSearchOpen(false); }}
              />
              <button onClick={() => setSearchOpen(false)} className="search-overlay-close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="search-overlay-results">
              {searchQuery.trim().length > 0 ? (
                searchResults.length > 0 ? (
                  searchResults.map(p => (
                    <div key={p.ID} className="search-result-item" onClick={() => { setSelectedProduct(p); navigate("product", p); setSearchOpen(false); setSearchQuery(""); }}>
                      <div className="search-result-img">
                        {p.IMAGE_URL ? <DriveImg src={p.IMAGE_URL} alt={p.NAME} style={{width:"100%",height:"100%",objectFit:"cover"}} /> : <div style={{width:"100%",height:"100%",background:"rgba(200,71,26,0.08)"}} />}
                      </div>
                      <div className="search-result-info">
                        <div className="search-result-name">{p.NAME}</div>
                        <div className="search-result-meta">{p.CATEGORY} · ₹{Number(p.PRICE).toLocaleString()}</div>
                      </div>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{opacity:.3}}><polyline points="9 18 15 12 9 6"/></svg>
                    </div>
                  ))
                ) : (
                  <div style={{textAlign:"center",padding:"48px 0",color:"rgba(var(--bord-fg-rgb),.3)",fontSize:".82rem"}}>No products found for &ldquo;{searchQuery}&rdquo;</div>
                )
              ) : (
                <div style={{padding:"20px 0"}}>
                  <div style={{fontSize:".58rem",letterSpacing:".2em",textTransform:"uppercase",color:"rgba(var(--bord-fg-rgb),.3)",marginBottom:14}}>Popular Searches</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:20}}>
                    {["240 GSM", "Oversized Tees", "Heavyweight", "New Arrivals", "Best Sellers", "Beige Series"].map(tag => (
                      <button key={tag} onClick={() => setSearchQuery(tag)} className="search-tag-btn">{tag}</button>
                    ))}
                  </div>
                  <div style={{borderTop:"1px solid var(--glass-border)",paddingTop:14,display:"flex",flexWrap:"wrap",gap:16}}>
                    <button
                      onClick={() => { setSearchOpen(false); navigate("collection", null, { slug: "oversized-t-shirts" }); }}
                      style={{background:"none",border:"none",color:"var(--accent-warm)",fontSize:".75rem",fontWeight:700,cursor:"pointer",padding:0}}
                    >
                      Browse Oversized Collection →
                    </button>
                    <button
                      onClick={() => { setSearchOpen(false); navigate("collection", null, { slug: "heavyweight-t-shirts" }); }}
                      style={{background:"none",border:"none",color:"var(--accent-warm)",fontSize:".75rem",fontWeight:700,cursor:"pointer",padding:0}}
                    >
                      Browse 240 GSM Collection →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── WISHLIST DRAWER ── */}
      <div className={`cart-overlay${wishlistOpen ? " open" : ""}`} onClick={() => setWishlistOpen(false)}></div>
      <div className={`cart-drawer${wishlistOpen ? " open" : ""}`} onClick={e => e.stopPropagation()} style={{display:"flex",flexDirection:"column"}}>
        <div className="cart-header">
          <h3 style={{display:"flex",alignItems:"center",gap:10}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#c8471a" stroke="#c8471a" strokeWidth="1.5"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
            Wishlist
          </h3>
          <button onClick={() => setWishlistOpen(false)} style={{background:"none",border:"none",color:"var(--white)",cursor:"pointer",opacity:.5,fontSize:"1.3rem"}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:"0 28px"}}>
          {wishlist.length === 0 ? (
            <div style={{textAlign:"center",padding:"60px 0"}}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(var(--bord-fg-rgb),.15)" strokeWidth="1" style={{marginBottom:16}}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
              <div style={{fontSize:".85rem",color:"rgba(var(--bord-fg-rgb),.35)"}}>Your wishlist is empty</div>
              <div style={{fontSize:".72rem",color:"rgba(var(--bord-fg-rgb),.22)",marginTop:6}}>Tap the 🎁 on any product to save it</div>
            </div>
          ) : wishlist.map(p => (
            <div key={p.ID} style={{display:"flex",alignItems:"center",gap:14,padding:"16px 0",borderBottom:"1px solid var(--border)"}}>
              <div style={{width:64,height:76,borderRadius:4,overflow:"hidden",flexShrink:0,background:"var(--off)"}}>
                {p.IMAGE_URL ? <DriveImg src={p.IMAGE_URL} alt={p.NAME} style={{width:"100%",height:"100%",objectFit:"cover"}} /> : null}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:".78rem",fontWeight:600,marginBottom:4,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.NAME}</div>
                <div style={{fontSize:".65rem",color:"rgba(var(--bord-fg-rgb),.4)",marginBottom:8}}>{p.CATEGORY} · ₹{Number(p.PRICE).toLocaleString()}</div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={() => { setSelectedProduct(p); navigate("product", p); setWishlistOpen(false); }}
                    style={{background:"#c8471a",border:"none",color:"#fff",padding:"6px 14px",borderRadius:3,fontSize:".6rem",letterSpacing:".12em",textTransform:"uppercase",cursor:"pointer"}}>View</button>
                  <button onClick={() => { const w=wishlist.filter(x=>x.ID!==p.ID); setWishlist(w); localStorage.setItem("bord_wishlist",JSON.stringify(w)); }}
                    style={{background:"none",border:"1px solid rgba(var(--bord-fg-rgb),.12)",color:"rgba(var(--bord-fg-rgb),.4)",padding:"6px 12px",borderRadius:3,fontSize:".6rem",cursor:"pointer"}}>Remove</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MOBILE HAMBURGER MENU */}
      <MobileMenuDrawer
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        theme={theme}
        setTheme={setTheme}
        vibe={vibe}
        toggleVibe={toggleVibe}
        navigate={navigate}
        user={user}
        handleLogout={handleLogout}
        wishlist={wishlist}
        setSelectedProduct={setSelectedProduct}
        setWishlistOpen={setWishlistOpen}
        storeSettings={storeSettings}
      />

      <main id="main-content">

      {/* Limited Drop Countdown Banner (Section 4B - Loud Mode only) */}
      {vibe === "loud" && (
        <DropCountdownBanner
          label={dropSettings.loud_drop_label}
          subtitle={dropSettings.loud_drop_subtitle}
          endDate={dropSettings.loud_drop_end_date}
          products={dropSettings.loud_drop_products}
          series={dropSettings.loud_drop_series}
          styles={dropSettings.loud_drop_styles}
        />
      )}

      <section className="hero" style={{ position: "relative" }}>
        {heroSlides.map((slide, i) => (
          <div key={i} className={`hero-slide ${i === heroIndex ? 'active' : ''}`}>
            <div className={`hero-img-wrap hero-img-wrap-${i}`}></div>
            <div className="hero-runway" aria-hidden="true">
              <span></span><span></span><span></span><span></span>
            </div>
            <div className="hero-editorial" aria-hidden="true">
              <div className="hero-editorial-panel hero-editorial-panel-main">
                <span>B</span>
              </div>
              <div className="hero-editorial-panel hero-editorial-panel-side">
                <span>{slide.tag}</span>
              </div>
            </div>
            <div className="hero-bg"></div>
            <div className="hero-grain"></div>
            {i === heroIndex && (
              <>
                <div className="hero-glow"></div>
                <div className="hero-glow hero-glow--2"></div>
                <div className="hero-shape"></div>
                <div className="hero-tag-vert">{slide.tag}</div>
                <div className="hero-content">
                  <p className="hero-overline">{slide.overline}</p>
                  <ThreadWeaveHero />
                  {i === 0 ? (
                    <h1 className="hero-title">
                      <span className="line">{slide.title1}</span>
                      <span className="line">{slide.title2}</span>
                    </h1>
                  ) : (
                    <div className="hero-title" role="heading" aria-level="2">
                      <span className="line">{slide.title1}</span>
                      <span className="line">{slide.title2}</span>
                    </div>
                  )}
                  <p className="hero-subtitle">{slide.subtitle}</p>
                  <div className="hero-proof">
                    <span>{slide.mood}</span>
                    <span>{slide.material}</span>
                    <span>Bords.co.in</span>
                  </div>
                  <div className="hero-actions">
                    <button onClick={() => navigate("shop")} className="btn-primary" style={{cursor:'pointer',border:'none'}}>Shop Oversized Tees <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg></button>
                    <button onClick={() => document.getElementById("why-bords")?.scrollIntoView({ behavior: "smooth" })} className="btn-ghost" style={{cursor:'pointer'}}>Why BORDS?</button>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
        <div className="hero-scroll"><span>Discover</span><div className="scroll-line"></div></div>
        
        {/* Slider Controls */}
        <div className="hero-slider-controls" style={{position:"absolute", bottom: isMobile ? 12 : 48, right: isMobile ? "auto" : 48, left: isMobile ? 12 : "auto", zIndex:10, display:"flex", gap: isMobile ? 8 : 16, alignItems:"center"}}>
          <div style={{fontFamily:"'Bebas Neue',sans-serif", fontSize: isMobile ? "1.05rem" : "1.3rem", color:"var(--accent)", letterSpacing:".1em"}}>0{heroIndex + 1}</div>
          <div style={{width: isMobile ? 40 : 60, height: 2, background: theme==="light" ? "rgba(44,42,41,0.18)" : "rgba(244,242,238,0.2)", position:"relative"}}>
            <div style={{position:"absolute", top:0, left:0, height:2, background:"var(--accent)", width: "100%", transformOrigin:"left", animation:"sliderProgress 5.5s linear infinite"}} key={heroIndex} />
          </div>
          <div style={{fontFamily:"'Bebas Neue',sans-serif", fontSize: isMobile ? ".85rem" : "1rem", color: theme==="light" ? "rgba(44,42,41,0.4)" : "rgba(244,242,238,0.4)", letterSpacing:".1em"}}>0{heroSlides.length}</div>
        </div>
      </section>

      {/* ── TICKER (Section 4B) ── */}
      <div className={`ticker ${vibe === 'quiet' ? 'ticker--quiet' : 'ticker--loud'}`}>
        <div className="ticker-inner">
          {(vibe === "loud" ? [
            "⚡ VAULT ARCHIVE DROP 01 LIVE",
            "UP TO 50% CAPSULE SAVINGS",
            "LIMITED ALLOCATION // STRICTLY NO RESTOCKS",
            "GEN Z LUXURY STREETWEAR",
            "AUTHENTIC DROP RUNWAY",
            "DIGITAL CERTIFICATE EMBEDDED",
            ...tickerItems
          ] : [
            "ONE FIBRE NEVER A BLEND",
            "WORN 30+ TIMES",
            "GOTS CERTIFIED ORGANIC",
            "HAND-SPUN KHADI",
            "ZERO GREENWASH",
            "CONSCIOUSLY CUT",
            ...tickerItems
          ]).map((t, i) => (
            <span key={i} className="ticker-item">{t}</span>
          ))}
        </div>
      </div>

      <section id="arrivals">
        <div className="section-header reveal" style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div className="section-eyebrow">
              {vibe === "loud" ? "⚡ CAPSULE VAULT // ARCHIVE DROP" : "Just Dropped"}
            </div>
            <h2 className="section-title">
              {vibe === "loud" ? <>LOUD<br/>CAPSULE</> : <>New<br/>Arrivals</>}
            </h2>
          </div>
          {vibe === "loud" && (
            <div className="loud-capsule-badge">
              <span className="loud-pulse-dot" />
              <span>DROP SPEC // AUTO-DISCOUNT APPLIED</span>
            </div>
          )}
        </div>

        {/* CATEGORY TABS */}
        <div style={{display:"flex",gap: isMobile ? 6 : 8,padding: isMobile ? "0 0 14px" : "0 0 28px",flexWrap:"wrap"}}>
          {[
            { key:"all",         label:"All" },
            ...(catSections.tops        ? [{ key:"tops",        label:"Tops" }]        : []),
            ...(catSections.bottoms     ? [{ key:"bottoms",     label:"Bottoms" }]     : []),
            ...(catSections.accessories ? [{ key:"accessories", label:"Accessories" }] : []),
          ].map(tab => (
            <button key={tab.key} onClick={()=>setActiveCategory(tab.key)}
              className={vibe === "loud" ? `loud-tab-btn ${activeCategory === tab.key ? "active" : ""}` : ""}
              style={vibe === "loud" ? undefined : {
                padding: isMobile ? "5px 11px" : "8px 20px",borderRadius:3,fontSize: isMobile ? ".58rem" : ".65rem",letterSpacing:".15em",
                textTransform:"uppercase",cursor:"pointer",transition:"all .3s",fontFamily:"'DM Sans',sans-serif",
                background: activeCategory===tab.key ? "var(--accent)" : (theme==="light" ? "rgba(44,42,41,0.05)" : "rgba(244,242,238,0.05)"),
                border: activeCategory===tab.key ? "2px solid var(--accent)" : (theme==="light" ? "1px solid rgba(44,42,41,0.12)" : "1px solid rgba(244,242,238,0.1)"),
                fontWeight: activeCategory===tab.key ? 600 : 500,
                color: activeCategory===tab.key ? "#ffffff" : (theme==="light" ? "#2C2A29" : "#F4F2EE")
              }}>
              {vibe === "loud" ? (
                <>
                  <span>[ {tab.label.toUpperCase()}</span>
                  <span className="loud-tab-count">{"// "}{String(categoryCounts[tab.key] ?? 0).padStart(2, "0")} ]</span>
                </>
              ) : (
                <>
                  {tab.label}
                  <span style={{marginLeft:6,fontSize:".58rem",opacity:.7}}>
                    ({categoryCounts[tab.key] ?? 0})
                  </span>
                </>
              )}
            </button>
          ))}
        </div>

        <div className="arrivals-scroll">
          {products.length === 0
            /* ── Shimmer placeholder cards while DB is waking up ── */
            /* Mobile shows 4 skeletons (fills 2-col viewport), desktop shows 8 */
            ? Array.from({length: isMobile ? 4 : 8}).map((_,i) => (
                <div key={i} style={{borderRadius:8,overflow:"hidden"}}>
                  <div className="sk" style={{width:"100%",aspectRatio:"3/4",borderRadius:8,marginBottom:10}}/>
                  <div className="sk" style={{height:13,width:"70%",marginBottom:7}}/>
                  <div className="sk" style={{height:12,width:"40%"}}/>
                </div>
              ))
            : displayedProducts.length === 0
                ? (
                  vibe === "loud" ? (
                    <div className="genz-vault-empty" style={{ width: "100%", gridColumn: "1 / -1" }}>
                      <div className="vault-icon-frame">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D4FF3F" strokeWidth="1.8">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                          <circle cx="12" cy="16" r="1.5" fill="#D4FF3F"/>
                        </svg>
                      </div>
                      <div className="vault-tag">[ STATUS // ARCHIVE VAULT SECURED ]</div>
                      <h3 className="vault-title">No Capsule Items Staged</h3>
                      <p className="vault-desc">
                        The loud drop archive is currently secured. Admin has not staged pieces for this drop window.
                      </p>
                      <button className="vault-cta-btn" onClick={toggleVibe}>
                        Explore Quiet Collection 🌿
                      </button>
                    </div>
                  ) : (
                    <div style={{ width: "100%", gridColumn: "1 / -1", textAlign: "center", padding: "60px 20px" }}>
                      <div style={{
                        fontFamily: "var(--font-stamp, 'Archivo Black', sans-serif)",
                        fontSize: "1.1rem",
                        color: "var(--accent)",
                        marginBottom: 8,
                        letterSpacing: ".06em",
                        textTransform: "uppercase"
                      }}>
                        No products available in this category
                      </div>
                      <p style={{ fontSize: ".8rem", opacity: 0.65 }}>
                        Try selecting another category or check back later.
                      </p>
                    </div>
                  )
                )
                : displayedProducts
                    .map((p, i) => (
                      <ProductCard
                        key={p.ID}
                        product={p}
                        delay={i % 3 + 1}
                        rank={i + 1}
                        isMobile={isMobile}
                        onAddToCart={handleAddToCart}
                        onViewProduct={(prod) => { navigate("product", prod); }}
                        wishlist={wishlist}
                        onToggleWishlist={(prod) => { const isIn = wishlist.some(w=>w.ID===prod.ID); const next = isIn ? wishlist.filter(w=>w.ID!==prod.ID) : [...wishlist, prod]; setWishlist(next); localStorage.setItem("bord_wishlist", JSON.stringify(next)); }}
                        theme={theme}
                        onCheckout={() => navigate("checkout")}
                        onOpenWaitlist={(prod) => { setWaitlistProduct(prod); setWaitlistModalOpen(true); }}
                        vibe={vibe}
                        showCertifications={storeSettings.show_fabric_certifications === "1"}
                        loudProducts={loudProductsList}
                        cart={cart}
                        showAppToast={showAppToast}
                      />
                    ))
          }
        </div>
      </section>

      {/* ── WHY BORDS? SECTION (Semantic H2 for SEO & Brand Positioning) ── */}
      <section id="why-bords" style={{ padding: isMobile ? "24px 10px" : "80px 48px", borderTop: "1px solid var(--border)" }}>
        <div className="section-header reveal" style={{ marginBottom: isMobile ? 16 : 36 }}>
          <div>
            <div className="section-eyebrow">The Philosophy</div>
            <h2 className="section-title">
              Why BORDS? <br />
              <span style={{ fontSize: isMobile ? "0.55em" : "0.62em", color: "var(--muted)", textTransform: "none", fontFamily: "var(--ff-ui)", fontWeight: 400, display: "block", marginTop: isMobile ? 4 : 8 }}>
                Streetwear for people bored of routine designs.
              </span>
            </h2>
          </div>
        </div>
        <div className="why-bords-grid" style={{
          display: isMobile ? "flex" : "grid",
          gridTemplateColumns: isMobile ? undefined : "repeat(3, 1fr)",
          overflowX: isMobile ? "auto" : "visible",
          scrollSnapType: isMobile ? "x mandatory" : "none",
          WebkitOverflowScrolling: "touch",
          paddingBottom: isMobile ? 8 : 0,
          gap: isMobile ? "10px" : "28px",
        }}>
          <div className="why-bords-card" style={{
            flex: isMobile ? "0 0 84%" : "1",
            scrollSnapAlign: isMobile ? "start" : "none",
            background: theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
            border: "1px solid var(--border)",
            borderRadius: isMobile ? 8 : 12,
            padding: isMobile ? "14px 14px" : "32px 28px",
            transition: "transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease",
          }}>
            <div style={{ fontFamily: "var(--ff-mono)", fontSize: isMobile ? "0.68rem" : "0.8rem", color: "var(--accent-warm)", marginBottom: isMobile ? 6 : 12, letterSpacing: ".08em" }}>01 / SUBSTANCE</div>
            <h3 style={{ fontFamily: "var(--ff-disp)", fontSize: isMobile ? "1.15rem" : "1.5rem", margin: isMobile ? "0 0 6px 0" : "0 0 10px 0", letterSpacing: ".02em", color: "var(--text)" }}>240 GSM Heavyweight Cotton</h3>
            <p style={{ fontFamily: "var(--ff-ui)", fontSize: isMobile ? "0.75rem" : "0.82rem", lineHeight: isMobile ? 1.45 : 1.6, color: "var(--muted)", margin: 0 }}>
              Custom-knit combed cotton with genuine substance. Substantial drape, zero transparency, and a structured collar that never bacon-curls wash after wash.
            </p>
          </div>

          <div className="why-bords-card" style={{
            flex: isMobile ? "0 0 84%" : "1",
            scrollSnapAlign: isMobile ? "start" : "none",
            background: theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
            border: "1px solid var(--border)",
            borderRadius: isMobile ? 8 : 12,
            padding: isMobile ? "14px 14px" : "32px 28px",
            transition: "transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease",
          }}>
            <div style={{ fontFamily: "var(--ff-mono)", fontSize: isMobile ? "0.68rem" : "0.8rem", color: "var(--accent-warm)", marginBottom: isMobile ? 6 : 12, letterSpacing: ".08em" }}>02 / SILHOUETTE</div>
            <h3 style={{ fontFamily: "var(--ff-disp)", fontSize: isMobile ? "1.15rem" : "1.5rem", margin: isMobile ? "0 0 6px 0" : "0 0 10px 0", letterSpacing: ".02em", color: "var(--text)" }}>Anti-Routine Silhouettes</h3>
            <p style={{ fontFamily: "var(--ff-ui)", fontSize: isMobile ? "0.75rem" : "0.82rem", lineHeight: isMobile ? 1.45 : 1.6, color: "var(--muted)", margin: 0 }}>
              Tired of fast-fashion templates? Every BORDS piece features relaxed drop-shoulder proportions, precision boxy cuts, and tailored architectural flow.
            </p>
          </div>

          <div className="why-bords-card" style={{
            flex: isMobile ? "0 0 84%" : "1",
            scrollSnapAlign: isMobile ? "start" : "none",
            background: theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
            border: "1px solid var(--border)",
            borderRadius: isMobile ? 8 : 12,
            padding: isMobile ? "14px 14px" : "32px 28px",
            transition: "transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease",
          }}>
            <div style={{ fontFamily: "var(--ff-mono)", fontSize: isMobile ? "0.68rem" : "0.8rem", color: "var(--accent-warm)", marginBottom: isMobile ? 6 : 12, letterSpacing: ".08em" }}>03 / ORIGIN</div>
            <h3 style={{ fontFamily: "var(--ff-disp)", fontSize: isMobile ? "1.15rem" : "1.5rem", margin: isMobile ? "0 0 6px 0" : "0 0 10px 0", letterSpacing: ".02em", color: "var(--text)" }}>Crafted in Bangalore</h3>
            <p style={{ fontFamily: "var(--ff-ui)", fontSize: isMobile ? "0.75rem" : "0.82rem", lineHeight: isMobile ? 1.45 : 1.6, color: "var(--muted)", margin: 0 }}>
              Born in Bangalore. Ethically produced with certified domestic partner mills using pre-shrunk organic cotton and zero-compromise finishing.
            </p>
          </div>
        </div>
      </section>

      <section id="collections" style={{ padding: isMobile ? "24px 16px 64px" : "48px 48px 96px", scrollMarginTop: isMobile ? "88px" : "112px" }}>
        <div className="section-header reveal" style={{ marginBottom: 40 }}>
          <div><div className="section-eyebrow">Curated</div><h2 className="section-title">Collections</h2></div>
        </div>
        {(() => {
          const activeCols = (collections && collections.length > 0)
            ? collections.filter(col => col.IS_ACTIVE !== false && Number(col.IS_ACTIVE || 1) !== 0)
            : DEFAULT_COLLECTIONS;
          const colsToRender = activeCols.length > 0 ? activeCols : DEFAULT_COLLECTIONS;
          const colCount = colsToRender.length;
          return (
            <div
              className="collections-grid"
              data-count={colCount}
              style={!isMobile && colCount === 2 ? { gridTemplateColumns: "1fr 1fr" } : {}}
            >
              {colsToRender.map((col, i) => {
                const bgClass = `col-bg-${((col.POSITION || i + 1) % 3) || 1}`;
                const h       = isMobile ? 260 : (colCount === 2 ? 380 : (i === 0 ? 540 : 256));
                return (
                  <div
                    key={col.ID ?? col.id ?? i}
                    className={`col-card reveal reveal-delay-${(i % 3) + 1}`}
                    onClick={() => navigate("collection", null, col)}
                    style={{
                      cursor: "pointer",
                      ...(colCount === 2 ? { gridRow: "span 1" } : {})
                    }}
                  >
                    <div className={`col-card-img ${bgClass}`} style={{ minHeight: h, position:"relative", overflow:"hidden" }}>
                      {(col.IMAGE_URL || col.image_url)
                        ? <DriveImg src={col.IMAGE_URL || col.image_url} alt={col.NAME||col.name||"Collection"}
                            style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",display:"block"}} />
                        : <div className="img-placeholder" style={{ height: h }}></div>
                      }
                    </div>
                    <div className="col-deco"><svg viewBox="0 0 24 24" strokeWidth="1.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg></div>
                    <div className="col-label">
                      <div className="col-label-tag">{col.TAG || col.tag}</div>
                      <div className="col-label-name" style={{ whiteSpace:"pre-line" }}>{col.NAME || col.name}</div>
                      <button className="col-label-cta" onClick={e=>{ e.preventDefault(); e.stopPropagation(); navigate("collection", null, col); }}>Explore <svg viewBox="0 0 24 24" strokeWidth="1.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg></button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </section>

      <section className="story" id="story">
        <div className="story-inner">
          <div>
            <div className="story-number reveal">01</div>
            <div className="story-label reveal">Our Story</div>
            <h2 className="story-heading reveal">{storyContent.story_heading}</h2>
            <p className="story-body reveal">{storyContent.story_body}</p>
            <div className="reveal"><a href="#story" className="btn-primary"
  onClick={e => { e.preventDefault(); document.getElementById("story")?.scrollIntoView({ behavior: "smooth" }); }}>
  Our Story
</a></div>
          </div>
          <div className="story-img-side reveal">
            <div className="story-accent-box"></div>
            <div className="story-img-box"></div>
            <div className="story-stat-row"><div className="stat-num">{storyContent.story_year}</div><div className="stat-label">Founded</div></div>
          </div>
        </div>
      </section>

      {/* ── FREQUENTLY ASKED QUESTIONS (Semantic H2 & FAQ Accordion) ── */}
      <section id="faq" style={{ padding: isMobile ? "48px 16px" : "80px 48px", borderTop: "1px solid var(--border)" }}>
        <div className="section-header reveal" style={{ marginBottom: 36 }}>
          <div>
            <div className="section-eyebrow">Got Questions?</div>
            <h2 className="section-title">Frequently Asked Questions</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, margin: "0 auto", display: "flex", flexDirection: "column", gap: 12 }}>
          {HOMEPAGE_FAQS.map((faq, idx) => {
            const isOpen = openFaq === idx;
            return (
              <div key={idx} style={{
                border: "1px solid var(--border)",
                borderRadius: 10,
                overflow: "hidden",
                background: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
                transition: "background 0.2s ease",
              }}>
                <button
                  onClick={() => setOpenFaq(isOpen ? null : idx)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: isMobile ? "16px 18px" : "20px 24px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    color: "var(--text)",
                    fontFamily: "var(--ff-ui)",
                    fontSize: isMobile ? ".85rem" : ".95rem",
                    fontWeight: 600,
                  }}
                  aria-expanded={isOpen}
                >
                  <span>{faq.q}</span>
                  <span style={{ fontSize: "1.2rem", transform: isOpen ? "rotate(45deg)" : "rotate(0deg)", transition: "transform 0.2s ease", color: "var(--accent-warm)", display: "inline-block", flexShrink: 0, marginLeft: 12 }}>+</span>
                </button>
                {isOpen && (
                  <div style={{
                    padding: isMobile ? "0 18px 18px 18px" : "0 24px 24px 24px",
                    fontFamily: "var(--ff-ui)",
                    fontSize: isMobile ? ".78rem" : ".85rem",
                    lineHeight: 1.65,
                    color: "var(--muted)",
                  }}>
                    {faq.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <div className="newsletter reveal">
        <div className="newsletter-inner">
          <div>
            <h2>Stay on the<br/><span>Drop List</span></h2>
            <p>First access to new drops, exclusive collabs, and restocks. No filler.</p>
          </div>
          {subscribed ? (
            <div className="sub-success">
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#44c864" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                <span style={{fontSize:"1rem",fontWeight:600,color:"var(--white)"}}>You're on the list!</span>
              </div>
              <p style={{fontSize:".8rem",color:"var(--muted)",margin:0,lineHeight:1.7}}>
                Thanks for subscribing. A welcome email is on its way to <strong style={{color:"var(--white)"}}>{email}</strong>.<br/>
                You'll be first to know about new drops and restocks.
              </p>
            </div>
          ) : (
            <div>
              <div className="newsletter-form">
                <input className="newsletter-input" type="email" placeholder="your@email.com"
                  value={email} onChange={e => { setEmail(e.target.value); setSubError(""); }}
                  onKeyDown={e => e.key === "Enter" && handleSubscribe()} />
                <button className="newsletter-submit" onClick={handleSubscribe} disabled={subLoading}>
                  {subLoading ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{animation:"spin 1s linear infinite"}}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg> : "Subscribe"}
                </button>
              </div>
              {subError && <div style={{marginTop:8,fontSize:".72rem",color:"#ff8080",display:"flex",alignItems:"center",gap:6}}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {subError}
              </div>}
            </div>
          )}
        </div>
      </div>

      {/* ── TRUST BADGES ── */}
      <div style={{padding:"24px 48px",borderTop:"1px solid var(--border)",display:"flex",flexWrap:"wrap",gap:"10px 32px",justifyContent:"center",alignItems:"center"}}>
        {[
          {svg:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>, label:cleanBadgeLabel(annBarItems[0] || "Secure Checkout")},
          {svg:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/></svg>, label:cleanBadgeLabel(annBarItems[1] || "Easy 7-Day Returns")},
          {svg:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1" y="3" width="15" height="13"/><path d="M16 8h4l3 5v4h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>, label:cleanBadgeLabel(annBarItems[2] || "Free Ship over ₹999")},
          {svg:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>, label:cleanBadgeLabel(annBarItems[3] || "Same-Day Processing")},
          {svg:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>, label:cleanBadgeLabel(annBarItems[4] || "1000+ Happy Customers")},
        ].map(({svg,label}) => (
          <div key={label} style={{display:"flex",alignItems:"center",gap:7,color:"var(--muted)",fontSize:".6rem",letterSpacing:".14em",textTransform:"uppercase",fontWeight:500}}>
            <span style={{color:"var(--accent-warm)",display:"flex",flexShrink:0}}>{svg}</span>{label}
          </div>
        ))}
      </div>

      <footer style={{ position: 'relative', overflow: 'hidden' }}>
        <FooterNoiseCanvas />
        <div className="footer-top" style={{ position: 'relative', zIndex: 2 }}>
          <div>
            <div style={{marginBottom:16}}>
              <button
                onClick={() => { navigate("home"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                style={{ background: "none", border: "none", cursor: "pointer", display: "inline-flex", padding: 0, textAlign: "left" }}
                aria-label="BORDS — Go to home"
              >
                <Logo size="md" showTagline={true} />
              </button>
            </div>
            <p className="footer-tagline">Bored of Routine Designs. Try New.</p>
          </div>
          {[
            { title: "Shop",   links: [
              { label:"All Drops",     action:()=>navigate("shop") },
              { label:"New Arrivals", action:()=>document.getElementById("arrivals")?.scrollIntoView({behavior:"smooth"}) },
              { label:"Tops",        action:()=>{ setActiveCategory("tops"); navigate("shop"); } },
              { label:"Bottoms",     action:()=>{ setActiveCategory("bottoms"); navigate("shop"); } },
              { label:"Accessories", action:()=>{ setActiveCategory("accessories"); navigate("shop"); } },
            ]},
            { title: "Universe", links: [
              { label:"🎴 Cards Vault", action:()=>navigate("cards") },
              { label:"📖 Lore of SAS", action:()=>navigate("comics") },
            ]},
            { title: "Transparency", links: [
              { label:"Our Fabrics",     action:()=>navigate("our-fabrics") },
              { label:"Sustainability",  action:()=>navigate("sustainability") },
              ...(storeSettings.show_fabric_certifications === "1" ? [{ label:"Certifications", action:()=>navigate("certifications") }] : []),
              { label:"Care Guide",      action:()=>navigate("care") },
              { label:"Journal",         action:()=>navigate("journal") },
            ]},
            { title: "Company", links: [
              { label:"About BORDS",       action:()=>navigate("about") },
              { label:"Contact",           action:()=>navigate("contact") },
              { label:"Size Guide",        action:()=>setShowSizeGuideGlobal(true) },
              { label:"Shipping & Returns",action:()=>{ setTrackTab(true); navigate("checkout"); } },
              { label:"My Orders",         action:()=>user ? navigate("myorders") : navigate("auth") },
            ]},
            { title: "Follow", links: [
              { label:"Instagram", action:()=>footerSettings.social_instagram ? window.open(footerSettings.social_instagram, "_blank") : window.open("https://instagram.com", "_blank") },
              { label:"Pinterest", action:()=>footerSettings.social_pinterest ? window.open(footerSettings.social_pinterest, "_blank") : window.open("https://pinterest.com", "_blank") },
              { label:"YouTube",   action:()=>footerSettings.social_youtube ? window.open(footerSettings.social_youtube, "_blank") : window.open("https://youtube.com", "_blank") },
              { label:"Live Chat (Helper)", action:()=>window.dispatchEvent(new CustomEvent('bords_open_helper')) },
            ]},
          ].map((col) => (
            <div key={col.title}>
              <div className="footer-col-title">{col.title}</div>
              <ul className="footer-links">{col.links.map(l => (
                <li key={l.label}><a href="#footer" onClick={e=>{e.preventDefault();l.action();}}>{l.label}</a></li>
              ))}</ul>
            </div>
          ))}
        </div>
        <div className="footer-bottom" style={{ position: 'relative', zIndex: 2 }}>
          <span>© 2026 BORDS. All rights reserved.</span>
          <span>Privacy · Terms · Cookies · <span style={{cursor:"pointer",opacity:0.3}} onClick={() => navigate("admin")}>Admin</span></span>
        </div>
      </footer>



      </main>{/* /#main-content */}

      {/* CART DRAWER */}
      <div className={`cart-overlay${cartOpen ? " open" : ""}`} onClick={() => setCartOpen(false)}></div>
      <div className={`cart-drawer${cartOpen ? " open" : ""}`}>
        <div className="cart-header">
          <h3>Your Bag</h3>
          <button className="cart-close" onClick={() => setCartOpen(false)}>
            <svg viewBox="0 0 24 24" strokeWidth="1.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div className="cart-body">
          {cart.length === 0 ? (
            <div className="cart-empty">
              <div className="cart-empty-icon">0</div>
              <p>Your bag is empty</p>
            </div>
          ) : (
            <div style={{ width: "100%" }}>
              {cart.map(item => {
                const itemProdId = item.product_id ?? item.PRODUCT_ID;
                const itemSize = item.size ?? item.SIZE ?? item.item_size;
                const matchedProd = products.find(p => String(p.ID ?? p.id) === String(itemProdId));
                const itemMaxStock = matchedProd ? getProductSizeStock(matchedProd, itemSize) : 99;
                const currentItemQty = item.QTY || item.qty || 1;
                const isMaxReached = currentItemQty >= itemMaxStock;
                return (
                <div key={item.ID} className="cart-item">
                  <div className="cart-item-info">
                    <div className="cart-item-name">{item.NAME || item.name}</div>
                    <div className="cart-item-meta" style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px", flexWrap: "wrap" }}>
                      <span>Size: {itemSize}</span>
                      <span style={{ opacity: 0.35 }}>|</span>
                      <div style={{ display: "inline-flex", alignItems: "center", background: theme === "light" ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.06)", border: theme === "light" ? "1px solid rgba(0,0,0,0.12)" : "1px solid rgba(255,255,255,0.12)", borderRadius: "6px", overflow: "hidden", height: "28px" }}>
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleUpdateQty(item.ID, currentItemQty - 1); }}
                          style={{ background: "none", border: "none", color: "var(--white)", width: "30px", height: "100%", cursor: "pointer", fontSize: "0.9rem", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, opacity: 0.75, transition: "background 0.2s" }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = 1; e.currentTarget.style.background = "rgba(var(--bord-fg-rgb), 0.08)"; }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = 0.75; e.currentTarget.style.background = "none"; }}
                          aria-label="Decrease quantity"
                        >
                          −
                        </button>
                        <span style={{ fontSize: "0.72rem", minWidth: "18px", textAlign: "center", fontWeight: "600", color: "var(--white)", padding: "0 2px" }}>
                          {currentItemQty}
                        </span>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isMaxReached) {
                              showAppToast(`Only ${itemMaxStock} pieces available in size ${itemSize}.`, "warning");
                              return;
                            }
                            handleUpdateQty(item.ID, currentItemQty + 1);
                          }}
                          disabled={isMaxReached}
                          style={{
                            background: "none",
                            border: "none",
                            color: "var(--white)",
                            width: "30px",
                            height: "100%",
                            cursor: isMaxReached ? "not-allowed" : "pointer",
                            fontSize: "0.9rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                            opacity: isMaxReached ? 0.3 : 0.75,
                            transition: "background 0.2s, opacity 0.2s"
                          }}
                          onMouseEnter={e => {
                            if (!isMaxReached) {
                              e.currentTarget.style.opacity = 1;
                              e.currentTarget.style.background = "rgba(var(--bord-fg-rgb), 0.08)";
                            }
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.opacity = isMaxReached ? 0.3 : 0.75;
                            e.currentTarget.style.background = "none";
                          }}
                          title={isMaxReached ? `Max ${itemMaxStock} available` : "Increase quantity"}
                          aria-label="Increase quantity"
                        >
                          +
                        </button>
                      </div>
                      {isMaxReached && (
                        <span style={{ fontSize: "0.68rem", color: "#FF7A59", fontWeight: 700, letterSpacing: "0.02em" }}>
                          (Max limit: {itemMaxStock})
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="cart-item-right">
                    <div className="cart-item-price">₹ {((item.PRICE || item.price) * currentItemQty).toLocaleString()}</div>
                    <button className="cart-item-remove" onClick={() => handleRemoveFromCart(item.ID)}>×</button>
                  </div>
                </div>
                );
              })}

              {/* INCLUDED PHYSICAL COLLECTIBLE CARD & 2x STICKERS IN CART */}
              <div className="co-collectible-bonus" style={{ margin: "14px 0 6px" }}>
                <div className="co-bonus-header">
                  <span className="co-bonus-tag">🎁 INCLUDED PHYSICAL DROP</span>
                  <span className="co-bonus-free">FREE</span>
                </div>
                {selectedCollectibleCard ? (
                  <div className="co-bonus-card-item">
                    <img
                      src={selectedCollectibleCard.card_art}
                      alt={selectedCollectibleCard.name}
                      className="co-bonus-card-img"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://images.unsplash.com/photo-1579783902614-a3fb3927b675?w=800&q=80";
                      }}
                    />
                    <div className="co-bonus-card-info">
                      <div className="co-bonus-card-name">🎴 {selectedCollectibleCard.name}</div>
                      <div className="co-bonus-card-meta">
                        <span style={{ color: "#00F0FF", fontWeight: 800 }}>{selectedCollectibleCard.rarity?.toUpperCase()} TIER</span>
                        <span>· {selectedCollectibleCard.card_code}</span>
                      </div>
                      <button
                        onClick={() => setSpinModalOpen(true)}
                        style={{ background: "none", border: "none", color: "#00F0FF", fontSize: "0.68rem", fontWeight: 700, cursor: "pointer", padding: "3px 0", textDecoration: "underline" }}
                      >
                        🔄 Re-spin / Change Card (3 chances)
                      </button>
                    </div>
                    <div className="co-bonus-price">FREE</div>
                  </div>
                ) : (
                  <div className="co-bonus-spin-prompt">
                    <div>
                      <div style={{ fontWeight: 700, fontSize: ".76rem", color: "#00F0FF" }}>🎴 1x Collectible Anime Card</div>
                      <div style={{ fontSize: ".66rem", color: "rgba(255,255,255,0.6)" }}>3 chances to spin & pick your card!</div>
                    </div>
                    <button className="co-btn-spin-now" onClick={() => setSpinModalOpen(true)}>⚡ Spin Card</button>
                  </div>
                )}
                <div className="co-bonus-sticker-item">
                  <div className="co-bonus-sticker-icon">✨</div>
                  <div className="co-bonus-sticker-info">
                    <div className="co-bonus-sticker-name">2x Holographic Die-Cut Vinyl Stickers</div>
                    <div className="co-bonus-sticker-meta">Limited Edition Waterproof Vinyl · Free with T-Shirt</div>
                  </div>
                  <div className="co-bonus-price">FREE</div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="cart-footer">
          <div className="cart-total">
            <span>Total</span>
            <span>₹ {cartTotal.toLocaleString()}</span>
          </div>
          <button className="btn-primary"
            style={{ width:"100%", justifyContent:"center", display:"flex", border:"none",
              cursor: cart.length === 0 ? "not-allowed" : "pointer",
              opacity: cart.length === 0 ? 0.4 : 1 }}
            disabled={cart.length === 0}
            onClick={() => { if(cart.length === 0) return; setCartOpen(false); navigate("checkout"); }}>
            {cart.length === 0 ? "Add items to checkout" : "Checkout"}
            {cart.length > 0 && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>}
          </button>
        </div>
      </div>

      {/* ── KHADI 7-PIECE CAPSULE WAITLIST MODAL (Section 4B) ── */}
      {waitlistModalOpen && (
        <div
          onClick={() => { setWaitlistModalOpen(false); setWaitlistSubmitted(false); }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(13, 13, 13, 0.75)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            zIndex: 999999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px"
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="neo-brutalist-card bords-mode-charged"
            style={{
              maxWidth: 480,
              width: "100%",
              padding: "32px 28px",
              background: "var(--bords-parchment, #F5F0E8)",
              position: "relative"
            }}
          >
            <button
              onClick={() => { setWaitlistModalOpen(false); setWaitlistSubmitted(false); }}
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                border: "2px solid #0D0D0D",
                background: "#fff",
                fontWeight: 900,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
              aria-label="Close modal"
            >
              ✕
            </button>

            <div className="sticker-stamp" style={{ background: "var(--bords-pop-lime, #D4FF3F)", color: "#0D0D0D", marginBottom: 12 }}>
              Limited Drop — 7 Pcs Only
            </div>

            <h3 style={{
              fontFamily: "var(--font-display, 'Neue Montreal', 'Bebas Neue', sans-serif)",
              fontSize: "1.8rem",
              lineHeight: 1.1,
              margin: "0 0 10px",
              color: "#0D0D0D"
            }}>
              HAND-SPUN KHADI CAPSULE
            </h3>

            <p style={{ fontSize: ".85rem", color: "#4A4744", lineHeight: 1.6, marginBottom: 20 }}>
              Woven on traditional charkha in Wardha, Maharashtra. Zero electricity, zero greenwashing.
              Because yield is strictly capped at 7 numbered shirts, this drop unlocks via private waitlist.
            </p>

            {waitlistSubmitted ? (
              <div style={{
                padding: "16px",
                background: "rgba(74,93,58,0.12)",
                border: "2px solid var(--bords-moss, #4A5D3A)",
                borderRadius: 6,
                textAlign: "center"
              }}>
                <div style={{ fontSize: "1.2rem", marginBottom: 6 }}>🪡 ✅</div>
                <div style={{ fontFamily: "var(--font-stamp, 'Archivo Black', sans-serif)", fontSize: ".82rem", color: "var(--bords-moss, #4A5D3A)", textTransform: "uppercase" }}>
                  Priority Access Confirmed
                </div>
                <div style={{ fontSize: ".76rem", color: "#2B2A28", marginTop: 4 }}>
                  We will email <strong>{waitlistEmail}</strong> exactly 1 hour before the 7 pieces go live.
                </div>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!waitlistEmail || !waitlistEmail.includes("@")) return;
                  setWaitlistSubmitted(true);
                  if (vibe === "loud") triggerConfetti();
                }}
              >
                <input
                  type="email"
                  required
                  placeholder="Enter your email for drop access..."
                  value={waitlistEmail}
                  onChange={e => setWaitlistEmail(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    border: "2px solid #0D0D0D",
                    borderRadius: 4,
                    fontSize: ".85rem",
                    marginBottom: 12,
                    boxSizing: "border-box",
                    outline: "none",
                    fontFamily: "'DM Sans', sans-serif"
                  }}
                />
                <button
                  type="submit"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    background: "var(--bords-pop-lime, #D4FF3F)",
                    border: "2px solid #0D0D0D",
                    color: "#0D0D0D",
                    fontFamily: "var(--font-stamp, 'Archivo Black', sans-serif)",
                    fontSize: ".82rem",
                    letterSpacing: ".06em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                    boxShadow: "3px 3px 0 #0D0D0D",
                    transition: "all .15s ease"
                  }}
                >
                  NOTIFY ME FIRST 🪡
                </button>
              </form>
            )}
          </div>
        </div>
      )}
      <HelperChat storeSettings={storeSettings} />
      <Suspense fallback={null}>
        <CardSpinRewardModal
          isOpen={spinModalOpen}
          onClose={() => setSpinModalOpen(false)}
          onSelectCard={(c) => {
            setSelectedCollectibleCard(c);
            try {
              localStorage.setItem('bords_selected_card', JSON.stringify(c));
            } catch (_) {}
          }}
          theme={theme}
          productName={cart[0]?.NAME || cart[0]?.name || "Heavyweight Streetwear T-Shirt"}
        />
      </Suspense>
    </>
  );
}


// ── PRODUCT DETAIL PAGE ───────────────────────────────────────────────────────
function ProductDetailPage({
  product: initialProduct,
  onBack,
  onAddToCart,
  cartCount,
  cart = [],
  showAppToast,
  onCartOpen,
  wishlist = [],
  onToggleWishlist,
  onCheckout,
  theme,
  prevPage,
  selectedCollection,
  onGoHome,
  onNavigate,
  showCertifications = false,
  vibe = "loud",
  loudProducts = [],
  storeSettings = {}
}) {
  const [selectedSize,  setSelectedSize]  = useState(null);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [showReturnsModal, setShowReturnsModal] = useState(false);
  const [activeImg,     setActiveImg]     = useState(0);
  const [added,         setAdded]         = useState(false);
  const [qty,           setQty]           = useState(0);
  const [product,       setProduct]       = useState(initialProduct);

  const saleItem = useMemo(() => {
    if (vibe !== "loud" || !loudProducts || loudProducts.length === 0) return null;
    const pName = (product?.NAME || initialProduct?.NAME || "").toLowerCase().trim();
    const item = loudProducts.find(item => item && item.name && (
      item.name.toLowerCase().trim() === pName ||
      pName.includes(item.name.toLowerCase().trim()) ||
      item.name.toLowerCase().trim().includes(pName)
    ));
    if (!item) return null;
    const end = item.saleEnd || storeSettings?.loud_drop_end_date;
    if (end && new Date(end).getTime() <= Date.now()) return null;
    return item;
  }, [vibe, loudProducts, product?.NAME, initialProduct?.NAME, storeSettings?.loud_drop_end_date]);

  const salePct = saleItem && saleItem.pct ? Number(saleItem.pct) : null;
  const salePrice = salePct && product?.PRICE ? Math.round(Number(product.PRICE) * (1 - salePct / 100)) : null;

  const [isMobile,      setIsMobile]      = useState(() => window.innerWidth <= 900);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", onResize);
    if (isMobile) {
      document.body.classList.add("has-pdp-sticky-bar");
    }
    return () => {
      window.removeEventListener("resize", onResize);
      document.body.classList.remove("has-pdp-sticky-bar");
    };
  }, [isMobile]);

  // Always fetch fresh product data when PDP opens so sizes/updates are current
  useEffect(() => {
    if (!initialProduct?.ID) return;
    fetch(`${API}/products/${initialProduct.ID}`)
      .then(r => r.json())
      .then(({ data, monthly_sales_count }) => {
        if (data) setProduct(data);
        if (typeof monthly_sales_count === 'number') setMonthlySales(monthly_sales_count);
      })
      .catch(() => {}); // silently keep initialProduct on error
  }, [initialProduct?.ID]);

  // ── SEO: dynamic title, meta, canonical, JSON-LD schema, GA4 event ─────────
  useEffect(() => {
    if (!product) return;
    const name = product.NAME || product.name || '';
    const slug = product.slug || `bords-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    const price = product.PRICE || product.price || 0;
    const desc = product.seo_description || product.SEO_DESCRIPTION ||
      `Shop the BORDS ${name}. Heavyweight 240 GSM oversized fit designed for everyday streetwear. Free shipping across India.`;

    const prodId = product.ID ?? product.id;
    const isNumericPath = window.location.pathname.startsWith('/product/');
    const productCanonical = isNumericPath
      ? `https://www.bords.co.in/product/${prodId}`
      : `https://www.bords.co.in/products/${slug}`;

    // Update page title and meta
    try {
      let rawImgs = [];
      try {
        const rawUrls = product.IMAGE_URLS || product.image_urls;
        if (rawUrls) rawImgs = Array.isArray(rawUrls) ? rawUrls : JSON.parse(rawUrls);
      } catch (_) {}
      const firstUrl = rawImgs.find(Boolean) || product.IMAGE_URL || product.image_url || '';
      const resolvedImg = firstUrl ? resolveImageUrl(firstUrl) : undefined;

      const { setPageMeta, injectProductSchema, injectBreadcrumb, analytics } = require('../lib/seo');
      setPageMeta({
        title:       `BORDS ${name} | Heavyweight Oversized T-Shirt`,
        description: desc,
        canonical:   productCanonical,
        image:       resolvedImg,
      });

      // Product + BreadcrumbList structured data
      injectProductSchema(product);
      injectBreadcrumb([
        { name: 'Home',        url: 'https://www.bords.co.in/' },
        { name: 'T-Shirts',   url: 'https://www.bords.co.in/collections/oversized-t-shirts' },
        { name },
      ]);

      // GA4 view_item event
      analytics.viewItem({ id: product.ID, name, price, category: product.CATEGORY });
    } catch (_) {} // seo.js is non-critical — never crash product page

    // Cleanup: restore homepage defaults and remove product schema on unmount
    return () => {
      try {
        const { resetPageMeta, removeProductSchema } = require('../lib/seo');
        resetPageMeta();
        removeProductSchema();
      } catch (_) {}
    };
  }, [product?.ID || product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Social Proof: Live Viewers (SSE) & Monthly Sales ──────────────────────
  const [liveViewers,  setLiveViewers]  = useState(0);
  const [monthlySales, setMonthlySales] = useState(0);

  useEffect(() => {
    if (!initialProduct?.ID) return;
    const prodId = initialProduct.ID;
    // Connect to SSE presence stream for live viewer count
    const presenceBase = typeof API === 'string' ? API.replace(/\/api$/, '') : '';
    let es;
    try {
      es = new EventSource(`${presenceBase}/api/presence/${prodId}`);
      es.onmessage = (e) => {
        try {
          const d = JSON.parse(e.data);
          if (typeof d.count === 'number') setLiveViewers(d.count);
        } catch (_) {}
      };
      es.onerror = () => { try { es.close(); } catch (_) {} };
    } catch (_) {}
    return () => { if (es) { try { es.close(); } catch (_) {} } };
  }, [initialProduct?.ID]);


  // Parse sizes — handles string or array, normalizes to {size, qty} format
  let sizes = [];
  try {
    const rawSizes = product.SIZES || product.sizes;
    if (rawSizes && rawSizes !== 'null' && rawSizes !== '[]') {
      const parsed = Array.isArray(rawSizes) ? rawSizes : JSON.parse(rawSizes);
      if (Array.isArray(parsed) && parsed.length > 0) {
        sizes = parsed.map(s => {
          if (typeof s === "string") return { size: s, qty: product.STOCK ?? 10 };
          return s;
        });
      }
    }
  } catch(_) { sizes = []; }
  const isSoldOut = product.STOCK === 0 || product.BADGE === "Sold Out";

  // Build images array — support IMAGE_URLS (JSON array) or fall back to IMAGE_URL
  let images = [];
  try {
    if (product.IMAGE_URLS) images = JSON.parse(product.IMAGE_URLS);
  } catch(_) {}
  if (!images.length && product.IMAGE_URL) images = [product.IMAGE_URL];
  images = images.map(toDirectImageUrl).filter(Boolean);

  const [sizeError, setSizeError] = useState(false);

  const handleAdd = () => {
    if (!selectedSize && sizes.length > 0) {
      setSizeError(true);
      setTimeout(() => setSizeError(false), 500);
      return;
    }
    const finalSize = selectedSize || "One Size";
    const finalQty = qty || 1;
    const itemToAdd = salePrice ? { ...product, PRICE: salePrice, ORIGINAL_PRICE: product.PRICE } : product;
    for (let i = 0; i < finalQty; i++) onAddToCart(itemToAdd, finalSize);
    setAdded(true);
  };

  return (
    <Suspense fallback={<PageFallback theme={theme} label="Loading Product" />}>
      <NoberoProductScreen
        product={salePrice ? {
          ...product,
          PRICE: salePrice,
          ORIGINAL_PRICE: product.PRICE,
          SALE_PCT: salePct,
          IS_LOUD_SALE: true,
          SALE_END: saleItem?.saleEnd || storeSettings?.loud_drop_end_date,
        } : product}
        onBack={onBack}
        onAddToCart={(p, s, q = 1) => {
          const itemToAdd = salePrice ? { ...p, PRICE: salePrice, ORIGINAL_PRICE: p.PRICE } : p;
          return onAddToCart(itemToAdd, s, q);
        }}
        cartCount={cartCount}
        cart={cart}
        onCartOpen={onCartOpen}
        wishlist={wishlist}
        onToggleWishlist={onToggleWishlist}
        onCheckout={onCheckout}
        theme={theme}
        onNavigate={onNavigate}
        onGoHome={onGoHome}
        storeSettings={storeSettings}
        vibe={vibe}
        saleCountdown={saleItem?.saleEnd || storeSettings?.loud_drop_end_date}
      />
    </Suspense>
  );
}

// ── Fabric Tab — isolated component so it can use useState cleanly ──────────
function FabricTab({ product, theme, onNavigate, showCertifications = false }) {
  const [open, setOpen] = React.useState(false);
  const isDark   = theme !== 'light';
  const fab  = product.FABRIC_TAG ? getFabricById(product.FABRIC_TAG) : null;
  const band = fab ? getBand(fab.score, isDark) : null;
  const tabBg     = isDark ? 'rgba(244,242,238,0.04)' : 'rgba(43,42,40,0.04)';
  const tabBorder = isDark ? 'rgba(244,242,238,0.1)'  : 'rgba(43,42,40,0.12)';
  const tabText   = isDark ? 'rgba(244,242,238,0.85)' : '#2B2A28';
  const tabMuted  = isDark ? 'rgba(244,242,238,0.45)' : 'rgba(43,42,40,0.5)';
  const tabActive = isDark ? '#f4f2ee' : '#2B2A28';

  return (
    <div style={{ marginTop: 20, borderRadius: 10, border: `1px solid ${tabBorder}`, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', background: tabBg, border: 'none', cursor: 'pointer',
          fontFamily: "'DM Sans',sans-serif", fontSize: '.78rem', fontWeight: 600,
          letterSpacing: '.08em', textTransform: 'uppercase', color: tabActive,
        }}
        aria-expanded={open}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {fab && band && showCertifications && (
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: band.color,
              display: 'inline-block', flexShrink: 0 }} />
          )}
          Fabric & Material
        </span>
        <span style={{ fontSize: '.9rem', color: tabMuted,
          transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>▾</span>
      </button>

      {open && (
        <div style={{ padding: '16px', background: isDark ? 'rgba(244,242,238,0.02)' : '#F7F3EA' }}>
          {fab && band ? (
            <div>
              {/* Score bar — only show when certifications enabled */}
              {showCertifications && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  <div style={{ flex: 1, height: 6, background: 'rgba(43,42,40,0.1)', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 999, background: band.color,
                      width: `${(fab.score / 10) * 100}%`,
                      transition: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'none' : 'width 0.8s cubic-bezier(0.4,0,0.2,1)',
                    }} />
                  </div>
                  <span style={{ fontSize: '.82rem', fontWeight: 700, color: band.color, minWidth: 24 }}>{fab.score}</span>
                  <span style={{
                    fontSize: '.65rem', fontWeight: 700, letterSpacing: '.06em',
                    padding: '2px 8px', borderRadius: 999, border: `1px solid ${band.border}`,
                    background: band.bg, color: band.color,
                  }}>{band.label}</span>
                </div>
              )}

              {/* Detail rows — stagger with CSS animation */}
              <style>{`
                @keyframes pdp-fabric-in {
                  from { opacity: 0; transform: translateY(6px); }
                  to   { opacity: 1; transform: translateY(0); }
                }
                @media (prefers-reduced-motion: reduce) {
                  @keyframes pdp-fabric-in { from { opacity: 0; } to { opacity: 1; } }
                }
              `}</style>
              {[
                { label: 'Fibre',   value: fab.fibre },
                { label: 'Verdict', value: fab.verdictLong },
                { label: 'Care',    value: fab.careNote },
                ...(showCertifications ? [
                  { label: 'Cert',    value: `${fab.certBody}` },
                  { label: 'Cert No', value: fab.certNumber },
                ] : []),
              ].map(({ label, value }, i) => (
                <div key={label} style={{
                  display: 'grid', gridTemplateColumns: '72px 1fr', gap: 8, marginBottom: 10,
                  opacity: 0,
                  animation: `pdp-fabric-in .35s ease forwards`,
                  animationDelay: `${i * 60}ms`,
                }}>
                  <span style={{ fontSize: '.62rem', letterSpacing: '.12em', textTransform: 'uppercase', color: tabMuted, paddingTop: 2 }}>{label}</span>
                  <span style={{ lineHeight: 1.6, color: tabText,
                    fontFamily: label === 'Cert No' ? "'DM Mono',monospace" : 'inherit', fontSize: label === 'Cert No' ? '.75rem' : '.82rem' }}>
                    {value}
                  </span>
                </div>
              ))}
              {showCertifications && (
                <a href="/certifications"
                  onClick={e => { if (onNavigate) { e.preventDefault(); onNavigate("certifications"); } }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6,
                    fontSize: '.72rem', color: isDark ? '#88D66C' : '#4A5D3A', textDecoration: 'none',
                    borderBottom: isDark ? '1px solid rgba(136,214,108,0.4)' : '1px solid rgba(74,93,58,0.3)', cursor: 'pointer' }}>
                  Verify cert independently ↗
                </a>
              )}
            </div>
          ) : (
            <div style={{ fontSize: '.83rem', color: tabMuted, lineHeight: 1.65 }}>
              <strong style={{ color: tabText }}>Natural Fibre Garment.</strong>{' '}
              Precision tailored from premium cotton.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const ProductCard = React.memo(function ProductCard({
  product,
  delay,
  rank,
  isMobile = false,
  onAddToCart,
  onViewProduct,
  wishlist = [],
  onToggleWishlist,
  theme,
  onCheckout,
  onOpenWaitlist,
  vibe = "loud",
  showCertifications = false,
  loudProducts = [],
  cart = [],
  showAppToast
}) {
  const saleItem = useMemo(() => {
    if (vibe !== "loud" || !loudProducts || loudProducts.length === 0) return null;
    const pName = (product.NAME || "").toLowerCase().trim();
    const item = loudProducts.find(item => item && item.name && (
      item.name.toLowerCase().trim() === pName ||
      pName.includes(item.name.toLowerCase().trim()) ||
      item.name.toLowerCase().trim().includes(pName)
    ));
    if (!item) return null;
    const end = item.saleEnd;
    if (end && new Date(end).getTime() <= Date.now()) return null;
    return item;
  }, [vibe, loudProducts, product.NAME]);

  const salePct = saleItem && saleItem.pct ? Number(saleItem.pct) : null;
  const salePrice = salePct && product.PRICE ? Math.round(Number(product.PRICE) * (1 - salePct / 100)) : null;

  const [selectedSize, setSelectedSize] = useState(null);
  const [justAdded, setJustAdded] = useState(false);
  const [sizeError, setSizeError] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });
  const [isRevealed, setIsRevealed] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (isRevealed) return;
    const el = cardRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setIsRevealed(true);
      return;
    }

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsRevealed(true);
          obs.disconnect();
        }
      },
      { rootMargin: "250px 0px", threshold: 0.01 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [isRevealed]);

  const sizes = useMemo(() => {
    let list = [];
    try {
      const rawSizes = product.SIZES || product.sizes;
      if (rawSizes && rawSizes !== "null" && rawSizes !== "[]") {
        const parsed = Array.isArray(rawSizes) ? rawSizes : JSON.parse(rawSizes);
        if (Array.isArray(parsed)) {
          list = parsed.map(s => {
            if (typeof s === "string") return { size: s, qty: product.STOCK ?? 10 };
            return s;
          });
        }
      }
    } catch(_) { list = []; }
    if (!list.length) {
      list = (product.SIZES || "S,M,L,XL").split(",").map(s => ({ size: s.trim(), qty: product.STOCK ?? 10 }));
    }
    return list;
  }, [product.SIZES, product.sizes, product.STOCK]);

  const isSoldOut = product.STOCK === 0 || product.BADGE === "Sold Out";

  const selectedSizeStock = useMemo(() => {
    if (!selectedSize) return product.STOCK ?? 10;
    const match = sizes.find(x => (x.size || x) === selectedSize);
    return match && (match.qty !== undefined || match.stock !== undefined) ? Number(match.qty ?? match.stock) : (product.STOCK ?? 10);
  }, [selectedSize, sizes, product.STOCK]);

  const inCartForSelectedSize = useMemo(() => {
    if (!selectedSize || !cart || !cart.length) return 0;
    const prodId = product.ID ?? product.id;
    return cart.reduce((acc, c) => {
      const cProdId = c.product_id ?? c.PRODUCT_ID;
      const cSize = c.size ?? c.SIZE ?? c.item_size;
      if (String(cProdId) === String(prodId) && String(cSize).toUpperCase() === String(selectedSize).toUpperCase()) {
        return acc + Number(c.qty ?? c.QTY ?? 1);
      }
      return acc;
    }, 0);
  }, [selectedSize, cart, product.ID, product.id]);

  const isSelectedSizeMaxed = selectedSize && inCartForSelectedSize >= selectedSizeStock;

  const handleQuickAdd = async (event) => {
    event?.stopPropagation();
    if (isSoldOut) return;
    if (!selectedSize && sizes.length > 0) {
      setSizeError(true);
      setTimeout(() => setSizeError(false), 500);
      return;
    }
    const finalSize = selectedSize || "One Size";
    const curStock = selectedSizeStock;
    if (inCartForSelectedSize >= curStock) {
      if (showAppToast) {
        showAppToast(`Only ${curStock} pieces available in size ${finalSize}. You already have ${inCartForSelectedSize} in your bag.`, "warning");
      }
      return;
    }
    const itemToAdd = salePrice ? { ...product, PRICE: salePrice, ORIGINAL_PRICE: product.PRICE } : product;
    if (onAddToCart) {
      const res = await onAddToCart(itemToAdd, finalSize, 1);
      if (res && res.success === false) return;
    }
    setJustAdded(true);
  };

  const tiltRafRef = useRef(null);

  const handleMouseMove = (e) => {
    if (isMobile) return;
    if (tiltRafRef.current) return;
    const currentTarget = e.currentTarget;
    const clientX = e.clientX;
    const clientY = e.clientY;
    tiltRafRef.current = requestAnimationFrame(() => {
      tiltRafRef.current = null;
      if (!currentTarget) return;
      const rect = currentTarget.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const xc = rect.width / 2;
      const yc = rect.height / 2;
      const dx = x - xc;
      const dy = y - yc;
      setTilt({
        rx: -(dy / yc) * 4,
        ry: (dx / xc) * 4
      });
    });
  };

  const handleMouseLeave = () => {
    if (tiltRafRef.current) {
      cancelAnimationFrame(tiltRafRef.current);
      tiltRafRef.current = null;
    }
    setTilt({ rx: 0, ry: 0 });
  };

  const fab = product.FABRIC_TAG ? getFabricById(product.FABRIC_TAG) : null;
  const band = fab ? getBand(fab.score, theme === 'dark') : null;

  return (
    <div
      ref={cardRef}
      className={`product-card reveal ${isRevealed ? "visible" : ""} reveal-delay-${delay} ${vibe === "loud" ? "neo-brutalist-card genz-loud-card" : ""}`}
      data-added={justAdded ? "true" : undefined}
      onClick={(e) => {
        if (!e.target.closest(".card-quick-add") && !e.target.closest(".card-flip-toggle-btn") && !e.target.closest(".card-waitlist-btn")) {
          onViewProduct(product);
        }
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        cursor: "pointer",
        perspective: "1000px",
        transform: (tilt.rx !== 0 || tilt.ry !== 0) ? `perspective(1000px) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)` : undefined,
        transition: (tilt.rx === 0 && tilt.ry === 0) ? "transform 0.4s ease, box-shadow 0.2s ease" : "none"
      }}
    >
      <div className="product-card-img" style={{ position: "relative" }}>
        {vibe === "loud" ? (
          <div className="genz-spec-tag">SPEC // {String(rank || delay).padStart(2, "0")}</div>
        ) : (
          <div className="product-card-index">{String(rank || delay).padStart(2, "0")}</div>
        )}

        {/* Sticker Stamp (Section 4B) */}
        {vibe === "loud" && (
          <div style={{ position: "absolute", top: 10, left: 10, zIndex: 4, pointerEvents: "none", display: "flex", flexDirection: "column", gap: 5 }}>
            {salePct ? (
              <span className="sticker-stamp genz-discount-stamp">
                ⚡ {salePct}% OFF // VAULT
              </span>
            ) : null}
            {product.FABRIC_TAG === "khadi" ? (
              <span className="sticker-stamp" style={{ background: "var(--bords-pop-cobalt, #2E4CFF)", color: "#FFF", fontSize: ".58rem" }}>
                Limited · 7 Pcs 🪡
              </span>
            ) : product.FABRIC_TAG === "recycled-cotton" ? (
              <span className="sticker-stamp" style={{ background: "var(--bords-pop-pink, #FF3EA5)", color: "#FFF", fontSize: ".58rem" }}>
                Zero Landfill ♻️
              </span>
            ) : (product.FABRIC_TAG === "organic-cotton-gots" && showCertifications) ? (
              <span className="sticker-stamp" style={{ background: "var(--bords-pop-lime, #D4FF3F)", color: "#0D0D0D", fontSize: ".58rem" }}>
                Certified Not Cap ✅
              </span>
            ) : null}
          </div>
        )}

        {/* Wishlist heart button */}
        <button
          className={`card-wishlist-btn${wishlist.some(w => w.ID === product.ID) ? " card-wishlist-btn--active" : ""}`}
          onClick={e => { e.stopPropagation(); onToggleWishlist && onToggleWishlist(product); }}
          title={wishlist.some(w => w.ID === product.ID) ? "Remove from Wishlist" : "Add to Wishlist"}
          aria-label="Toggle wishlist"
        >
          <svg viewBox="0 0 24 24" strokeWidth="1.8" style={{
            width: 14, height: 14,
            fill: wishlist.some(w => w.ID === product.ID) ? "#c8471a" : "none",
            stroke: wishlist.some(w => w.ID === product.ID) ? "#c8471a" : "currentColor",
            transition: "all .25s"
          }}>
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
        </button>

        {/* Card Flip Button — only shown if fabric certifications enabled */}
        {fab && showCertifications && (
          <button
            className="card-flip-toggle-btn"
            onClick={(e) => { e.stopPropagation(); setIsFlipped(f => !f); }}
            title="Flip card for full fibre & scorecard breakdown"
            aria-label="Flip card for fabric details"
            style={{
              position: "absolute",
              bottom: 10,
              right: 10,
              zIndex: 5,
              background: isFlipped ? "var(--bords-charcoal, #2B2A28)" : (theme === 'dark' ? "rgba(20,20,25,0.92)" : "rgba(255,255,255,0.9)"),
              color: isFlipped ? "#FFF" : (theme === 'dark' ? "#FAF9F6" : "#2B2A28"),
              border: theme === 'dark' ? "1.5px solid rgba(255,255,255,0.3)" : "1.5px solid var(--bords-pop-black, #0D0D0D)",
              borderRadius: "4px",
              padding: "4px 8px",
              fontSize: ".6rem",
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 700,
              letterSpacing: ".05em",
              cursor: "pointer",
              boxShadow: theme === 'dark' ? "2px 2px 0 rgba(255,255,255,0.15)" : "2px 2px 0 var(--bords-pop-black, #0D0D0D)"
            }}
          >
            {isFlipped ? "✕ Image" : "⟳ Fibre %"}
          </button>
        )}

        {/* Flippable card face container with 3D animation */}
        <div className={`card-flip-stage ${isFlipped ? "is-flipped" : ""}`} style={{ position: "relative", width: "100%", height: "100%", minHeight: isMobile ? 180 : 280 }}>
          <div className="card-flip-inner" style={{ position: "relative", width: "100%", height: "100%" }}>
            {/* Front Face: Product Image */}
            <div className={`card-face card-face-front product-card-img-inner img-${(product.ID % 5) + 1} glitch-hover-img`}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
              {product.IMAGE_URL ? (
                <DriveImg
                  src={product.IMAGE_URL}
                  alt={product.NAME ? `${product.NAME} — Heavyweight 240 GSM Oversized Streetwear T-Shirt BORDS` : "BORDS Heavyweight Oversized T-Shirt"}
                  width={600}
                  height={800}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  loading={(rank || 99) <= (isMobile ? 2 : 3) ? "eager" : "lazy"}
                  fetchPriority={(rank || 99) <= 2 ? "high" : undefined}
                />
              ) : (
                <div className="img-placeholder">
                  <svg viewBox="0 0 24 24"><path d="M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zM16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                </div>
              )}
              {/* Minimal Streetwear Technical Spec Corner Accent */}
              <div className="card-tech-spec-corner" aria-hidden="true">
                <span className="tech-corner-cross">+</span>
                <span className="tech-corner-label">240 GSM</span>
              </div>
            </div>

            {/* Back Face: Fabric Scorecard Breakdown (Section 4 & 4B) */}
            {fab && (
              <div className="card-face card-face-back" style={{
                position: "absolute",
                inset: 0,
                background: "var(--bords-parchment, #F7F3EA)",
                color: "var(--bords-charcoal, #2B2A28)",
                padding: "24px 20px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                zIndex: 3,
              }}>
                <div>
                  <div style={{ fontSize: ".6rem", letterSpacing: ".15em", textTransform: "uppercase", color: band?.color, fontWeight: 700, marginBottom: 4 }}>
                    Fabric Scorecard · Academy India
                  </div>
                  <div style={{ fontFamily: "var(--font-editorial, serif)", fontSize: "1.15rem", fontWeight: 700, marginBottom: 8 }}>
                    {fab?.name}
                  </div>
                  <div style={{ display: "inline-block", padding: "3px 8px", borderRadius: 999, background: band?.bg, border: `1px solid ${band?.border}`, color: band?.color, fontSize: ".65rem", fontWeight: 700, marginBottom: 14 }}>
                    Score {fab?.score} · {band?.label}
                  </div>
                  <div style={{ fontSize: ".75rem", lineHeight: 1.5, color: "rgba(43,42,40,0.8)", marginBottom: 10 }}>
                    <strong>Composition:</strong> {fab?.fibre}
                  </div>
                  <div style={{ fontSize: ".72rem", lineHeight: 1.5, color: "rgba(43,42,40,0.7)", marginBottom: 10 }}>
                    <strong>Source:</strong> {fab?.sourceIndia}
                  </div>
                  <div style={{ fontSize: ".7rem", lineHeight: 1.4, color: "rgba(43,42,40,0.65)" }}>
                    <strong>Care:</strong> {fab?.careNote}
                  </div>
                </div>
                <div style={{ fontSize: ".65rem", color: "rgba(43,42,40,0.5)", borderTop: "1px solid rgba(43,42,40,0.1)", paddingTop: 8 }}>
                  Cert: {fab?.certBody}
                </div>
              </div>
            )}
          </div>
        </div>

        {product.BADGE && !isFlipped && (
          <div className={`badge ${product.BADGE === "New" ? "badge-new" : product.BADGE === "Sold Out" ? "badge-sold" : "badge-ltd"}`}>
            {product.BADGE}
          </div>
        )}

        {/* Quick Add Bar */}
        <div className="card-quick-add">
          <div className={`sizes-row ${sizeError ? "error-flash" : ""}`}>
            {(isSoldOut ? [{ size: "One Size", qty: 0 }] : sizes).map(sObj => {
              const sName = sObj.size || sObj;
              const sQty = sObj.qty ?? 10;
              const isDisabled = isSoldOut || sQty <= 0;
              return (
                <button
                  key={sName}
                  className={`size-btn${selectedSize === sName ? " active" : ""}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!isDisabled) {
                      setSelectedSize(sName);
                    } else if (showAppToast) {
                      showAppToast(`Size ${sName} is sold out.`, "warning");
                    }
                  }}
                  disabled={isDisabled}
                  style={isDisabled ? { opacity: 0.35, textDecoration: "line-through", cursor: "not-allowed" } : {}}
                >
                  {sName}
                </button>
              );
            })}
          </div>

          {/* Real Stock Status Notice */}
          {selectedSize && (
            isSelectedSizeMaxed ? (
              <div style={{ fontSize: ".65rem", color: "#FF7A59", marginTop: 4, marginLeft: 4, letterSpacing: ".02em", fontWeight: 700 }}>
                ⚠️ Max reached ({inCartForSelectedSize} in bag)
              </div>
            ) : inCartForSelectedSize > 0 ? (
              <div style={{ fontSize: ".65rem", color: "var(--bord-accent-warm)", marginTop: 4, marginLeft: 4, letterSpacing: ".02em" }}>
                {inCartForSelectedSize} in bag • Only {selectedSizeStock - inCartForSelectedSize} left
              </div>
            ) : selectedSizeStock < 10 && selectedSizeStock > 0 ? (
              <div style={{ fontSize: ".65rem", color: "var(--bord-accent-warm)", marginTop: 4, marginLeft: 4, letterSpacing: ".05em" }}>
                ⚡ Only {selectedSizeStock} left in stock
              </div>
            ) : null
          )}

          {/* Khadi Capsule 7 Pcs Waitlist CTA or Quick Add */}
          {product.FABRIC_TAG === "khadi" ? (
            <button
              onClick={(e) => { e.stopPropagation(); onOpenWaitlist && onOpenWaitlist(); }}
              className="card-waitlist-btn"
              style={{
                width: '100%',
                height: 36,
                background: "var(--bords-pop-black, #0D0D0D)",
                color: "var(--bords-pop-lime, #D4FF3F)",
                border: "none",
                borderRadius: 4,
                fontFamily: "var(--font-stamp, 'Archivo Black')",
                fontSize: ".68rem",
                letterSpacing: ".06em",
                textTransform: "uppercase",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6
              }}
            >
              Join 7-Pc Waitlist 🪡
            </button>
          ) : (
            <div style={{ position: "relative", width: "100%", height: "36px", overflow: "hidden", borderRadius: 4 }}>
              <button
                className={`add-btn ${sizeError ? "error" : ""}`}
                style={{
                  width: '100%',
                  height: "100%",
                  transform: justAdded ? "translateY(-100%)" : "translateY(0)",
                  transition: "transform 0.3s cubic-bezier(0.25, 1, 0.5, 1), opacity 0.2s ease",
                  opacity: justAdded ? 0 : (isSoldOut || isSelectedSizeMaxed ? 0.6 : 1),
                  cursor: isSelectedSizeMaxed ? "not-allowed" : "pointer"
                }}
                onClick={handleQuickAdd}
                disabled={isSoldOut}
              >
                <svg viewBox="0 0 24 24" strokeWidth="2" style={{ stroke: sizeError ? "#ff4a4a" : "currentColor" }}><path d="M12 5v14M5 12h14"/></svg>
                <span style={{ color: sizeError ? "#ff4a4a" : "inherit" }}>{sizeError ? "Select Size" : isSelectedSizeMaxed ? `Max in Bag (${inCartForSelectedSize})` : "Add to Bag"}</span>
              </button>
              <div style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                gap: "4px",
                width: "100%",
                height: "100%",
                transform: justAdded ? "translateY(0)" : "translateY(100%)",
                transition: "transform 0.3s cubic-bezier(0.25, 1, 0.5, 1), opacity 0.2s ease",
                opacity: justAdded ? 1 : 0,
                pointerEvents: justAdded ? "auto" : "none"
              }}>
                <button onClick={(e) => { e.stopPropagation(); onCheckout && onCheckout(); }} className="card-buy-btn">
                  Buy
                </button>
                <button onClick={(e) => { e.stopPropagation(); setJustAdded(false); }} className="card-shop-btn">
                  Shop
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="product-card-info">
        <div className="product-card-category">{product.CATEGORY || "BORDS"}</div>
        <div className="product-card-name">{product.NAME}</div>

        {/* Fabric Score Badge */}
        {fab && band && (
          <div
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              marginTop: 4, marginBottom: 2,
              padding: '3px 8px', borderRadius: 999,
              background: band.bg, border: `1px solid ${band.border}`,
              fontSize: '.62rem', fontWeight: 700, letterSpacing: '.05em',
              color: band.color, fontFamily: "'DM Sans',sans-serif",
              cursor: 'pointer', width: 'fit-content',
            }}
            onClick={e => { e.stopPropagation(); setIsFlipped(f => !f); }}
            title={`${fab.name} · Score ${fab.score} · ${band.label} — Click to inspect`}
            aria-label={`Fabric: ${fab.name}, score ${fab.score}, ${band.label}`}
          >
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: band.color, flexShrink: 0, display: 'inline-block',
            }} />
            {fab.name} {fab.score}
          </div>
        )}

        <div className="product-card-meta">
          <div className="product-card-price" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {salePrice ? (
              <div className="genz-price-row">
                <span className="genz-sale-price">
                  ₹ {salePrice.toLocaleString()}
                </span>
                <span className="genz-orig-price">
                  ₹ {Number(product.PRICE).toLocaleString()}
                </span>
                <span className="genz-pct-pill">-{salePct}%</span>
              </div>
            ) : (
              <span>₹ {Number(product.PRICE).toLocaleString()}</span>
            )}
          </div>
          <div className="product-card-colors">
            <div className="color-dot" style={{ background: "#1a1a1a" }}></div>
            <div className="color-dot" style={{ background: "#e8e8e8" }}></div>
            <div className="color-dot" style={{ background: "var(--accent)" }}></div>
          </div>
        </div>
      </div>
    </div>
  );
});

// ── SHOP ALL PAGE (Section 3 & 4B) ──────────────────────────────────────────
function ShopPage({
  products,
  activeCategory,
  setActiveCategory,
  fabricFilter,
  setFabricFilter,
  onAddToCart,
  onViewProduct,
  wishlist,
  onToggleWishlist,
  theme,
  isMobile,
  onCheckout,
  onOpenWaitlist,
  vibe,
  showCertifications = false,
  loudProducts = [],
  onToggleVibe,
  onBack,
  onGoHome,
  onOpenMenu,
  cartCount = 0,
  cart = [],
  showAppToast,
  onOpenCart,
}) {
  const catFiltered = activeCategory === 'all'
    ? products
    : products.filter(p => {
        const c = (p.CATEGORY || '').toLowerCase();
        if (activeCategory === 'tops') return c.includes('top') || c.includes('tee') || c.includes('shirt');
        if (activeCategory === 'bottoms') return c.includes('pant') || c.includes('bottom') || c.includes('cargo') || c.includes('short');
        if (activeCategory === 'accessories') return c.includes('acc') || c.includes('cap') || c.includes('bag') || c.includes('tote');
        return true;
      });

  const finalFiltered = fabricFilter === 'all'
    ? catFiltered
    : catFiltered.filter(p => (p.FABRIC_TAG || '') === fabricFilter);

  return (
    <div className={`shop-page-container bords-mode-charged ${vibe === 'loud' ? 'vibe-loud' : 'vibe-quiet'}`} style={{ minHeight: '100vh', paddingTop: '76px', paddingBottom: '80px', background: vibe === 'loud' ? '#09090C' : 'var(--bord-bg)', color: vibe === 'loud' ? '#FFFFFF' : 'var(--bord-fg)' }}>
      {/* Top Navigation Bar for Shop Page */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '56px',
        background: vibe === 'loud' ? 'rgba(9,9,12,0.92)' : (theme === 'dark' ? 'rgba(15,15,15,0.92)' : 'rgba(250,249,246,0.92)'),
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: vibe === 'loud' ? '1px solid rgba(255,255,255,0.08)' : (theme === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)'),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        zIndex: 1000,
      }}>
        {/* Left: Close / Back cross button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={onBack}
            aria-label="Close shop and return"
            title="Close"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'rgba(var(--bord-fg-rgb, 20,20,20), 0.06)',
              border: '1px solid rgba(var(--bord-fg-rgb, 20,20,20), 0.14)',
              borderRadius: 999,
              padding: '7px 14px 7px 10px',
              cursor: 'pointer',
              color: 'inherit',
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '.68rem',
              letterSpacing: '.12em',
              textTransform: 'uppercase',
              fontWeight: 600,
              minHeight: 38,
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            <span>Close</span>
          </button>
        </div>

        {/* Center: Logo */}
        <button
          onClick={() => {
            if (typeof onGoHome === "function") {
              onGoHome();
            } else if (typeof window !== "undefined" && typeof window.__bordsNavigate === "function") {
              window.__bordsNavigate("home");
            } else {
              onBack();
            }
          }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}
          aria-label="BORDS — Go home"
        >
          <Logo size="sm" showTagline={false} />
        </button>

        {/* Right: Menu + Cart */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isMobile && (
            <button
              onClick={onOpenMenu}
              aria-label="Open menu"
              title="Menu"
              style={{
                background: 'rgba(var(--bord-fg-rgb, 20,20,20), 0.06)',
                border: '1px solid rgba(var(--bord-fg-rgb, 20,20,20), 0.14)',
                borderRadius: 8,
                width: 38,
                height: 38,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'inherit',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          )}
          {onOpenCart && (
            <div
              className="cart-bubble"
              onClick={onOpenCart}
              role="button"
              aria-label="Open shopping bag"
              style={{ minHeight: 38, padding: '6px 12px' }}
            >
              <svg className="nav-icon" viewBox="0 0 24 24" strokeWidth="1.5" style={{ width: 16, height: 16 }}>
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
              <div className="cart-count">{cartCount}</div>
            </div>
          )}
        </div>
      </nav>

      <div style={{ maxWidth: '1240px', margin: '0 auto', padding: '0 24px' }}>
        {/* Header */}
        <div style={{ marginBottom: 36, textAlign: 'center' }}>
          {showCertifications && (
            <div className="sticker-stamp" style={{ background: 'var(--bords-pop-lime, #D4FF3F)', color: '#0D0D0D', marginBottom: 12 }}>
              Consciously Cut · Certified Not Cap ✅
            </div>
          )}
          {vibe === "loud" ? (
            <>
              <div style={{
                fontFamily: "'Space Mono', monospace",
                fontSize: ".72rem",
                color: "#D4FF3F",
                letterSpacing: ".15em",
                textTransform: "uppercase",
                marginBottom: 8,
                fontWeight: 700
              }}>
                ⚡ CAPSULE VAULT // ARCHIVE DROP
              </div>
              <h1 style={{
                fontFamily: "var(--font-display, 'Neue Montreal', 'Bebas Neue', sans-serif)",
                fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
                letterSpacing: '-.02em',
                margin: '0 0 12px',
                color: '#FFFFFF'
              }}>
                LOUD CAPSULE
              </h1>
              <p style={{
                color: 'rgba(255,255,255,0.7)',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '.95rem',
                maxWidth: '540px',
                margin: '0 auto'
              }}>
                High-performance architectural silhouettes released in strictly limited numbers. Vault discounts applied.
              </p>
            </>
          ) : (
            <>
              <h1 style={{
                fontFamily: "var(--font-display, 'Neue Montreal', 'Bebas Neue', sans-serif)",
                fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
                letterSpacing: '-.02em',
                margin: '0 0 12px'
              }}>
                Drop Archive
              </h1>
              <p style={{
                color: 'var(--bords-muted)',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '.95rem',
                maxWidth: '540px',
                margin: '0 auto'
              }}>
                Raw garments tailored for intention and presence. Zero synthetic shortcuts.
              </p>
            </>
          )}
        </div>

        {/* Category Filter Pills */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
          {[
            { key: 'all', label: 'All Pieces' },
            { key: 'tops', label: 'Tops' },
            { key: 'bottoms', label: 'Bottoms' },
            { key: 'accessories', label: 'Accessories' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveCategory(tab.key)}
              className={vibe === 'loud' ? `loud-tab-btn ${activeCategory === tab.key ? 'active' : ''}` : ''}
              style={vibe === 'loud' ? undefined : {
                padding: '8px 20px',
                borderRadius: 999,
                fontFamily: "var(--font-stamp, 'Archivo Black', sans-serif)",
                fontSize: '.72rem',
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                border: activeCategory === tab.key ? '2px solid var(--bords-pop-black, #0D0D0D)' : (theme === 'dark' ? '1px solid rgba(244,242,238,0.2)' : '1px solid rgba(43,42,40,0.2)'),
                background: activeCategory === tab.key ? 'var(--bords-pop-lime, #D4FF3F)' : 'transparent',
                color: activeCategory === tab.key ? '#0D0D0D' : (theme === 'dark' ? '#FAF9F6' : '#2B2A28'),
                boxShadow: activeCategory === tab.key ? '2px 2px 0 var(--bords-pop-black, #0D0D0D)' : 'none',
                transition: 'all .18s ease'
              }}
            >
              {vibe === 'loud' ? `[ ${tab.label.toUpperCase()} ]` : tab.label}
            </button>
          ))}
        </div>

        {/* Fabric Filter Pills */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 40 }}>
          {[
            { key: 'all', label: 'All Fabrics' },
            { key: 'organic-cotton-gots', label: showCertifications ? '🌿 GOTS Organic Cotton (Score 7.0)' : '🌿 Organic Cotton' },
            { key: 'recycled-cotton', label: showCertifications ? '♻️ Recycled Cotton (Score 8.5)' : '♻️ Recycled Cotton' },
            { key: 'khadi', label: showCertifications ? '🪡 Handloom Khadi (Score 8.5)' : '🪡 Handloom Khadi' }
          ].map(fab => (
            <button
              key={fab.key}
              onClick={() => setFabricFilter(fab.key)}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                fontSize: '.68rem',
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: fabricFilter === fab.key ? 700 : 500,
                cursor: 'pointer',
                border: fabricFilter === fab.key ? '1.5px solid var(--bords-moss-accessible, #4A5D3A)' : (theme === 'dark' ? '1px solid rgba(244,242,238,0.2)' : '1px solid rgba(43,42,40,0.15)'),
                background: fabricFilter === fab.key ? (theme === 'dark' ? 'rgba(181,214,159,0.15)' : 'rgba(74,93,58,0.12)') : 'transparent',
                color: fabricFilter === fab.key ? (theme === 'dark' ? '#B5D69F' : '#3E4E30') : (theme === 'dark' ? '#FAF9F6' : '#2B2A28'),
                transition: 'all .15s ease'
              }}
            >
              {fab.label}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {finalFiltered.length === 0 ? (
          vibe === "loud" ? (
            <div className="genz-vault-empty">
              <div className="vault-icon-frame">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#D4FF3F" strokeWidth="1.8">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  <circle cx="12" cy="16" r="1.5" fill="#D4FF3F"/>
                </svg>
              </div>
              <div className="vault-tag">[ STATUS // ARCHIVE VAULT SECURED ]</div>
              <h3 className="vault-title">No Capsule Items Staged</h3>
              <p className="vault-desc">
                The loud drop archive is currently secured. Admin has not staged pieces for this drop window.
              </p>
              {onToggleVibe && (
                <button className="vault-cta-btn" onClick={onToggleVibe}>
                  Explore Quiet Collection 🌿
                </button>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--bords-muted)' }}>
              <div style={{
                fontFamily: "var(--font-stamp, 'Archivo Black', sans-serif)",
                fontSize: "1.1rem",
                color: "var(--accent)",
                marginBottom: 8,
                letterSpacing: ".06em",
                textTransform: "uppercase"
              }}>
                No pieces found matching these filters.
              </div>
            </div>
          )
        ) : (
          <div className="arrivals-scroll" style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(280px, 1fr))', gap: isMobile ? 12 : 24 }}>
            {finalFiltered.map((p, i) => (
              <ProductCard
                key={p.ID}
                product={p}
                delay={(i % 4) + 1}
                rank={i + 1}
                isMobile={isMobile}
                onAddToCart={onAddToCart}
                onViewProduct={onViewProduct}
                wishlist={wishlist}
                onToggleWishlist={onToggleWishlist}
                theme={theme}
                onCheckout={onCheckout}
                onOpenWaitlist={onOpenWaitlist}
                vibe={vibe}
                showCertifications={showCertifications}
                loudProducts={loudProducts}
                cart={cart}
                showAppToast={showAppToast}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FavoritesPage({ wishlist, onBack, onAddToCart, onViewProduct, onToggleWishlist, theme, onCheckout, vibe = "quiet", showCertifications = false, loudProducts = [], cart = [], showAppToast }) {
  return (
    <div style={{minHeight:"100vh", background:"var(--black)", color:"var(--white)", paddingTop: "clamp(64px, 10vw, 100px)", paddingBottom: "60px"}}>
      <div style={{maxWidth: "1200px", margin: "0 auto", padding: "0 24px"}}>
        <div style={{display:"flex", alignItems:"center", gap: 16, marginBottom: 40}}>
          <button
            onClick={onBack}
            aria-label="Close favorites and return"
            title="Close"
            style={{
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.18)",
              color: "var(--white)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: ".68rem",
              letterSpacing: ".12em",
              textTransform: "uppercase",
              padding: "8px 16px 8px 12px",
              borderRadius: 999,
              fontWeight: 600,
              transition: "all .2s ease",
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            Close
          </button>
          <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"2.2rem", fontWeight:500, letterSpacing:".02em", margin:0}}>My Favorites</h1>
        </div>

        {wishlist.length === 0 ? (
          <div style={{textAlign: "center", padding: "100px 20px", border: "1px dashed rgba(244,242,238,0.1)", borderRadius: 16}}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(244,242,238,0.2)" strokeWidth="1.5" style={{marginBottom: 20}}>
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
            <div style={{fontFamily:"'DM Sans',sans-serif", fontSize: ".85rem", opacity: .5, letterSpacing: ".1em", textTransform: "uppercase"}}>Your favorites list is empty</div>
          </div>
        ) : (
          <div className="collection-products-grid">
            {wishlist.map((p, i) => (
              <ProductCard
                key={p.ID}
                product={p}
                delay={i % 3 + 1}
                rank={i + 1}
                onAddToCart={onAddToCart}
                onViewProduct={onViewProduct}
                wishlist={wishlist}
                onToggleWishlist={onToggleWishlist}
                theme={theme}
                onCheckout={onCheckout}
                vibe={vibe}
                showCertifications={showCertifications}
                loudProducts={loudProducts}
                cart={cart}
                showAppToast={showAppToast}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CollectionPage({ collection, products, onBack, onAddToCart, onViewProduct, wishlist, onToggleWishlist, theme, onCheckout, vibe = "quiet", showCertifications = false, loudProducts = [], cart = [], showAppToast }) {
  const [openFaq, setOpenFaq] = useState(null);

  const colName = (collection?.NAME || collection?.name || "").replace(/\n/g, " ");
  const colTag = collection?.TAG || collection?.tag || "";
  const targetColId = collection?.ID ?? collection?.id;
  const colSlug = (collection?.slug || (colName ? colName.toLowerCase().replace(/[^a-z0-9]+/g, '-') : String(targetColId || ''))).toLowerCase();

  // Link products to this collection
  let linkedProducts = (products || []).filter(p => {
    // Flagship oversized / heavyweight collections show all tees
    if (colSlug === "oversized-t-shirts" || colSlug === "heavyweight-t-shirts") {
      const cat = (p.CATEGORY || p.category || "").toLowerCase();
      const name = (p.NAME || p.name || "").toLowerCase();
      return cat.includes("top") || cat.includes("tee") || name.includes("tee") || name.includes("classico") || name.includes("classic");
    }
    if (colSlug === "new-arrivals" || colSlug === "best-sellers") {
      return true; // Show all active pieces
    }
    
    const pColId = p.COLLECTION_ID ?? p.collection_id;
    
    // 1. Direct ID match (handles numeric IDs like 1, 2, 3 and string IDs like "f1", "f2")
    if (pColId != null && String(pColId).trim() !== "" && targetColId != null && String(targetColId).trim() !== "") {
      const pColStr = String(pColId).trim().toLowerCase();
      const targetColStr = String(targetColId).trim().toLowerCase();
      if (pColStr === targetColStr) return true;
      
      const numP = Number(pColId);
      const numTarget = Number(targetColId);
      if (!isNaN(numP) && !isNaN(numTarget) && numP !== 0) {
        if (numP === numTarget) return true;
        if (!isNaN(numTarget)) return false;
      }
    }
    
    // 2. Fallback keyword matching for unassigned products or fallback named collections
    const name = (p.NAME || p.name || "").toLowerCase();
    const desc = (p.DESCRIPTION || p.description || "").toLowerCase();
    const cat = (p.CATEGORY || p.category || "").toLowerCase();
    const colNameLower = colName.toLowerCase();
    
    if (colNameLower.includes("shadow") && (name.includes("shadow") || desc.includes("shadow"))) return true;
    if (colNameLower.includes("blank") && (name.includes("blank") || cat.includes("tops") || cat.includes("bottoms"))) return true;
    if (colNameLower.includes("raw") && (name.includes("raw") || name.includes("hoodie") || name.includes("pants"))) return true;
    if (colNameLower.includes("beige") && (name.includes("beige") || desc.includes("beige"))) return true;
    
    const words = colNameLower.split(/[\s\n]+/).filter(w => w.length > 3);
    return words.some(w => name.includes(w) || desc.includes(w));
  });

  if (colSlug === "new-arrivals") {
    linkedProducts = [...linkedProducts].sort((a, b) => Number(b.ID ?? b.id ?? 0) - Number(a.ID ?? a.id ?? 0));
  }

  // ── Dynamic SEO Metadata & ItemList Structured Data ────────────────────────
  useEffect(() => {
    if (!collection) return;
    try {
      const { setPageMeta, injectCollectionSchema, removeCollectionSchema, injectBreadcrumb } = require('../lib/seo');
      const canonicalUrl = `https://www.bords.co.in/collections/${colSlug}`;
      const pageTitle = `${colName} — Indian Streetwear & Heavyweight T-Shirts | BORDS`;
      const pageDesc = collection.DESCRIPTION || `Shop the ${colName} collection by BORDS. Heavyweight 240 GSM oversized streetwear t-shirts crafted in Bangalore for people bored of routine designs. Free shipping across India.`;

      setPageMeta({
        title: pageTitle,
        description: pageDesc,
        canonical: canonicalUrl,
      });

      injectCollectionSchema(colName, linkedProducts);
      injectBreadcrumb([
        { name: 'Home', url: 'https://www.bords.co.in/' },
        { name: 'Collections', url: 'https://www.bords.co.in/shop' },
        { name: colName },
      ]);

      return () => {
        removeCollectionSchema();
      };
    } catch (_) {}
  }, [collection, linkedProducts, colName, colSlug]);

  if (!collection) return null;

  const collectionDescription = collection.DESCRIPTION || (
    colSlug === "oversized-t-shirts"
      ? "Engineered for people bored of routine designs. BORDS oversized t-shirts are crafted from premium 240 GSM combed cotton with a structured, anti-routine drape. Featuring drop-shoulder proportions, high-density ribbed crew collars that retain shape after 50+ washes, and breathable luxury comfort built for the Indian climate. Designed in Bangalore, shipped free across India."
      : colSlug === "heavyweight-t-shirts"
      ? "240 GSM heavyweight cotton isn't just thicker fabric — it's architectural integrity. Unlike flimsy fast-fashion tees that warp and cling, BORDS heavyweight t-shirts maintain an intentional silhouette with zero body cling. Finished with reinforced twin-needle stitching and deep reactive dyes that prevent color wash-out."
      : colSlug === "new-arrivals"
      ? "The newest streetwear iterations from our Bangalore design studio. Limited-run silhouettes, fresh colorways, and anti-routine fits engineered for everyday expression."
      : colSlug === "best-sellers"
      ? "Our most demanded streetwear cuts. Tried, tested, and embraced by creatives across Bangalore, Mumbai, Delhi, and beyond."
      : "Understated silhouettes in premium heavyweight cotton. Minimal branding, anti-routine proportions, maximum everyday presence."
  );

  const faqs = [
    {
      q: "How does the BORDS oversized fit compare to standard Indian sizing?",
      a: "BORDS tees are intentionally cut oversized with dropped shoulders, wider sleeves, and a boxy torso drape. If you prefer a relaxed, true streetwear silhouette, choose your regular size. If you want a more tailored or standard regular fit, we recommend sizing down one size."
    },
    {
      q: "What makes 240 GSM heavyweight cotton different from standard t-shirts?",
      a: "Standard retail t-shirts in India typically weigh between 140 and 180 GSM, which can feel thin, cling to the body, and lose their shape quickly. BORDS 240 GSM heavyweight combed cotton provides a structured drape that falls cleanly without clinging, combined with high breathability suited for Indian weather."
    },
    {
      q: "Where are BORDS streetwear pieces crafted and how long does delivery take?",
      a: "All BORDS garments are designed and crafted in Bangalore, Karnataka. Orders are dispatched within 24 hours. Delivery takes 2 to 3 days within Bangalore and South India, and 3 to 5 business days for pan-India delivery. Free shipping is provided across India."
    },
    {
      q: "What is the return and exchange policy?",
      a: "We offer a 7-day hassle-free return and exchange window on all unworn items with tags intact. If you need a different size, our Bangalore customer support team handles exchanges promptly."
    }
  ];

  return (
    <div style={{minHeight:"100vh", background:"var(--black)", color:"var(--white)", paddingTop: "110px", paddingBottom: "80px"}}>
      <div style={{maxWidth: "1200px", margin: "0 auto", padding: "0 24px"}}>
        
        {/* Breadcrumb Navigation */}
        <nav aria-label="Breadcrumb" style={{display:"flex", alignItems:"center", gap:8, fontSize:".7rem", letterSpacing:".08em", textTransform:"uppercase", color:"var(--muted)", marginBottom:24}}>
          <span onClick={onBack} style={{cursor:"pointer", transition:"color .2s"}} onMouseEnter={e=>e.target.style.color="var(--white)"} onMouseLeave={e=>e.target.style.color="var(--muted)"}>Shop</span>
          <span>›</span>
          <span>Collections</span>
          <span>›</span>
          <span style={{color:"#c8471a", fontWeight:600}}>{colName}</span>
        </nav>

        {/* Back button and title info */}
        <div style={{display:"flex", flexDirection:"column", gap: 12, marginBottom: 36}}>
          <button
            onClick={onBack}
            aria-label="Close collection and return"
            title="Back to Shop"
            style={{
              alignSelf: "flex-start",
              background: "rgba(var(--bord-fg-rgb), 0.06)",
              border: "1px solid rgba(var(--bord-fg-rgb), 0.16)",
              color: "var(--white)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: ".68rem",
              letterSpacing: ".12em",
              textTransform: "uppercase",
              padding: "8px 16px 8px 12px",
              borderRadius: 999,
              fontWeight: 600,
              transition: "all .2s ease",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Shop
          </button>
          
          <div style={{marginTop: 12}}>
            {colTag && <div style={{fontSize:".65rem", letterSpacing:".28em", textTransform:"uppercase", color:"#c8471a", fontWeight:700, marginBottom: 8}}>{colTag}</div>}
            <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(2.4rem, 5vw, 3.4rem)", fontWeight:500, letterSpacing:".02em", margin:0, textTransform: "capitalize", lineHeight:1.15}}>{colName}</h1>
            <p style={{fontFamily:"'DM Sans',sans-serif", fontSize:".85rem", color:"var(--muted)", letterSpacing:".04em", textTransform:"uppercase", marginTop:6, marginBottom:16}}>
              Heavyweight 240 GSM Oversized Streetwear · Crafted in Bangalore
            </p>
            <p style={{fontFamily:"'DM Sans',sans-serif", fontSize:".92rem", lineHeight:1.75, color:"rgba(var(--bord-fg-rgb), 0.78)", maxWidth: "800px", margin: "0 0 24px 0"}}>
              {collectionDescription}
            </p>

            {/* Category Value Pillars */}
            <div style={{display:"flex", flexWrap:"wrap", gap:10, marginBottom:28}}>
              <span style={{display:"inline-flex", alignItems:"center", gap:6, padding:"6px 14px", borderRadius:6, background:"rgba(var(--bord-fg-rgb),0.04)", border:"1px solid rgba(var(--bord-fg-rgb),0.12)", fontSize:".72rem", fontFamily:"'DM Sans',sans-serif", fontWeight:600, color:"var(--white)"}}>
                ⚡ 240 GSM Combed Cotton
              </span>
              <span style={{display:"inline-flex", alignItems:"center", gap:6, padding:"6px 14px", borderRadius:6, background:"rgba(var(--bord-fg-rgb),0.04)", border:"1px solid rgba(var(--bord-fg-rgb),0.12)", fontSize:".72rem", fontFamily:"'DM Sans',sans-serif", fontWeight:600, color:"var(--white)"}}>
                ✦ Anti-Routine Drop Shoulders
              </span>
              <span style={{display:"inline-flex", alignItems:"center", gap:6, padding:"6px 14px", borderRadius:6, background:"rgba(var(--bord-fg-rgb),0.04)", border:"1px solid rgba(var(--bord-fg-rgb),0.12)", fontSize:".72rem", fontFamily:"'DM Sans',sans-serif", fontWeight:600, color:"var(--white)"}}>
                🇮🇳 Designed in Bangalore
              </span>
              <span style={{display:"inline-flex", alignItems:"center", gap:6, padding:"6px 14px", borderRadius:6, background:"rgba(var(--bord-fg-rgb),0.04)", border:"1px solid rgba(var(--bord-fg-rgb),0.12)", fontSize:".72rem", fontFamily:"'DM Sans',sans-serif", fontWeight:600, color:"var(--white)"}}>
                📦 Free Pan-India Delivery
              </span>
            </div>
          </div>
        </div>

        {/* Product Grid */}
        {linkedProducts.length === 0 ? (
          <div style={{textAlign: "center", padding: "100px 20px", border: "1px dashed rgba(var(--bord-fg-rgb), 0.15)", borderRadius: 16, marginBottom:60}}>
            <div style={{fontFamily:"'DM Sans',sans-serif", fontSize: ".85rem", opacity: .5, letterSpacing: ".1em", textTransform: "uppercase"}}>No products in this collection currently</div>
          </div>
        ) : (
          <div className="collection-products-grid" style={{marginBottom:72}}>
            {linkedProducts.map((p, i) => (
              <ProductCard
                key={p.ID}
                product={p}
                delay={i % 3 + 1}
                rank={i + 1}
                onAddToCart={onAddToCart}
                onViewProduct={onViewProduct}
                wishlist={wishlist}
                onToggleWishlist={onToggleWishlist}
                theme={theme}
                onCheckout={onCheckout}
                vibe={vibe}
                showCertifications={showCertifications}
                loudProducts={loudProducts}
                cart={cart}
                showAppToast={showAppToast}
              />
            ))}
          </div>
        )}

        {/* Collection FAQ Accordion Section */}
        <section aria-labelledby="collection-faq-title" style={{marginTop: 64, paddingTop: 48, borderTop: "1px solid rgba(var(--bord-fg-rgb), 0.12)"}}>
          <div style={{fontSize:".65rem", letterSpacing:".3em", textTransform:"uppercase", color:"#c8471a", fontWeight:700, marginBottom:8}}>
            Frequently Asked Questions
          </div>
          <h2 id="collection-faq-title" style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"2.2rem", fontWeight:500, margin:"0 0 28px 0", letterSpacing:".02em"}}>
            Everything You Need to Know
          </h2>
          <div style={{display:"flex", flexDirection:"column", gap:12}}>
            {faqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div key={idx} style={{border:"1px solid rgba(var(--bord-fg-rgb), 0.12)", borderRadius:8, background:"rgba(var(--bord-fg-rgb), 0.02)", overflow:"hidden", transition:"border-color .2s"}}>
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    aria-expanded={isOpen}
                    style={{
                      width:"100%", padding:"18px 24px", background:"none", border:"none",
                      display:"flex", alignItems:"center", justifyContent:"space-between",
                      textAlign:"left", cursor:"pointer", color:"inherit"
                    }}
                  >
                    <span style={{fontFamily:"'DM Sans',sans-serif", fontSize:".92rem", fontWeight:600, letterSpacing:".01em"}}>
                      {faq.q}
                    </span>
                    <span style={{fontSize:"1.2rem", color:"#c8471a", marginLeft:16, flexShrink:0, transition:"transform .25s", transform: isOpen ? "rotate(45deg)" : "rotate(0deg)"}}>
                      +
                    </span>
                  </button>
                  {isOpen && (
                    <div style={{padding:"0 24px 20px 24px", fontFamily:"'DM Sans',sans-serif", fontSize:".86rem", lineHeight:1.7, color:"rgba(var(--bord-fg-rgb), 0.74)"}}>
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

      </div>
    </div>
  );
}

const CSS = ""; // Extracted to BordApp.css for CDN caching & performance


